/**
 * Drug detail page component with DailyMed-style paged document view and versioning
 */
import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { authApi } from '../services/authApi';
import { findSectionScrollTarget } from '../utils/sectionScrollTarget';
import { formatApprovalDate } from '../utils/approvalDate';
import {
  claimChunkHighlight,
  mayClearChunkHighlight,
  releaseChunkHighlight,
} from '../utils/chunkHighlightOwner';
import type { ApprovalLetterSummary, DrugDetail, VersionSummary, CommentResponse, UploadDraftResponse, VersionDetailResponse, AdjacentVersionsResponse, AllVersionsResponse, VersionSource, SBASummaryForDrug, SBAReviewCycle, UnmatchedLabel, UnifiedVersionSummary, SBACycleLabelVersion } from '../types';
import type { SBADocumentDetail } from '../types/sba';
import type { FDALetterSummary, FDALetterDetail } from '../types/fdaLetter';
import { logger } from '../lib/logger';
import { useSubmissionViewPreference } from '../hooks/useSubmissionViewPreference';
import { SUBMISSION_VIEW_TOGGLE_VISIBLE } from '../config/submissionViewToggle';
import { useDbUser } from '../hooks/useDbUser';
import { hasCycleEnvelope } from '../utils/sbaResponseShape';
import GenericLinks from '../components/GenericLinks';
import VersionSelector from '../components/VersionSelector';
import DrugsAtFdaPicker, { firstDocument, type DrugsAtFdaApp, type DrugsAtFdaSource } from '../components/DrugsAtFdaPicker';
import { isUsFdaVisible } from '../constants/filterConstants';
import type { UsFdaProductRef } from '../types';
import DraftUpload from '../components/DraftUpload';
import VersionComparisonModal from '../components/VersionComparisonModal';
import SplContentRenderer, { highlightHtmlString } from '../components/SplContentRenderer';
import DraftContentRenderer from '../components/DraftContentRenderer';
import { OnlyOfficeDraftViewer } from '../components/OnlyOfficeDraftViewer';
import type { OnlyOfficeViewerHandle } from '../components/OnlyOfficeDraftViewer';
import SBAViewerEmbed, { type SBAViewerHandle } from '../components/viewer/SBAViewerEmbed';
import EPARViewerEmbed from '../components/viewer/EPARViewerEmbed';
import { ANCHOR_TEXT_MAX } from '../components/viewer/anchorBbox';
import ApprovalLetterViewerEmbed from '../components/viewer/ApprovalLetterViewerEmbed';
import UKPILViewerEmbed from '../components/viewer/UKPILViewerEmbed';
import CAPMViewerEmbed from '../components/viewer/CAPMViewerEmbed';
import AUViewerEmbed from '../components/viewer/AUViewerEmbed';
import EUViewerEmbed from '../components/viewer/EUViewerEmbed';
import EuHistoricalCompareLauncher from '../components/viewer/EuHistoricalCompareLauncher';
import DeVersionCompareLauncher from '../components/viewer/DeVersionCompareLauncher';
import EuHistoricalViewer from '../components/viewer/EuHistoricalViewer';
import { applyEuHistoricalCompareTarget } from '../utils/euHistoricalCompareLink';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';
import { safeCompetitiveIntelUrl } from '../utils/competitiveIntelUrl';
import JPSectionView from '../components/viewer/JPSectionView';
import JPVersionCompareModal, { lineageLabel, type JpLineage } from '../components/JPVersionCompareModal';
import AUVersionCompareModal from '../components/AUVersionCompareModal';
import JPPdfViewerEmbed from '../components/viewer/JPPdfViewerEmbed';
import ZhPdfViewerEmbed from '../components/viewer/ZhPdfViewerEmbed';
import ChPdfViewerEmbed from '../components/viewer/ChPdfViewerEmbed';
import DePdfViewerEmbed, { type LabelPdfFamily } from '../components/viewer/DePdfViewerEmbed';

// Regions whose label is a national label PDF in the de_pdf viewer slot.
const LABEL_PDF_REGIONS: ReadonlySet<string> = new Set(['DE', 'IE', 'IT']);

/** A link that picks a document itself (``?source=depdfdoc&version=…``) owns the
 *  selection. ``source=keyword`` (a keyword result's drug-name link) names no
 *  document, so the region's main label must still open — without this the
 *  CH / DE / IE / IT viewer stayed blank. */
function namesADocument(params: URLSearchParams): boolean {
  const source = params.get('source');
  return !!source && source !== 'keyword';
}
import FdaLabelPdfViewerEmbed from '../components/viewer/FdaLabelPdfViewerEmbed';
import FdaLabelPdfVersionCompareLauncher from '../components/viewer/FdaLabelPdfVersionCompareLauncher';
import FrHtmlViewer from '../components/viewer/FrHtmlViewer';
import type { ChunkAnchor } from '../components/viewer/RegulatoryViewerEmbed';
import { planPageAnchors } from '../components/viewer/RegulatoryViewerEmbed';
import { citedChunkIds } from '../utils/regulatoryCitationHref';
import { isReservedSectionNumber } from '../components/viewer/tocHelpers';
import { resolveSectionRef } from '../api/viewerApi';
import { resolveSectionRef as resolveEparSectionRef } from '../api/eparViewerApi';
import { usePublishChatContext } from '../shell/chatContext';
import { ShellContext } from '../shell/shellContext';
import { citationOnTabPage, citationTargetPath, pathnameOf } from '../shell/citationTarget';
import type { PublishedContext } from '../shell/types';
import { useTab, findInTab } from '../shell/TabContext';
import CommentsPanel from '../components/CommentsPanel';
import DrugReferencesPanel from '../components/fdaLetter/DrugReferencesPanel';
import FdaLetterPageViewer from '../components/fdaLetter/FdaLetterPageViewer';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import SubscribeLabelButton from '../components/SubscribeLabelButton';
import LabelBackLink from '../components/label/LabelBackLink';
import CompareMenu from '../components/label/CompareMenu';
import LabelContentsRail, { useMinWidthLg, type RailEntry } from '../components/label/LabelContentsRail';
import LabelToolbar from '../components/label/LabelToolbar';
import { useArticleMatches } from '../hooks/useArticleMatches';
import { US_ROWS, JP_ROWS, marksOfMatch, matchesOnlyKeep } from '../utils/labelMatches';
import { parseLabelTerms, highlightPlainText } from '../utils/labelTerms';
import { formatRegionalVersionLabel } from '../utils/labelVersionFormat';
import { resolveSbaApplicationType } from '../utils/sbaApplicationType';
import { stashEvidenceChunk, takeEvidenceChunk } from '../utils/evidenceChunkHandoff';
import { DEFAULT_ANCHOR_LENGTH } from '../types/qa';
import type { SimpleCitation, ChunkClickData } from '../types/qa';
import {
  SECTION_NUMBER_NAMES,
  isHighlightsSection,
  compareSectionNumbers,
  getParentSectionNum,
  isSubsection as isSubsectionCheck,
} from '../utils/drugDetailUtils';

/** `?source=` values that make Drugs@FDA the active source (C1). */
const DRUGS_AT_FDA_SOURCES: ReadonlySet<string> = new Set(['fdalabelpdfdoc', 'approval-letter', 'sba']);
const NO_US_FDA_APPS: DrugsAtFdaApp[] = [];
const NO_FDA_LABEL_PDFS: never[] = [];

interface TocSection {
  id: string;
  title: string;
  level: number;
  /** SBA-only: the raw ``section_number`` the viewer should scroll to.
   *  When set, TOC clicks route through ``sbaViewerRef.scrollToSection``
   *  (Phase 10a contract) instead of the accordion DOM-ref path — SBA
   *  docs render via ``<SBAViewerEmbed>`` and have no
   *  ``sba-section-${id}`` DOM refs to scroll to. */
  sectionNumber?: string;
  /** JP-only: route the click to the ``jp-sec-${num}`` DOM anchor that
   *  ``JPSectionView`` renders (no viewer ref; plain getElementById). */
  jp?: boolean;
  /** JP-only: this canonical section is NOT in this drug's label —
   *  rendered greyed/non-clickable so the full PMDA template is visible
   *  without polluting the document body. */
  absent?: boolean;
}

const SEG_CONTAINER = /^SEG\d+$/i;

function parseApprovalLetterLandingPage(raw: string | null): number | undefined {
  if (!raw || !/^[1-9]\d*$/.test(raw)) return undefined;
  const page = Number(raw);
  return Number.isSafeInteger(page) ? page : undefined;
}

/** Task 6 (sba-bundle-split-consumers): map one SBA section row to a TOC
 *  entry. Shows the FDA-printed local number (``section_number_local``,
 *  T4) instead of the internal ``SEG<N>`` composite canonical number —
 *  blank for a bundle container row (bare ``SEG<N>``). The canonical
 *  ``section_number`` always stays the navigation key so TOC clicks keep
 *  routing through the viewer's ``scrollToSection``. Bundle rows indent
 *  by canonical depth (``level + 1``); non-bundle rows keep the legacy
 *  1-vs-2 clamp unchanged so existing (non-bundle) documents don't
 *  regress. */
export function sbaTocEntry(s: {
  id: number; section_number: string | null; section_number_local?: string | null;
  title?: string | null; level: number;
}): { id: string; title: string; level: number; sectionNumber: string } {
  const canon = s.section_number ?? '';
  const isContainer = SEG_CONTAINER.test(canon);
  const isBundle = /^SEG\d+(?:\.|$)/i.test(canon);
  const cleanTitle = (s.title || '').replace(/^[\d.]+\.?\s*/, '');
  const printed = (
    isContainer
    || isReservedSectionNumber(canon)
    || isReservedSectionNumber(s.section_number_local ?? null)
  ) ? '' : (s.section_number_local || canon);
  const title = printed ? `${printed}. ${cleanTitle}` : cleanTitle;
  return {
    id: `sba-toc-${s.id}`,
    title,
    level: isBundle ? s.level + 1 : (s.level > 1 ? 2 : 1),
    // Bundle rows use full canonical depth. Legacy rows preserve the existing
    // 1-vs-2 mapping exactly; a normal level-1 row must not jump to tier 2.
    sectionNumber: canon,          // canonical navigation key
  };
}

const CHUNK_HIGHLIGHT_NAME = 'chunk-evidence';

const formatSbaSubmissionLabel = (
  type: string | null | undefined,
  number: number | null | undefined,
): string | null => {
  const normalized = type?.toUpperCase();
  if (normalized === 'ORIG') return 'Original';
  if (normalized === 'SUPPL') {
    return number == null ? 'Supplement' : `Supplement ${number}`;
  }
  if (!type) return null;
  return number == null ? type : `${type} ${number}`;
};

const formatSbaRegulatoryDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00Z`)
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Delete the highlight, but only if this tab is the one that drew it.
 *
 * ``CSS.highlights`` is per-DOCUMENT while AppShell keeps every tab mounted, so
 * an unconditional delete here erases whatever the reader is looking at in
 * another tab. At ``TAB_CAP`` every citation click evicts a tab and runs its
 * unmount cleanup, which is exactly when that happens.
 */
function clearChunkHighlights(tabId: string): void {
  if (!mayClearChunkHighlight(tabId)) return;
  if ('highlights' in CSS) {
    (CSS as any).highlights.delete(CHUNK_HIGHLIGHT_NAME);
  }
  releaseChunkHighlight(tabId);
}

function scrollToChunkHighlight(): void {
  if (!('highlights' in CSS)) return;
  const hl = (CSS as any).highlights.get(CHUNK_HIGHLIGHT_NAME);
  const firstRange: Range | undefined = hl?.values()?.next()?.value;
  firstRange?.startContainer.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

const collapseWs = (s: string) => s.replace(/\s+/g, ' ');

function mapNormalizedToRaw(rawText: string, normalizedPos: number): number {
  let normIdx = 0;
  let rawIdx = 0;
  let inWhitespace = false;
  while (rawIdx < rawText.length && normIdx < normalizedPos) {
    const ch = rawText[rawIdx]!;
    if (/\s/.test(ch)) {
      if (!inWhitespace) {
        normIdx++;
        inWhitespace = true;
      }
      rawIdx++;
    } else {
      normIdx++;
      rawIdx++;
      inWhitespace = false;
    }
  }
  return rawIdx;
}

function applyHighlightRanges(
  textNodes: Text[],
  nodeOffsets: number[],
  rawStart: number,
  rawEnd: number,
  tabId: string,
): boolean {
  if (!('highlights' in CSS) || typeof (globalThis as any).Highlight === 'undefined') return false;

  const ranges: Range[] = [];
  for (let i = 0; i < textNodes.length; i++) {
    const node = textNodes[i]!;
    const nodeStart = nodeOffsets[i]!;
    const nodeLen = node.textContent?.length || 0;
    const nodeEnd = nodeStart + nodeLen;
    if (nodeEnd > rawStart && nodeStart < rawEnd) {
      const rangeStart = Math.max(0, rawStart - nodeStart);
      const rangeEnd = Math.min(nodeLen, rawEnd - nodeStart);
      const range = new Range();
      range.setStart(node, rangeStart);
      range.setEnd(node, rangeEnd);
      ranges.push(range);
    }
  }
  if (ranges.length === 0) return false;

  const HighlightCtor = (globalThis as any).Highlight;
  const highlight = new HighlightCtor(...ranges);
  (CSS as any).highlights.set(CHUNK_HIGHLIGHT_NAME, highlight);
  // Claim AFTER the write succeeds: a tab that failed to draw must not acquire
  // the right to delete the tab that did.
  claimChunkHighlight(tabId);
  return true;
}

function anchorMatchAndHighlight(
  container: Element,
  anchorStart: string,
  anchorEnd: string,
  anchorLength: number,
  tabId: string,
): boolean {
  const rawInner = container.textContent || '';
  const normalizedFull = collapseWs(rawInner);
  const normalizedStartStr = collapseWs(anchorStart).trim();
  const normalizedEndStr = anchorEnd ? collapseWs(anchorEnd).trim() : '';

  let highlightStart = -1;
  let highlightEnd = -1;

  const startIdx = normalizedFull.indexOf(normalizedStartStr);
  const endIdx = normalizedEndStr
    ? normalizedFull.indexOf(normalizedEndStr, startIdx !== -1 ? startIdx + normalizedStartStr.length : 0)
    : -1;

  if (startIdx !== -1 && endIdx !== -1) {
    // Both anchors found — most precise
    highlightStart = startIdx;
    highlightEnd = endIdx + normalizedEndStr.length;
  } else if (startIdx !== -1) {
    // Only start found — walk forward by chunk length
    highlightStart = startIdx;
    highlightEnd = Math.min(startIdx + anchorLength, normalizedFull.length);
  } else if (endIdx !== -1) {
    // Only end found — walk backward by chunk length
    highlightEnd = endIdx + normalizedEndStr.length;
    highlightStart = Math.max(highlightEnd - anchorLength, 0);
  } else {
    return false;
  }

  const rawStart = mapNormalizedToRaw(rawInner, highlightStart);
  const rawEnd = mapNormalizedToRaw(rawInner, highlightEnd);

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  const nodeOffsets: number[] = [];
  let offset = 0;
  let tNode: Text | null;
  while ((tNode = walker.nextNode() as Text | null)) {
    nodeOffsets.push(offset);
    textNodes.push(tNode);
    offset += tNode.textContent?.length || 0;
  }

  return applyHighlightRanges(textNodes, nodeOffsets, rawStart, rawEnd, tabId);
}

function waitForContent(sectionEl: HTMLElement, timeout = 500): Promise<Element | null> {
  const container = sectionEl.querySelector('.spl-content, .prose');
  if (container) return Promise.resolve(container);
  return new Promise(resolve => {
    const observer = new MutationObserver(() => {
      const el = sectionEl.querySelector('.spl-content, .prose');
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });
    observer.observe(sectionEl, { childList: true, subtree: true });
    setTimeout(() => { observer.disconnect(); resolve(null); }, timeout);
  });
}

// Module-level so the derived `sbaDocuments` keeps a stable identity when no
// payload is committed. It is passed straight to `VersionSelector`, whose
// `docGroups` memo lists it as a dependency — a fresh `[]` literal per render
// would invalidate that memo on every render.
// Frozen: it is a shared sentinel handed to a child, never a working array.
const EMPTY_SBA_DOCUMENTS: SBASummaryForDrug[] = [];
Object.freeze(EMPTY_SBA_DOCUMENTS);

type SbaListMode = 'classic' | 'submission';
/**
 * `transition-error` vs `load-error` is the failed request's `reason`, carried
 * into the status. Only a user-initiated toggle may show the transition-specific
 * copy ("Couldn't switch views…"); a mount-time load failure — including the
 * classic load of a user who never opted in — gets mode-neutral copy instead.
 */
type SbaListStatus = 'idle' | 'loading' | 'unavailable' | 'transition-error' | 'load-error';

interface SbaCycleInfo {
  selectedCycle: SBAReviewCycle | null;
  availableCycles: SBAReviewCycle[];
  unclassifiedCount: number;
  unclassifiedLetterCount: number;
  // Task A3/B1: labels `roll_up_labels` didn't place onto a submission,
  // categorized (loud vs. quiet). Mapped from `data.unmatched_labels` in
  // `fetchDrugSBAs` below; drives the VersionSelector B1 surface
  // independently of `availableCycles`.
  unmatchedLabels: UnmatchedLabel[];
}

/**
 * One atomic last-successful value. `documents` and `cycleInfo` are committed
 * TOGETHER so the rendered list and the rendered mode can never come from two
 * different responses. `drugId` is carried so a payload is never rendered
 * under a different drug (drug A's reviews on drug B).
 */
interface SbaListPayload {
  drugId: number;
  documents: SBASummaryForDrug[];
  reviewLetters: ApprovalLetterSummary[];
  cycleInfo: SbaCycleInfo | null;
}

const DrugDetailPage: React.FC = () => {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Drop a deep-link param this page has consumed.
   *
   * A citation URL carries TWO of them — `citeAnchors` (the cited sentences)
   * plus the family's own id param — and their effects fire in the SAME
   * commit. Each used to rewrite the URL from this render's `searchParams`, so
   * the second write put the first one's param straight BACK. That effect then
   * ran a second time, found the pending anchor it had already consumed, and
   * took its else-branch: the sentence highlight was set and then silently
   * cleared one commit later. The second write also reached the shell as an
   * address-bar change it had not issued, which it restored as a duplicate tab.
   *
   * `setSearchParams`'s functional form does NOT help — react-router passes it
   * the same render-snapshot (`nextInit(searchParams)`), not the live search.
   * So every drop in a commit is remembered and re-applied by the next one; the
   * last write of the commit therefore carries all of them.
   */
  const pendingDrops = useRef<string[]>([]);
  useEffect(() => { pendingDrops.current = []; }, [searchParams]);
  const dropParam = useCallback((name: string) => {
    pendingDrops.current.push(name);
    setSearchParams((prev) => {
      const cleaned = new URLSearchParams(prev);
      for (const n of pendingDrops.current) cleaned.delete(n);
      return cleaned;
    }, { replace: true });
  }, [setSearchParams]);

  // Every shell tab stays mounted, so anything global (document ids, the CSS
  // highlight registry) must be scoped to this tab's pane and to when it is
  // the visible one.
  const { isActive, rootRef, canGoBack, tabId } = useTab();
  const shell = useContext(ShellContext);
  const { pathname: tabPathname, search: tabSearch } = useLocation();
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  // Pane-relative sizing. Height chain: workspace column (flex-col) > flex-1 div >
  // TabPane (h-full overflow-auto = the scroller, definite height) > TabBody
  // (h-full) > this page's root (min-h-full: NOT definite, so a child's
  // `h-full` cannot resolve). The viewer <main> and the Comments panel need a
  // definite height that fits the scroller below the sticky header, so measure
  // the pane and the header instead of using 100vh (the TabStrip, and in narrow
  // mode the view toggle, sit above the pane).
  const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);
  const [paneFit, setPaneFit] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    const pane = rootRef.current;
    if (!pane || !headerEl || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // A hidden tab's pane measures 0: keep the last real size, or the viewer
      // renders at height 0 on re-activation and clamps its scroll to the top.
      if (pane.clientHeight === 0) return;
      const top = headerEl.offsetHeight;
      setPaneFit((prev) => {
        const height = Math.max(0, pane.clientHeight - top);
        return prev && prev.top === top && prev.height === height ? prev : { top, height };
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(pane);
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, [rootRef, headerEl]);
  // TWO conditions, and they catch different things.
  //
  //   ``isActiveRef``  a HIDDEN tab never clears — its unmount must leave the
  //                    visible tab's highlight alone (DrugDetailPage.shellTab).
  //   ownership        an ACTIVE tab does not delete a highlight a DIFFERENT
  //                    tab drew, which visibility alone cannot see: at
  //                    ``TAB_CAP`` a citation click evicts a tab, and the one
  //                    that survives is often not the one that drew.
  //
  // Ownership is not a replacement for the visibility guard. An inactive owner
  // declining to clear costs nothing: its ranges point into a pane that is
  // being torn down, so the entry dies with the DOM it referenced.
  const clearHighlights = useCallback(() => {
    if (isActiveRef.current) clearChunkHighlights(tabId);
  }, [tabId]);

  // Keyword/phrase to box at word level inside the regulatory doc viewers
  // (Ctrl+F-style), carried by the keyword-search deep-link's ``highlight``
  // param. Same param the US SPL path uses to highlight section text.
  const approvalLetterLandingPage = searchParams.get('source') === 'approval-letter'
    ? parseApprovalLetterLandingPage(searchParams.get('approvalLetterPage'))
    : undefined;
  const citationLandingPage = searchParams.get('source') !== 'approval-letter'
    ? parseApprovalLetterLandingPage(searchParams.get('citationPage'))
    : undefined;

  // Current drug ID, synced from the route
  const [currentDrugId, setCurrentDrugId] = useState<string>(urlId || '');

  // Sync currentDrugId when URL changes (e.g., direct navigation, back button)
  useEffect(() => {
    if (urlId && urlId !== currentDrugId) {
      setCurrentDrugId(urlId);
    }
  }, [urlId]);

  // Back walks this tab's own history; a tab opened straight onto the drug
  // (no history) goes Home.
  const handleBackToSearch = useCallback(() => {
    if (canGoBack) navigate(-1);
    else navigate('/');
  }, [navigate, canGoBack]);
  // Keyword tab this page was opened from. Captured when the URL brings a new
  // non-empty `from`, never cleared by a param removal: a version switch
  // rewrites the search params (handleUnifiedVersionChange) and drops `from`.
  const [fromTabId, setFromTabId] = useState<string | null>(null);
  const fromParam = searchParams.get('from');
  useEffect(() => {
    if (fromParam) setFromTabId(fromParam);
  }, [fromParam, urlId]);
  const [drug, setDrug] = useState<DrugDetail | null>(null);
  // Route id the loaded `drug` was fetched for: during an in-tab drug change
  // the previous drug stays loaded until the new fetch resolves.
  const [drugForId, setDrugForId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The route id whose metadata fetch has finished, whatever the outcome.
  // `isLoading` cannot answer "is `drug` the route's drug?" — it is set INSIDE
  // `fetchDrug` (so the render between a route change and the effect still
  // reads false) and the non-numeric/set_id path sets and clears it in the same
  // tick. This is compared against `currentDrugId` during render instead, and
  // is written on EVERY exit path of `fetchDrug` (success, failure, and the
  // set_id throw) so a failed load never strands a control disabled forever.
  const [settledDrugRouteId, setSettledDrugRouteId] = useState<string | null>(null);

  // Versioning state
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [versionDetail, setVersionDetail] = useState<VersionDetailResponse | null>(null);
  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Comment highlighting state
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);

  // ONLYOFFICE viewer: handle ref for programmatic control (e.g. scrollToComment in Task 8)
  const viewerHandleRef = useRef<OnlyOfficeViewerHandle | null>(null);
  // Feature flag: true = render OnlyOfficeDraftViewer; flips to false (legacy
  // per-section DraftContentRenderer) if ONLYOFFICE isn't configured/usable in
  // this environment (e.g. prod without a Document Server) — see onUnavailable.
  const [useOnlyOfficeViewer, setUseOnlyOfficeViewer] = useState(true);

  // The ONLYOFFICE viewer renders comment threads natively in the document, so
  // our right-sidebar comments panel is redundant when the viewer is active.
  const showDraftCommentsPanel = !useOnlyOfficeViewer;

  // Search result deep linking state (Jan 2026)
  // When user clicks a search result, we auto-scroll to the matched section and highlight terms
  // Find text (typed) -> committedText (debounced / Enter) -> activeTerms. The
  // URL keyword is captured in deepLink so a version switch (which rewrites the
  // URL params) keeps the toggle and the terms.
  const [findText, setFindText] = useState('');
  const [committedText, setCommittedText] = useState('');
  const [deepLink, setDeepLink] = useState<{ highlight: string; jpja: boolean; phrase: boolean; englishExact: boolean } | null>(null);
  const kwHighlightTerm = searchParams.get('highlight') ?? (deepLink?.englishExact ? deepLink.highlight : null);
  const [highlightsOpen, setHighlightsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'full' | 'matches'>('full');
  const [snapshot, setSnapshot] = useState<{ counts: Map<string, number>; keep: Set<string> } | null>(null);
  // Citation prep (R4.6): a citation first forces Full label (and opens
  // Highlights when it targets that row); `run` fires after that commit.
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  const highlightsOpenRef = useRef(highlightsOpen);
  highlightsOpenRef.current = highlightsOpen;
  const highlightsRowRef = useRef<{ sectionNumber: string | null } | null>(null);
  const afterCommitRef = useRef<(() => void)[]>([]);
  const [commitTick, setCommitTick] = useState(0);
  const withFullLabel = useCallback((sectionNumber: string | null | undefined, run: () => void) => {
    const hl = highlightsRowRef.current;
    const needsHighlights = !!hl && !highlightsOpenRef.current
      && (!sectionNumber || sectionNumber === 'Unknown' || sectionNumber === hl.sectionNumber);
    if (viewModeRef.current === 'full' && !needsHighlights) {
      run();
      return;
    }
    setViewMode('full');
    setSnapshot(null);
    if (needsHighlights) setHighlightsOpen(true);
    afterCommitRef.current.push(run);
    setCommitTick((t) => t + 1);
  }, []);
  useEffect(() => {
    const queued = afterCommitRef.current;
    afterCommitRef.current = [];
    queued.forEach((fn) => fn());
  }, [commitTick]);
  // Collapsible label rows (K1-K9): the set holds each row's OWN collapsed flag;
  // descendants are hidden at render by prefix (K3). `rowsRef` mirrors rowsModel.
  const [collapsedRows, setCollapsedRows] = useState<ReadonlySet<string>>(() => new Set());
  const rowsRef = useRef<{ key: string; sectionNumber: string | null }[]>([]);
  // Expand a row and its ancestors; `run` (the scroll) fires after that commit.
  const revealRow = useCallback((key: string, run?: () => void) => {
    const rows = rowsRef.current;
    const num = rows.find((r) => r.key === key)?.sectionNumber;
    const open = new Set([key, ...rows.filter((r) => num && r.sectionNumber && num.startsWith(r.sectionNumber + '.')).map((r) => r.key)]);
    setCollapsedRows((prev) => ([...open].some((k) => prev.has(k)) ? new Set([...prev].filter((k) => !open.has(k))) : prev));
    if (run) {
      afterCommitRef.current.push(run);
      setCommitTick((t) => t + 1);
    }
  }, []);
  const pendingMoveRef = useRef<{ dir: 1 | -1; key: string } | null>(null);
  const [articleRoot, setArticleRoot] = useState<HTMLElement | null>(null);
  const highlightTerms = useMemo(
    () => parseLabelTerms(committedText, { jpja: !!deepLink?.jpja, phrase: !!deepLink?.phrase }),
    [committedText, deepLink],
  );
  const [targetSectionNumber, setTargetSectionNumber] = useState<string | null>(null);
  // Multi-section highlighting: all sections with matching evidence from V2 search
  const [highlightedSections, setHighlightedSections] = useState<Set<string>>(new Set());
  // Evidence chunk text for chunk-level highlighting (from V2 search click-through)
  const [evidenceChunkText, setEvidenceChunkText] = useState<string | null>(null);

  // Captured keyword state belongs to one drug. A route change to another drug
  // reuses this component, so reset it during render (before effects run); the
  // capture effects, keyed on urlId, then re-capture from the new URL. A version
  // switch of the SAME drug keeps it (R2.2).
  const [capturedForId, setCapturedForId] = useState(urlId);
  if (capturedForId !== urlId) {
    setCapturedForId(urlId);
    setFromTabId(null);
    setFindText('');
    setCommittedText('');
    setDeepLink(null);
    setViewMode('full');
    setSnapshot(null);
    setHighlightedSections(new Set());
    setEvidenceChunkText(null);
  }

  // Version comparison state
  const [adjacentVersions, setAdjacentVersions] = useState<AdjacentVersionsResponse | null>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showJpCompare, setShowJpCompare] = useState(false);
  // JP main-label version control (lifted here so the insert picker sits in the
  // header beside the doc-type selector, and the Compare launcher sits top-right).
  // A JP product holds several PARALLEL package inserts (lineages), each with its
  // own editions; pickers name the insert and never mix two of them.
  const [jpLineages, setJpLineages] = useState<JpLineage[]>([]);
  const [jpViewDoc, setJpViewDoc] = useState<number | null>(null); // jp_documents.id; null = current
  // launcher "other" version as `${lineage_key}|${version_number}`
  const [jpCompareTarget, setJpCompareTarget] = useState<string | null>(null);

  // AU (TGA PI) main-label version compare (single-language English PDF; EU-style panes).
  const [showAuCompare, setShowAuCompare] = useState(false);
  const [auVersions, setAuVersions] = useState<
    { version_number: number; tga_code: string | null; revision_date: string | null; is_current: boolean; document_id: number }[]
  >([]);
  // Which AU version to render in the viewer; null = current. Points AUViewerEmbed
  // at a historical version's document_id (#99 "view old version" toggle).
  const [auViewVersion, setAuViewVersion] = useState<number | null>(null);

  // EU unified version timeline (current + consolidated historical) — drives the
  // EU "view any version" dropdown. legacy_historical_id = the eu_historical_documents.id
  // the existing EuHistoricalViewer opens (?euhdoc=); current rows carry null.
  const [euVersions, setEuVersions] = useState<
    { version_number: number; source_version_key: string; effective_date: string | null;
      is_current: boolean; document_id: number; legacy_historical_id: number | null }[]
  >([]);

  // Load the JP version timeline (drives the header version picker + Compare launcher).
  useEffect(() => {
    // A doc id / insert key belongs to ONE drug: drop the previous drug's picks.
    setJpViewDoc(null);
    setJpCompareTarget(null);
    if (drug?.region !== 'JP' || !drug?.id) { setJpLineages([]); return; }
    let alive = true;
    authApi.getDrugJpVersions(drug.id)
      .then((d: { lineages?: JpLineage[] }) => { if (alive) setJpLineages(d.lineages || []); })
      .catch(() => { if (alive) setJpLineages([]); });
    return () => { alive = false; };
  }, [drug?.region, drug?.id]);

  // Load the AU version timeline (drives the Compare launcher; hidden unless >=2 versions).
  useEffect(() => {
    if (drug?.region !== 'AU' || !drug?.id) { setAuVersions([]); return; }
    let alive = true;
    setAuViewVersion(null);   // reset the "view old version" picker on drug change
    authApi.getDrugAuVersions(drug.id)
      .then((d: { versions?: typeof auVersions }) => { if (alive) setAuVersions(d.versions || []); })
      .catch(() => { if (alive) setAuVersions([]); });
    return () => { alive = false; };
  }, [drug?.region, drug?.id]);

  // Load the EU version timeline (drives the "view any version" dropdown; >=2 to show).
  useEffect(() => {
    if (drug?.region !== 'EU' || !drug?.id) { setEuVersions([]); return; }
    let alive = true;
    authApi.getDrugEuVersions(drug.id)
      .then((d: { versions?: typeof euVersions }) => { if (alive) setEuVersions(d.versions || []); })
      .catch(() => { if (alive) setEuVersions([]); });
    return () => { alive = false; };
  }, [drug?.region, drug?.id]);

  // EU single-version viewer addressing. The STABLE public handle is
  // ?euver=<source_version_key> (survives re-ingest). ?euhdoc=<legacy id> stays
  // as a back-compat alias so old deep-links/citations never break. The
  // EuHistoricalViewer still needs the legacy eu_historical_documents.id (Phase B
  // repoints the viewer itself), so resolve the stable key → that id via the
  // already-loaded version list.
  const euVerParam = searchParams.get('euver');
  const euHdocParam = Number(searchParams.get('euhdoc')) || 0;
  const euViewerDocId = euHdocParam > 0
    ? euHdocParam
    : (euVerParam
        ? (euVersions.find((v) => v.source_version_key === euVerParam)?.legacy_historical_id || 0)
        : 0);
  const euHistoricalOpen = searchParams.get('euhview') === '1' && euViewerDocId > 0;
  const closeEuHistoricalView = () => {
    const c = new URLSearchParams(searchParams);
    ['euhview', 'euhdoc', 'euver', 'euhpage', 'euhy', 'euhpres', 'euhterm', 'euhsection'].forEach((k) => c.delete(k));
    setSearchParams(c, { replace: true });
  };
  const [comparisonVersions, setComparisonVersions] = useState<{ a: number; b: number } | null>(null);

  // Unified versions state (3-table architecture: FDA current + historical + drafts)
  const [allVersionsData, setAllVersionsData] = useState<AllVersionsResponse | null>(null);

  // Track selected FDA historical version (null = viewing current FDA, number = viewing historical)
  const [selectedHistoricalId, setSelectedHistoricalId] = useState<number | null>(null);
  // Historical version data (when viewing an FDA historical version)
  const [historicalVersionData, setHistoricalVersionData] = useState<any | null>(null);
  const [_isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [historicalVersionError, setHistoricalVersionError] = useState<string | null>(null);

  // SBA (FDA Review) document state
  const [selectedSbaId, setSelectedSbaId] = useState<number | null>(null);
  const [selectedApprovalLetterId, setSelectedApprovalLetterId] = useState<number | null>(null);
  const [reviewLettersLoaded, setReviewLettersLoaded] = useState(false);
  const [sbaDocDetail, setSbaDocDetail] = useState<SBADocumentDetail | null>(null);
  const [isLoadingSba, setIsLoadingSba] = useState(false);
  const [sbaDetailError, setSbaDetailError] = useState<string | null>(null);
  const sbaDetailGenerationRef = useRef(0);

  // Desired request mode. This remains independent from the active UI mode,
  // which is derived from the last committed response below.
  const [useSubmissionView, setUseSubmissionView] = useSubmissionViewPreference();

  // The `[currentDrugId]` effect's async closure captures this value when the
  // effect runs, but the user can toggle during `await getDrug(...)`. Read the
  // preference through a ref so an in-flight mount fetch uses the CURRENT
  // choice rather than the one captured before the await — otherwise the new
  // drug loads the opposite view from what the preference now says.
  const submissionViewPrefRef = useRef(useSubmissionView);
  useEffect(() => {
    submissionViewPrefRef.current = useSubmissionView;
  }, [useSubmissionView]);

  // Admin gate for the Beta opt-in. `useDbUser` fires its OWN `/auth/sync`
  // round-trip per hook instance (no cache, no shared context) starting at
  // `isAdmin: false, loading: true`, so on a cold page load the drug fetch
  // below always wins the race and `isAdmin` is still false when it runs.
  // On CLIENT-SIDE navigation it has already resolved (its deps are
  // `[user, authLoading]`, neither of which changes on a route change), which
  // is why the mount fetch reads the ref: an admin navigating between drugs
  // then lands straight in Submission View with no flip and no second request.
  //
  // This is a UX gate, not a security boundary — the backend routes stay open
  // to every allowlisted user (see `useIsAdmin`'s own note). It hides an
  // unfinished surface; it does not protect data.
  // `loading` is no longer destructured: the promotion effect used to wait
  // on it to avoid acting before /auth/sync landed. Submission view no
  // longer depends on admin, so there is nothing to wait for.
  const { isAdmin } = useDbUser();
  const isAdminRef = useRef(isAdmin);
  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);

  // US-Drugs@FDA drug page ("Layout A"). Inert unless the region is visible
  // to this user: both endpoints 403 otherwise, so nothing here may run for a
  // user who cannot see US_FDA. When visible, EVERY US / US_FDA page is in the
  // new mode: no /sbas roll-up (letters/reviews come from us_fda_submissions),
  // no submission UI; the Drugs@FDA picker appears when a product is linked.
  const usFdaVisible = isUsFdaVisible(isAdmin);
  const usFdaMode = !!drug && usFdaVisible && (drug.region === 'US' || drug.region === 'US_FDA');
  const [usFdaData, setUsFdaData] = useState<{
    drugId: number;
    dailymed: UsFdaProductRef[];
    apps: DrugsAtFdaApp[];
  } | null>(null);

  const [sbaListPayload, setSbaListPayload] = useState<SbaListPayload | null>(null);
  const [sbaListStatus, setSbaListStatus] = useState<SbaListStatus>('idle');

  // Newest-request-wins plumbing. The generation counter is the real guard;
  // the AbortController is an optimization that also stops the network call.
  // A response may commit ONLY when its generation is still current AND its
  // drug still matches — abort can be delivered after a response completes.
  const sbaAbortRef = useRef<AbortController | null>(null);
  const sbaGenerationRef = useRef(0);
  const sbaDrugIdRef = useRef<number | null>(null);

  // `commitSbaPayload` runs inside an async closure created before the
  // response arrived; read the selection through a ref so reconciliation
  // never compares against a stale id.
  const selectedSbaIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedSbaIdRef.current = selectedSbaId;
  }, [selectedSbaId]);

  // Is the drug currently on screen the drug the route asks for? Route entry
  // clears the SBA payload and repoints `sbaDrugIdRef` at the NEW drug, but
  // `drug` — and the header controls row that hosts the Beta toggle — keeps
  // rendering the PREVIOUS drug until `getDrug` settles, and the center-pane
  // loading overlay (`absolute inset-0`) does not cover that header. Without
  // this, the toggle stays clickable for a drug the user has already left: a
  // dead click that still writes the preference.
  const drugRouteSettled = settledDrugRouteId === currentDrugId;

  // Derived, never stored separately — this is what makes the commit atomic.
  // US-Drugs@FDA mode ignores any /sbas payload (one may have landed on a
  // cold load, before /auth/sync resolved the admin flag).
  const sbaPayloadLive = !!sbaListPayload && !!drug && sbaListPayload.drugId === drug.id && !usFdaMode;
  const sbaDocuments =
    sbaPayloadLive
      ? sbaListPayload!.documents
      : EMPTY_SBA_DOCUMENTS;
  const reviewLetters =
    sbaPayloadLive
      ? sbaListPayload!.reviewLetters
      : [];
  const sbaCycleInfo =
    sbaPayloadLive
      ? sbaListPayload!.cycleInfo
      : null;

  const pinnedSbaDocument = useMemo<SBASummaryForDrug | null>(() => {
    if (
      !sbaDocDetail
      || selectedSbaId == null
      || sbaDocDetail.id !== selectedSbaId
      || sbaDocuments.some(document => document.id === selectedSbaId)
    ) {
      return null;
    }
    return {
      id: sbaDocDetail.id,
      trade_name: sbaDocDetail.trade_name,
      established_name: sbaDocDetail.established_name,
      application_type: sbaDocDetail.application_type,
      application_number: sbaDocDetail.application_number,
      applicant: sbaDocDetail.applicant,
      regulatory_action: sbaDocDetail.regulatory_action,
      total_sections: sbaDocDetail.total_sections,
      priority_review: sbaDocDetail.priority_review,
      ingestion_status: sbaDocDetail.ingestion_status,
      created_at: sbaDocDetail.created_at,
      doc_type: sbaDocDetail.doc_type ?? sbaDocDetail.effective_doc_type ?? null,
      // Field absence can be a rolling-deploy payload, so do not call it
      // "Other Review" unless the backend says so.
      doc_type_label:
        sbaDocDetail.doc_type_label
        ?? sbaDocDetail.effective_doc_type_label
        ?? 'FDA Review',
      fda_submission_id: sbaDocDetail.fda_submission_id ?? null,
      submission_type: sbaDocDetail.submission_type ?? null,
      submission_number: sbaDocDetail.submission_number ?? null,
      submission_source: sbaDocDetail.submission_source ?? null,
      is_multi_submission: sbaDocDetail.is_multi_submission ?? false,
      submission_status_date: sbaDocDetail.submission_status_date ?? null,
    };
  }, [sbaDocDetail, sbaDocuments, selectedSbaId]);

  // EPAR (EMA Review) document state — mirrors SBA state above; the
  // EPAR backend exposes identical response shapes so we reuse the
  // SBASummaryForDrug type.  Selection is mutually exclusive with
  // SBA / Historical / Version / FDA Letter — the existing
  // ``isViewing*`` guards below add ``selectedEparId !== null`` to
  // every branch.
  const [selectedEparId, setSelectedEparId] = useState<number | null>(null);
  const [eparDocuments, setEparDocuments] = useState<SBASummaryForDrug[]>([]);
  const [eparDocDetail, setEparDocDetail] = useState<SBADocumentDetail | null>(null);
  const [isLoadingEpar, setIsLoadingEpar] = useState(false);

  // UK MHRA PIL (Patient Information Leaflet PDF) state — mirrors SBA/EPAR.
  // The backend response shape is intentionally similar (id, trade_name,
  // doc_type, doc_type_label, total_sections, ingestion_status,
  // created_at) plus PIL-specific fields (pmid, pil_version_str,
  // sor_pdf_uri, ocr_pages, parse_method).  We reuse the SBASummaryForDrug
  // type because the dropdown only reads the shared fields; PIL-specific
  // fields are accessed via type-narrowing where needed.  Selection is
  // mutually exclusive with SBA / EPAR / Historical / Version / FDA Letter
  // — the existing ``isViewing*`` guards below add
  // ``selectedUkPilId !== null`` to every branch.
  const [selectedUkPilId, setSelectedUkPilId] = useState<number | null>(null);
  const [ukPilDocuments, setUkPilDocuments] = useState<SBASummaryForDrug[]>([]);
  // UK NICE-HTA demo controls (UK-NICE view only): placeholder module/corpus
  // picklists — only Commercial Intelligence -> NICE Assessments is wired; the
  // rest are inert scaffolding showing the taxonomy.
  // Default first state = Regulatory Affairs → UK SmPC, so landing on the drug
  // shows the SmPC label (not the NICE docs). The user switches to Commercial
  // Intelligence → NICE Assessments to see the 6 NICE docs.
  const [niceModule, setNiceModule] = useState('Regulatory Affairs');
  const [niceCorpus, setNiceCorpus] = useState('UK SmPC');
  const niceInitRef = useRef(false);
  // CA Health Canada Product Monograph state — mirrors the UK PIL
  // state above.  Doc objects reuse SBASummaryForDrug (dropdown reads
  // shared fields); PM-specific fields (pm_code, pm_version_str,
  // generic_name, pdf_page_count, total_sections) are accessed via
  // type-narrowing where needed.  Selection is mutually exclusive with
  // SBA / EPAR / UK PIL / Historical / Version / FDA Letter — the
  // ``isViewing*`` guards below add ``selectedCaPmId === null`` to
  // every branch.
  const [selectedCaPmId, setSelectedCaPmId] = useState<number | null>(null);
  const [caPmDocuments, setCaPmDocuments] = useState<SBASummaryForDrug[]>([]);
  // AU TGA (PI / CMI) state — mirrors the CA PM state above.  Doc
  // objects reuse SBASummaryForDrug (dropdown reads shared fields);
  // AU is a two-doc-type family — each document's `doc_type` is "pi"
  // or "cmi", carried on the shared shape and accessed via
  // type-narrowing where needed.  Selection is mutually exclusive
  // with SBA / EPAR / UK PIL / CA PM / Historical / Version / FDA
  // Letter — the ``isViewing*`` guards below add
  // ``selectedAuId === null`` to every branch.
  const [selectedAuId, setSelectedAuId] = useState<number | null>(null);
  const [auDocuments, setAuDocuments] = useState<SBASummaryForDrug[]>([]);
  // EU EMA SmPC state — mirrors the AU TGA state above.  EU is a
  // single-doc-type family ("smpc"); the dropdown reads shared fields.
  // Selection is mutually exclusive with every other regulatory family
  // — the ``isViewing*`` guards add ``selectedEuId === null``.
  const [selectedEuId, setSelectedEuId] = useState<number | null>(null);
  const [euDocuments, setEuDocuments] = useState<SBASummaryForDrug[]>([]);
  // Has the EU SmPC fetch resolved yet (success OR empty)?  An EU drug's
  // main label lives in eu_documents → the viewer auto-lands on the SmPC PDF
  // once this loads.  Until then we must NOT paint the legacy section view,
  // or it flashes for a frame before the PDF viewer mounts.  Reset to false
  // on every drug load; set true once fetchDrugEUDocs resolves.
  const [euDocsLoaded, setEuDocsLoaded] = useState(false);
  // AU twin of euDocsLoaded: lets the stale-id redirect tell "still loading"
  // from "loaded, id not in the list".  Reset per drug load; set in
  // fetchDrugAUDocs once it resolves.
  const [auDocsLoaded, setAuDocsLoaded] = useState(false);
  // Drug whose AU/EU doc lists are wanted.  Nulled synchronously on a route
  // change and set just before the list fetches, so a late response for the
  // previous drug is dropped instead of feeding the stale-id redirect.
  const regDocsDrugIdRef = useRef<number | null>(null);
  // JP PMDA supplementary-PDF state — mirrors the AU state above. JP is
  // a multi-doc-type family (patient leaflet / interview form / review
  // report / RMP); the dropdown reads shared fields. Selected via the
  // VersionSelector dropdown (jppdfdoc-<id>); when null the JP XML label
  // (JPSectionView) renders.
  const [selectedJpPdfId, setSelectedJpPdfId] = useState<number | null>(null);
  const [jpPdfDocuments, setJpPdfDocuments] = useState<SBASummaryForDrug[]>([]);
  // CN NMPA supplementary-PDF state — mirrors the JP state above.
  // Region='CN' drugs; the doc list + viewer are GA for all users since
  // 2026-08-31 (backend gate: ZH_PDF_ADMIN_ONLY, zh_pdf_routes.py).
  const [selectedZhPdfId, setSelectedZhPdfId] = useState<number | null>(null);
  const [zhPdfDocuments, setZhPdfDocuments] = useState<SBASummaryForDrug[]>([]);
  // Region='CH' Swissmedic labels (ch_pdf). Admin-only during the staging canary
  // (backend gate: CH_PDF_ADMIN_ONLY) — non-admins get an empty list.
  const [selectedChPdfId, setSelectedChPdfId] = useState<number | null>(null);
  const [chPdfDocuments, setChPdfDocuments] = useState<SBASummaryForDrug[]>([]);
  // Region='DE' BfArM labels (de_pdf). Admin-only canary (DE_PDF_ADMIN_ONLY).
  const [selectedDePdfId, setSelectedDePdfId] = useState<number | null>(null);
  // The DE / IE / IT lists share one slot: until they load, a deep-linked doc
  // id has no family, and guessing de_pdf fetched the same-id German doc.
  const [dePdfDocsLoaded, setDePdfDocsLoaded] = useState(false);
  const [dePdfDocuments, setDePdfDocuments] = useState<(SBASummaryForDrug & {
    version_str?: string | null; family?: LabelPdfFamily; part_title?: string | null })[]>([]);
  // US Drugs@FDA label PDFs (fda_label_pdf). Admin-only (FDA_LABEL_PDF_ADMIN_ONLY).
  // NOT region-gated like de_pdf/ch_pdf/fr_html: these are region='US' drugs,
  // where the SPL label viewer already lives, so this is an ADDITIONAL document
  // family for a US drug — the sba/epar shape — not a region's only view.
  const [selectedFdaLabelPdfId, setSelectedFdaLabelPdfId] = useState<number | null>(null);
  const [fdaLabelPdfDocuments, setFdaLabelPdfDocuments] = useState<
    (SBASummaryForDrug & { version_str?: string | null; action_date?: string | null })[]>([]);
  // Region='FR' ANSM labels (fr_html). Admin-only canary (FR_HTML_ADMIN_ONLY → 403).
  const [selectedFrHtmlId, setSelectedFrHtmlId] = useState<number | null>(null);
  const [frHtmlDocuments, setFrHtmlDocuments] = useState<
    { id: number; doc_type: string; doc_type_label: string; updated_source?: string | null }[]>([]);
  // Phase 10c removed the pre-viewer SBA accordion state —
  // ``sbaSectionContent`` / ``loadingSbaSections`` cached per-
  // section content for accordion expansion, and
  // ``sbaTargetSectionId`` / ``sbaHighlightRanges`` drove
  // char-range DOM highlighting.  ``<SBAViewerEmbed>`` handles
  // all of that natively via page+bbox rendering; the click-
  // through URL shape is now ``?sbaChunkIds=1,2,3`` which
  // flows into the viewer's ``highlightChunkIds`` prop.

  // FDA Letter state
  const [fdaLetters, setFdaLetters] = useState<FDALetterSummary[]>([]);
  const [selectedFdaLetterId, setSelectedFdaLetterId] = useState<number | null>(null);
  const [fdaLetterDetail, setFdaLetterDetail] = useState<FDALetterDetail | null>(null);
  const [isLoadingFdaLetter, setIsLoadingFdaLetter] = useState(false);
  const [fdaLetterSectionContent, setFdaLetterSectionContent] = useState<Record<number, { content_html?: string; content_text?: string }>>({});
  const [loadingFdaLetterSections, setLoadingFdaLetterSections] = useState<Set<number>>(new Set());
  // Phase 7: chunk id passed to FdaLetterPageViewer for the bbox-highlight
  // overlay.  Set by handleFdaLetterCitationClick on citation chip click;
  // cleared when the user navigates away from the FDA-letter view.
  const [fdaLetterActiveChunkId, setFdaLetterActiveChunkId] = useState<number | null>(null);

  // Paged layout state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  // JP main-label sections (region='JP') — lifted from JPSectionView so
  // the shared "Contents" TOC dropdown can list them.  jpTemplate is the
  // full canonical PMDA template (present + absent) so the TOC shows the
  // whole scheme with absent sections greyed out.
  const [jpSections, setJpSections] = useState<{ section_number: string; title: string; is_parent: boolean }[]>([]);
  const [jpTemplate, setJpTemplate] = useState<{ section_number: string; title_en: string; title_ja: string; present: boolean }[]>([]);
  const handleJpSections = useCallback((data: {
    present: { section_number: string; title: string; is_parent: boolean }[];
    template: { section_number: string; title_en: string; title_ja: string; present: boolean }[];
  }) => {
    setJpSections(data.present);
    setJpTemplate(data.template);
  }, []);
  // Japanese-text keyword hit (?source=jpja&section=&highlight=, plan
  // 2026-10-09 §13 / Q3 proposal): the JP viewer opens on the Japanese side
  // with the typed terms marked, then expands + scrolls to the matched section
  // once its sections have loaded (the same jp-goto-section event the
  // Contents dropdown uses), and on to the first mark.
  const jpJaDeepLink = searchParams.get('source') === 'jpja';
  const jpJaSection = jpJaDeepLink ? searchParams.get('section') : null;
  const jpJaHighlight = jpJaDeepLink ? (searchParams.get('highlight') ?? '') : '';
  const jpJaTerms = useMemo(() => jpJaHighlight.split(/\s+/).filter(Boolean), [jpJaHighlight]);
  const jpJaScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    // No section = a Near row opened before its passages loaded: scroll to the
    // first highlighted word anywhere in the document instead.
    if ((!jpJaSection && jpJaTerms.length === 0) || jpSections.length === 0) return undefined;
    const key = `${currentDrugId}:${jpJaSection ?? ''}:${jpJaHighlight}`;
    if (jpJaScrolledRef.current === key) return undefined;
    // Open the section, then poll for its first mark (the subsection only
    // mounts after the expand re-renders). The link counts as handled only
    // once it scrolled, so a re-run that cancels the poll tries again.
    let timer = window.setTimeout(() => {
      if (jpJaSection) window.dispatchEvent(new CustomEvent('jp-goto-section', { detail: jpJaSection }));
      let tries = 0;
      const toMark = () => {
        const mark = jpJaSection
          ? findInTab(rootRef, `jp-sec-${jpJaSection}`)?.querySelector('mark')
          : (rootRef.current ?? document).querySelector('[id^="jp-sec-"] mark');
        if (mark) {
          jpJaScrolledRef.current = key;
          mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (tries++ < 30) {
          timer = window.setTimeout(toMark, 100);
        }
      };
      timer = window.setTimeout(toMark, 100);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [jpJaSection, jpJaHighlight, jpJaTerms, jpSections, currentDrugId, rootRef]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [showTocDropdown, setShowTocDropdown] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  // Section NUMBER an origin-version citation asked to open, held until the
  // historical version's sections finish loading. See the effect that consumes
  // it — the accordion rows don't exist at click time, so the scroll cannot
  // happen inline.
  const pendingHistoricalSectionRef = useRef<string | null>(null);
  const sbaViewerRef = useRef<SBAViewerHandle>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sbaHighlightChunkIds, setSbaHighlightChunkIds] = useState<number[]>([]);
  // Mirror of sbaHighlightChunkIds for the EPAR viewer.  Populated by the
  // ``?eparChunkIds=...`` URL-param parser below, consumed by
  // ``<EPARViewerEmbed highlightChunkIds={...}>``.
  const [eparHighlightChunkIds, setEparHighlightChunkIds] = useState<number[]>([]);
  // Mirror for the JP PMDA supplementary (jp_pdf) viewer.  Populated by the
  // ``?jpPdfChunkIds=...`` URL-param parser below, consumed by
  // ``<JPPdfViewerEmbed highlightChunkIds={...}>``.
  const [jpPdfHighlightChunkIds, setJpPdfHighlightChunkIds] = useState<number[]>([]);
  // Mirror for the CN NMPA supplementary (zh_pdf) viewer.  Populated by the
  // ``?zhPdfChunkIds=...`` URL-param parser below, consumed by
  // ``<ZhPdfViewerEmbed highlightChunkIds={...}>``.
  const [zhPdfHighlightChunkIds, setZhPdfHighlightChunkIds] = useState<number[]>([]);
  // Same for the Swiss (ch_pdf) viewer: ``?chPdfChunkIds=...`` from a Cogni result.
  const [chPdfHighlightChunkIds, setChPdfHighlightChunkIds] = useState<number[]>([]);
  const [dePdfHighlightChunkIds, setDePdfHighlightChunkIds] = useState<number[]>([]);
  const [fdaLabelPdfHighlightChunkIds, setFdaLabelPdfHighlightChunkIds] = useState<number[]>([]);
  // Same shape for UK PIL.  Driven by ``handleRegulatoryCitationClick``
  // when a CIPI citation lands on a UK PIL chunk; consumed by
  // ``<UKPILViewerEmbed highlightChunkIds={...}>``.
  const [ukPilHighlightChunkIds, setUkPilHighlightChunkIds] = useState<number[]>([]);
  // Same shape for CA PM.  Driven by ``handleRegulatoryCitationClick``
  // when a citation lands on a CA PM chunk; consumed by
  // ``<CAPMViewerEmbed highlightChunkIds={...}>``.
  const [caPmHighlightChunkIds, setCaPmHighlightChunkIds] = useState<number[]>([]);
  // Same shape for AU TGA.  Driven by ``handleRegulatoryCitationClick``
  // when a citation lands on an AU chunk; consumed by
  // ``<AUViewerEmbed highlightChunkIds={...}>``.
  const [auHighlightChunkIds, setAuHighlightChunkIds] = useState<number[]>([]);
  // Same shape for EU EMA SmPC.  Driven by
  // ``handleRegulatoryCitationClick`` when a citation lands on an EU
  // chunk; consumed by ``<EUViewerEmbed highlightChunkIds={...}>``.
  const [euHighlightChunkIds, setEuHighlightChunkIds] = useState<number[]>([]);
  const [approvalLetterHighlightChunkIds, setApprovalLetterHighlightChunkIds] = useState<number[]>([]);
  // Per-chunk anchor phrases (citation.anchor_start / anchor_end).
  // When present, the viewer tightens the highlight from chunk-bbox
  // (paragraph) to a sentence-line bbox computed by matching the
  // anchor text against the page's text_spans.  Falls back silently
  // when text_spans don't contain a match (PIL footer bullets, OCR
  // pages, etc).  One map per family so we don't have to demux on
  // version_type at click time.
  const [sbaChunkAnchors, setSbaChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [eparChunkAnchors, setEparChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [ukPilChunkAnchors, setUkPilChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [caPmChunkAnchors, setCaPmChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [auChunkAnchors, setAuChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [euChunkAnchors, setEuChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  const [approvalLetterChunkAnchors, setApprovalLetterChunkAnchors] = useState<Record<number, ChunkAnchor[]>>({});
  // PAGE-KEYED citation target (progressive read). Deliberately ONE state, not
  // one per family like the chunk anchors above: a page anchor carries its own
  // family, and only one citation is active at a time, so clicking the next one
  // replaces this wholesale. That is what the per-family split exists to prevent
  // — a stale EPAR anchor surviving into a later SBA click — so tagging the
  // value with its family gets the same guarantee from one piece of state.
  // ALL the cited spans, not one. This held a single `{page, anchor}`, so a
  // citation whose evidence spanned two pages — or sat twice on one page — kept
  // only the first and the rest were never drawn.
  // SCOPED TO A DOCUMENT, not just a family. This held `{family, anchors}`
  // and nothing ever cleared it on a document change, so anchors planned for
  // one review were matched against the NEXT review's pages the moment the
  // reader switched documents (the HA REVIEW dropdown, a tab, or either of the
  // defensive paths that already clear the CHUNK map and missed this one).
  //
  // That is how a confident highlight ends up on a document whose citation was
  // never clicked: the sentence happens to occur there too, so it resolves and
  // paints. Observed on Creon doc 39423 page 7, where the matcher itself
  // returned ZERO rects for the hovered citation while a box was on screen.
  // A wrong box is the one outcome this whole subsystem exists to prevent.
  const [citationPageAnchor, setCitationPageAnchor] = useState<{
    family: string;
    documentId: number | null;
    anchors: { page: number; anchor: ChunkAnchor }[];
  } | null>(null);
  // Derived per embed. Two helpers rather than the same ternary at seven call
  // sites, where one typo'd family string is a silently-missing highlight.
  /**
   * Seed the cited sentence for the viewer, keyed by chunk, BEFORE navigating.
   *
   * The per-family setters further down `handleRegulatoryCitationClick` sit
   * about a hundred lines below its `navigate(href); return;`, so for any
   * citation carrying drug_id + document_id — which every regulatory citation
   * does — they never ran. Only approval_letter escaped, through a pending-ref
   * stashed above the navigate.
   *
   * Without an entry here the viewer computes no tight bbox, and with the
   * stored-rectangle flag off there is no fallback, so PageRenderer renders
   * nothing at all. Measured live: `highlights: 0` while the backend had
   * resolved 3/3 spans and the anchor matched the page's real word boxes.
   *
   * Plain state is sufficient: this navigate is a same-route parameter change,
   * so the page does not unmount. approval_letter is deliberately NOT handled
   * here — it switches document first and its pending-ref path already applies
   * the anchor once the new document's chunks arrive; setting it twice would
   * fight that effect's own `{}` reset.
   */
  /** Route an anchor map to the viewer that will draw it. Shared by the
   *  in-page citation click and the URL parser below, which is the path a tab
   *  that did not exist at click time has to use. */
  const applyChunkAnchors = useCallback((
    family: string | null | undefined,
    entry: Record<number, ChunkAnchor[]>,
  ) => {
    switch (family) {
      case 'epar': setEparChunkAnchors(entry); break;
      case 'uk_pil': setUkPilChunkAnchors(entry); break;
      case 'ca_pm': setCaPmChunkAnchors(entry); break;
      case 'au': setAuChunkAnchors(entry); break;
      case 'eu': setEuChunkAnchors(entry); break;
      case 'approval_letter': break;   // see above — pending-ref owns it
      // jp_pdf / zh_pdf draw NO sentence box, and must not fall through to the
      // SBA map, which they never read — that was a silent cross-family write.
      //
      // Wiring `pageAnchors`/`chunkAnchors` into those viewers would not help:
      // the anchor is built from the stored ENGLISH evidence text while their
      // page word layer is the Japanese/Chinese PDF's own (`jp_pdf_pages`
      // carries `line_regions.text_ja` and `table_regions.html_ja`, translated
      // lazily on hover). An English needle cannot match a Japanese haystack,
      // so `computeAnchorBbox` would refuse every time. Making these families
      // work needs a Japanese-side anchor — `chunk_text_ja` (3d128be88) is the
      // ingredient that was missing, which is why this is a separate piece of
      // work and not a prop that was forgotten.
      case 'jp_pdf': case 'zh_pdf': case 'ch_pdf': case 'de_pdf': case 'ie_pdf': case 'it_pdf': case 'fr_html': break;   // ch/de_pdf: same (native page layer)
      default: setSbaChunkAnchors(entry); break;
    }
  }, []);

  const seedChunkAnchor = useCallback((citation: SimpleCitation) => {
    // NO `citation.chunk_id == null` GUARD. It used to stand here and it
    // disagreed with `citedChunkIds`, which reads
    // `[cit.chunk_id, ...evidence_anchors.map(a => a.chunk_id)]`. A citation
    // whose chunk id lives only on its SPANS therefore fetched the right chunk
    // and seeded nothing.
    //
    // The early return was also the only path that skipped `applyChunkAnchors`
    // altogether, so the PREVIOUS citation's anchors stayed on screen — a box
    // claiming evidence this answer never cited, which is worse than no box.
    // Falling through to an empty entry clears them.
    // A LIST PER CHUNK, not one entry. Several cited spans can share a chunk:
    // the server merges only units that TOUCH (`_MERGE_GAP = 1`), so a citation
    // to `[E1, E34, E76]` scattered through one chunk resolves to three
    // independent spans. Keying one anchor per chunk let the last overwrite the
    // rest, and the reader saw only the final cited passage.
    //
    // `evidence_anchors` is absent on older cached answers; fall back to the
    // single pair so a replayed snapshot still highlights its first span.
    //
    // No sentence at all ⇒ clear, never leave the previous citation's anchor in
    // place: it would draw over this chunk and claim evidence this answer never
    // cited.
    const entry: Record<number, ChunkAnchor[]> = {};
    if (citation.evidence_anchors?.length) {
      for (const a of citation.evidence_anchors) {
        if (a.chunk_id != null && a.start) {
          (entry[a.chunk_id] ??= []).push({
            start: a.start, end: a.end ?? null, text: a.text ?? null,
          });
        }
      }
    } else if (citation.chunk_id != null && citation.anchor_start) {
      entry[citation.chunk_id] = [{
        start: citation.anchor_start,
        end: citation.anchor_end ?? null,
        // `evidence_text` is the whole cited sentence the answer carried; the
        // fifth matching pass needs it and the 60-char probes above cannot
        // substitute. Capped by the SHARED constant, not a local 400 — the
        // three writers of this needle disagreed and one citation highlighted
        // differently in a new tab than in this one. Null on older cached
        // answers, which then behave exactly as they did.
        text: citation.evidence_text?.slice(0, ANCHOR_TEXT_MAX) ?? null,
      }];
    }
    applyChunkAnchors(citation.version_type, entry);
  }, [applyChunkAnchors]);

  const pageAnchorsFor = useCallback(
    (family: string, docId: number | null | undefined):
      Record<number, ChunkAnchor[]> | undefined => {
      if (citationPageAnchor?.family !== family) return undefined;
      // Wrong document => these anchors are not about what is on screen.
      // A null documentId on the citation cannot be checked, so it is refused
      // rather than trusted: unverifiable provenance is exactly the case that
      // produced a box nobody could account for.
      if (citationPageAnchor.documentId == null) return undefined;
      if (docId == null || citationPageAnchor.documentId !== docId) return undefined;
      // Grouped, not assigned: two cited sentences on one page both belong to
      // that page's list. `{ [page]: anchor }` made the second silently replace
      // the first, which is the bug this shape exists to make impossible.
      const byPage: Record<number, ChunkAnchor[]> = {};
      for (const { page, anchor } of citationPageAnchor.anchors) {
        (byPage[page] ??= []).push(anchor);
      }
      return byPage;
    },
    [citationPageAnchor],
  );
  const pageAnchorLandingFor = useCallback(
    // The FIRST cited span's page — where the reader should land, same as the
    // chunk path lands on the first chunk. Not the lowest page number: the
    // spans arrive in cited order and that order is the answer's own.
    (family: string, docId: number | null | undefined): number | undefined => (
      citationPageAnchor?.family === family
        && citationPageAnchor.documentId != null
        && docId != null
        && citationPageAnchor.documentId === docId
        ? citationPageAnchor.anchors[0]?.page
        : undefined),
    [citationPageAnchor],
  );
  const approvalLetterPendingAnchorRef = useRef<{
    documentId: number;
    chunkId: number;
    anchor: ChunkAnchor | null;
  } | null>(null);
  const previousApprovalLetterIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (previousApprovalLetterIdRef.current === selectedApprovalLetterId) return;
    previousApprovalLetterIdRef.current = selectedApprovalLetterId;
    setApprovalLetterHighlightChunkIds([]);
    setApprovalLetterChunkAnchors({});
  }, [selectedApprovalLetterId]);
  // Phase 10b chunkless-cross-doc handoff: when the resolver
  // matches a section with no persisted chunks (``first_chunk_id``
  // is null), we can't feed ``highlightChunkIds``.  Stash the
  // target ``(docId, page)`` here; an effect below fires
  // ``viewer.scrollToPage`` once ``sbaDocDetail`` loads for the
  // matching doc.  ``page`` is 1-based viewer-native (the
  // resolver emits the shift — see
  // ``section_ref_resolver.SectionMatch.page_start`` docstring).
  const [sbaPendingPageScroll, setSbaPendingPageScroll] = useState<
    { docId: number; page: number } | null
  >(null);

  // Clean up CSS highlights on unmount
  useEffect(() => {
    return () => clearHighlights();
  }, [clearHighlights]);


  // Resizable right (Comments) panel
  const [rightSidebarWidth, setRightSidebarWidth] = useState(() => Math.round(window.innerWidth * 0.3));
  const [isResizingRight, setIsResizingRight] = useState(false);

  // Handle resize for right sidebar
  const handleRightResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingRight(true);
  }, []);

  // Handle mouse move during resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizingRight) {
        const newWidth = Math.max(280, Math.min(700, (rootRef.current?.getBoundingClientRect().right ?? window.innerWidth) - e.clientX - 32));
        setRightSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingRight(false);
    };

    if (isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingRight]);

  // Compute which drug data to display (current FDA vs historical)
  // This determines what shows in the header (brand name, NDC, manufacturer, etc.)
  const displayDrug = useMemo(() => {
    if (selectedHistoricalId && historicalVersionData) {
      // Viewing historical version - use historical data
      return {
        id: historicalVersionData.id,
        brand_name: historicalVersionData.brand_name,
        generic_name: historicalVersionData.generic_name,
        manufacturer: historicalVersionData.manufacturer,
        dosage_form: historicalVersionData.dosage_form,
        route: historicalVersionData.route,
        ndc_code: historicalVersionData.ndc_code,
        set_id: historicalVersionData.set_id,
        approval_date: historicalVersionData.approval_date,
        approval_date_precision: historicalVersionData.approval_date_precision,
        effective_time: historicalVersionData.effective_time,
        ingredients: historicalVersionData.ingredients || [],
        sections: historicalVersionData.sections || [],
        version_number: historicalVersionData.version_number,
        is_historical: true,
      };
    }
    // Viewing current FDA version - use drug data
    return drug ? { ...drug, is_historical: false } : null;
  }, [drug, selectedHistoricalId, historicalVersionData]);

  // Formatted approval-date label at its precision (spec §6.4). Date and
  // precision must come from the SAME object — displayDrug when it carries
  // an approval_date (current or historical view), else the base drug — so a
  // historical payload's date is never paired with the current drug's
  // precision or vice versa.
  const approvalLabel = useMemo(() => {
    const src = displayDrug?.approval_date ? displayDrug : drug;
    return src ? formatApprovalDate(src.approval_date, src.approval_date_precision) : null;
  }, [displayDrug, drug]);

  // Q&A Panel state - uses currentDrugId to always point to the drug in center pane
  const drugIdForQA = currentDrugId ? parseInt(currentDrugId, 10) : 0;

  // Determine if we're viewing an SBA document
  const isViewingSba = selectedSbaId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedEparId === null && selectedUkPilId === null && selectedCaPmId === null && selectedAuId === null && selectedEuId === null;

  // Determine if we're viewing an EPAR document
  const isViewingEpar = selectedEparId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedUkPilId === null && selectedCaPmId === null && selectedAuId === null && selectedEuId === null;

  // Determine if we're viewing a UK PIL PDF
  const isViewingUkPil = selectedUkPilId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedEparId === null && selectedCaPmId === null && selectedAuId === null && selectedEuId === null;

  // Determine if we're viewing a CA PM PDF
  const isViewingCaPm = selectedCaPmId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedEparId === null && selectedUkPilId === null && selectedAuId === null && selectedEuId === null;

  // Determine if we're viewing an AU TGA (PI / CMI) document
  const isViewingAu = selectedAuId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedEparId === null && selectedUkPilId === null && selectedCaPmId === null && selectedEuId === null;

  // Determine if we're viewing an EU EMA SmPC document
  const isViewingEu = selectedEuId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedFdaLetterId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedEparId === null && selectedUkPilId === null && selectedCaPmId === null && selectedAuId === null;

  // UK NICE-HTA demo view (UK-NICE ONLY): when the drug's uk_pil docs are all
  // nice_* types, show the module/corpus placeholder picklists + the docs as
  // buttons instead of the standard version dropdown. Gated on region==='UK'
  // + presence of nice_ docs so no other drug/region/family is affected.
  const niceHtaDocs = ukPilDocuments.filter(
    (d) => String((d as { doc_type?: string }).doc_type || '').startsWith('nice_'),
  );
  const isNiceHtaView = drug?.region === 'UK' && niceHtaDocs.length > 0;
  // 3-tier NICE view (demo spec): pillar (module) → group (corpus) → documents.
  // Commercial Intelligence → NICE Assessments has the 6 NICE doc buttons;
  // Regulatory Affairs → UK SmPC shows the drug's main label; every other
  // combination has nothing wired and shows a blank viewer.
  const niceGroupOptions =
    niceModule === 'Commercial Intelligence'
      ? ['NICE Assessments', 'G-BA Assessments', 'Press Releases', '10K']
      : niceModule === 'Regulatory Affairs'
        ? ['UK SmPC']
        : []; // Clinical Development — nothing at mid/low levels
  const showNiceButtons = isNiceHtaView && niceModule === 'Commercial Intelligence' && niceCorpus === 'NICE Assessments';
  const showNiceSmpc = isNiceHtaView && niceModule === 'Regulatory Affairs' && niceCorpus === 'UK SmPC';
  const niceBlank = isNiceHtaView && !showNiceButtons && !showNiceSmpc;

  // First-state guarantee for the NICE demo view. A keyword search can match
  // "keytruda" inside a NICE doc and auto-select it, leaving the viewer on a
  // NICE doc while the picklists say Regulatory → UK SmPC. On the first landing
  // in this view, force the intended first state — Regulatory Affairs → UK SmPC
  // → the SmPC label — clearing any keyword-selected NICE doc. The ONE
  // exception is an explicit NICE-doc deep-link (?source=ukpil, e.g. a CEPI
  // citation): respect it and flip the picklists to Commercial → NICE so they
  // match the viewer.
  useEffect(() => {
    if (niceInitRef.current || !isNiceHtaView) return;
    niceInitRef.current = true;
    if (searchParams.get('source') === 'ukpil') {
      setNiceModule('Commercial Intelligence');
      setNiceCorpus('NICE Assessments');
    } else {
      setNiceModule('Regulatory Affairs');
      setNiceCorpus('UK SmPC');
      setSelectedUkPilId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNiceHtaView, searchParams]);

  // May the admin ORIGIN toggle be offered for this drug's CEPI chat?
  //
  // Two independent preconditions, both DATA facts rather than permissions
  // (the admin gate is separate and lives in useOriginScope):
  //   1. US only — ``us_origin_evidence_chunks`` is built exclusively from
  //      ``drugs.region='US'``, so the lane has nothing for any other region.
  //   2. More than one version — for a single-version product the origin label
  //      IS the current label (58,924 of 163,113 US drugs), so the toggle would
  //      offer a distinction that does not exist and could not change an answer.
  // Offering it where neither holds is how an admin ends up believing they read
  // the original label when they read the current one.
  const originToggleEligible =
    drug?.region === 'US' && (allVersionsData?.versions?.length ?? 0) > 1;

  // EU auto-land in flight: an EU drug whose SmPC fetch hasn't resolved yet
  // and where nothing has been explicitly selected.  While true the render
  // ladder shows a loader instead of falling through to the legacy section
  // view (which would flash for a frame before the SmPC PDF viewer mounts).
  // Once euDocsLoaded flips, EITHER selectedEuId lands on the SmPC (→ PDF
  // viewer) or there were no eu_documents (→ legacy section fallback, the
  // correct behaviour for an un-backfilled EU drug).
  const euAutolandPending = (
    drug?.region === 'EU' && !euDocsLoaded
    && selectedEuId === null && selectedVersionId === null
    && selectedHistoricalId === null && selectedFdaLetterId === null
    && selectedApprovalLetterId === null
    && selectedSbaId === null && selectedEparId === null
    && selectedUkPilId === null && selectedCaPmId === null
    && selectedAuId === null
  );

  // Determine if we're viewing a JP PMDA supplementary PDF (region='JP'
  // drugs; the XML label renders when this is false).
  const isViewingJpPdf = selectedJpPdfId !== null;

  // Determine if we're viewing a CN NMPA supplementary PDF. GA for all users
  // since 2026-08-31 — mirrors isViewingJpPdf above. (The admin gate here was
  // the reason non-admins saw a blank body on a CN drug: the doc list loaded
  // but no doc could ever be selected or rendered.)
  const isViewingZhPdf = selectedZhPdfId !== null;
  const isViewingChPdf = selectedChPdfId !== null;
  const isViewingDePdf = selectedDePdfId !== null;
  const isViewingFrHtml = drug?.region === 'FR';   // the ANSM label is the only FR view

  // Drugs@FDA label PDF. The long null-chain is the US pattern (sba / epar /
  // fda_letter / approval_letter), NOT de_pdf's bare `selectedDePdfId !== null`:
  // a US drug already has the SPL label and version viewers, so this must
  // yield to `selectedVersionId` / `selectedHistoricalId` rather than race them.
  const isViewingFdaLabelPdf = selectedFdaLabelPdfId !== null
    && selectedVersionId === null
    && selectedHistoricalId === null
    && selectedFdaLetterId === null
    && selectedApprovalLetterId === null
    && selectedSbaId === null
    && selectedEparId === null
    && selectedUkPilId === null
    && selectedCaPmId === null
    && selectedAuId === null
    && selectedEuId === null;

  // Determine if we're viewing an FDA letter
  const isViewingFdaLetter = selectedFdaLetterId !== null && selectedVersionId === null && selectedHistoricalId === null && selectedApprovalLetterId === null && selectedSbaId === null && selectedEparId === null && selectedUkPilId === null && selectedCaPmId === null && selectedAuId === null && selectedEuId === null;

  const isViewingApprovalLetter = selectedApprovalLetterId !== null
    && selectedVersionId === null
    && selectedHistoricalId === null
    && selectedFdaLetterId === null
    && selectedSbaId === null
    && selectedEparId === null
    && selectedUkPilId === null
    && selectedCaPmId === null
    && selectedAuId === null
    && selectedEuId === null
    && selectedJpPdfId === null;

  /**
   * A citation whose target is a DIFFERENT drug's page belongs to that drug's
   * tab: hand it to the shell (which opens/focuses the tab and runs ITS
   * handler) and stop. Same-drug navigation stays here. Guarded on a real,
   * different target path so a null-target citation (which the shell would
   * route straight back to this handler) cannot loop.
   */
  const routeToOtherDrugTab = useCallback((citation: SimpleCitation): boolean => {
    if (!shell) return false;
    const target = citationTargetPath(citation);
    if (!target || pathnameOf(target) === tabPathname) return false;
    if (citationOnTabPage(citation, tabPathname + tabSearch)) return false;
    shell.openCitation(citation);
    return true;
  }, [shell, tabPathname, tabSearch]);

  // Scroll to a section row of the viewed historical version (retries while rows mount).
  const resolveHistoricalSection = useCallback((wanted: string) => {
    const candidates = [wanted];
    const bits = wanted.split('.');
    for (let i = bits.length - 1; i > 0; i--) candidates.push(bits.slice(0, i).join('.'));

    let attempts = 0;
    const tryScroll = () => {
      for (const num of candidates) {
        for (const [sectionId, el] of sectionRefs.current.entries()) {
          if (el.getAttribute('data-section-number') !== num) continue;
          setExpandedSections(prev => new Set([...prev, sectionId]));
          revealRow(sectionId);
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.add('section-highlight');
            setTimeout(() => el.classList.remove('section-highlight'), 2000);
          }, 120);
          return true;
        }
      }
      return false;
    };
    // Rows mount a beat after the data lands; retry rather than guess a delay.
    const attempt = () => {
      if (tryScroll() || ++attempts >= 8) return;
      setTimeout(attempt, 200);
    };
    attempt();
  }, [revealRow]);

  /**
   * Handle citation click from Q&A panel
   * Switches to current FDA version (if not already) and scrolls to the cited section
   */
  const handleCitationClick = useCallback((citation: SimpleCitation) => {
    if (routeToOtherDrugTab(citation)) return;
    const { section_number } = citation;

    // ORIGIN-VERSION label citation → open the cited HISTORICAL version, not
    // the current label. Without this branch an origin citation fell through
    // to the current-label section scroll below: the answer quoted the 2006
    // label while the viewer showed 2024, so the reader could not find the
    // quoted sentence and reasonably concluded the answer was fabricated.
    // Routes through the SAME ('fda_historical', drug_version_id) path the
    // version selector uses, so the viewer, TOC and URL all stay consistent.
    if (
      citation.document_type === 'label' &&
      citation.version_type === 'fda_historical' &&
      citation.drug_version_id != null
    ) {
      // Stash the target section and let the effect below do the scroll.
      // A setTimeout here would race: ``historicalVersionData`` is fetched
      // AFTER the version switch, and the accordion rows (and their refs)
      // only exist once it lands. The effect fires exactly when the data
      // arrives, however long that takes.
      if (selectedHistoricalId === citation.drug_version_id && historicalVersionData?.sections?.length) {
        // Already on that version: no version switch, so resolve directly,
        // after returning to Full label / opening Highlights (R4.6).
        if (section_number) withFullLabel(section_number, () => resolveHistoricalSection(section_number));
        return;
      }
      pendingHistoricalSectionRef.current = section_number || null;
      handleUnifiedVersionChange('fda_historical', citation.drug_version_id);
      return;
    }

    // EU historical change citation → open the EU historical COMPARE at the
    // cited version pair (NOT a single-version viewer). The shared helper writes
    // the canonical target (source + pair + section + presentation + block) the
    // EuHistoricalCompareLauncher consumes to auto-open its modal and land on
    // the exact change. ``from_version`` is the archived "old" side; ``to``
    // defaults to the newest archived version in the launcher when absent.
    if (citation.document_type === 'eu_historical') {
      const next = applyEuHistoricalCompareTarget(searchParams, citation);
      if (next) setSearchParams(next);
      return;
    }

    // EU historical COMPLETE-SECTION citation → open the INDIVIDUAL archived-
    // version PDF viewer (NOT the compare). Deep-link params the viewer overlay
    // below consumes: euhview=1 + the document id + the section's page +
    // highlight text (historical versions have no chunk_ids → box the text).
    if (citation.document_type === 'eu_history_section'
        && (citation.source_version_key || citation.document_id != null)) {
      const next = new URLSearchParams(searchParams);
      next.set('euhview', '1');
      // Stable handle preferred (?euver=); volatile document_id is the fallback.
      if (citation.source_version_key) next.set('euver', citation.source_version_key);
      else next.set('euhdoc', String(citation.document_id));
      if (citation.page != null) next.set('euhpage', String(citation.page));
      if (citation.highlight_y != null) next.set('euhy', String(citation.highlight_y));
      if (citation.presentation_key) next.set('euhpres', citation.presentation_key);
      if (section_number) next.set('euhsection', section_number);
      setSearchParams(next);
      return;
    }

    // Competitive-intel (CI) citations point at an EXTERNAL page (a
    // competitor's own product page / press release) — there is no in-app
    // viewer to route to, unlike every other citation type below. Open the
    // safe HTTPS URL in a new tab; an invalid/missing URL no-ops rather
    // than falling through to the regulatory/section resolvers below.
    if (citation.document_type === 'competitive_intel') {
      const safeUrl = safeCompetitiveIntelUrl(citation.external_url);
      if (safeUrl) {
        window.open(safeUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Current HA citations are physical-row addressed. Always route through
    // the canonical family URL so a citation to a different version opens the
    // cited document instead of highlighting whichever viewer is already open.
    const regulatoryHref = regulatoryCitationHref(citation);
    if (regulatoryHref) {
      navigate(regulatoryHref);
      return;
    }

    // JP drugs render via JPSectionView (jp-sec-${num} anchors + the
    // jp-goto-section event), NOT the FDA ``section-${num}`` accordion
    // refs. Additive branch — reuses the same event the JP TOC uses to
    // expand the target section (+ its parent), then highlights the cited
    // chunk with the SAME anchor-highlighter the FDA onChunkClick path
    // uses (it takes any container Element).
    if (drug?.region && drug.region.toUpperCase() === 'JP' && section_number) {
      clearHighlights();
      // Clear the JP-specific highlight too (senior's clearChunkHighlights
      // only knows the chunk-evidence name).
      if (isActiveRef.current && 'highlights' in CSS) (CSS as any).highlights.delete('chunk-evidence-jp');
      withFullLabel(section_number, () => {
      window.dispatchEvent(new CustomEvent('jp-goto-section', { detail: section_number }));
      const anchorStart = citation.anchor_start;
      if (anchorStart) {
        // The JP content container is ``.jp-content`` (NOT the FDA
        // ``.spl-content``/``.prose`` that waitForContent looks for), and a
        // subsection element (e.g. jp-sec-10.1) only mounts AFTER its parent
        // expands + React re-renders — so poll for the .jp-content node.
        let tries = 0;
        const tryHighlight = () => {
          const el = findInTab(rootRef, `jp-sec-${section_number}`);
          const content = el?.querySelector('.jp-content');
          if (!content) {
            if (tries++ < 15) setTimeout(tryHighlight, 100);
            return;
          }
          const ok = anchorMatchAndHighlight(
            content,
            anchorStart,
            citation.anchor_end || '',
            citation.anchor_length || DEFAULT_ANCHOR_LENGTH,
            tabId,
          );
          if (ok) {
            // Re-register the highlight under a JP-specific name so it
            // picks up the softer ::highlight(chunk-evidence-jp) style —
            // the senior's anchorMatchAndHighlight + chunk-evidence rule
            // stay untouched.
            if (isActiveRef.current && 'highlights' in CSS) {
              const h = (CSS as any).highlights.get('chunk-evidence');
              if (h) {
                (CSS as any).highlights.delete('chunk-evidence');
                (CSS as any).highlights.set('chunk-evidence-jp', h);
              }
            }
            scrollToChunkHighlight();
          }
        };
        setTimeout(tryHighlight, 120);
      }
      });
      return;
    }

    // EPAR view has its own viewer (EPARViewerEmbed) with chunk-based
    // navigation; the FDA-section refs surfaced in the CEPI panel don't
    // apply here. Bail out early so the EPAR view is preserved.
    if (isViewingEpar) {
      return;
    }

    // Resolve section_number -> the registered accordion row via its
    // ``data-section-number`` ATTRIBUTE, not by building a ref-map key.
    //
    // This used to be ``sectionRefs.current.get(`section-${section_number}`)``,
    // which can never hit: rows register under ``section-${section.id}``, a
    // database bigint. The lookup returned undefined, the ``if (element)``
    // guard swallowed it, and ``setExpandedSections`` added a key no row
    // checks — so a Sources-chip click did nothing at all, silently, from
    // 2026-01-25 to 2026-09-09. It went unnoticed because the inline
    // section-links in the same answer take handleSectionLinkClick, which
    // matched on the attribute and therefore worked. Both paths now share
    // findSectionScrollTarget so they cannot drift apart again.
    const scrollToSection = () => {
      const target = findSectionScrollTarget(
        sectionRefs.current.entries(),
        section_number || '',
      );
      if (!target) return;
      setExpandedSections(prev => new Set([...prev, target.sectionId]));
      revealRow(target.sectionId);
      setTimeout(() => {
        target.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // NO highlight on this path, deliberately. Citation highlighting is
        // withheld product-wide while exact-line citation identity is being
        // solved (7d1e727f killed the viewer bbox behind
        // VITE_CITATION_BBOX_HIGHLIGHT_ENABLED, unset in every env). That
        // commit's rule is the one to follow here: "scrolling to the cited
        // page is unchanged; only the rectangle is withheld." Navigation was
        // the bug; a highlight is a separate product decision and not one to
        // reintroduce as a side effect of fixing the click.
      }, 150);
    };

    // If viewing historical, draft, SBA, or FDA letter, switch back to current FDA first
    withFullLabel(section_number, () => {
    if (selectedHistoricalId !== null || selectedVersionId !== null || selectedSbaId !== null || selectedFdaLetterId !== null || selectedApprovalLetterId !== null) {
      setSelectedHistoricalId(null);
      setSelectedVersionId(null);
      setSelectedSbaId(null);
      setSelectedFdaLetterId(null);
      setSelectedApprovalLetterId(null);
      // Wait for state to update, then scroll
      setTimeout(scrollToSection, 200);
    } else {
      // Already on current FDA, just scroll
      scrollToSection();
    }
    });
  }, [routeToOtherDrugTab, withFullLabel, resolveHistoricalSection, revealRow, historicalVersionData, drug?.region, isViewingEpar, navigate, selectedHistoricalId, selectedVersionId, selectedSbaId, selectedFdaLetterId, selectedApprovalLetterId, searchParams, setSearchParams]);

  /**
   * Handle SBA citation click — scroll to exact section with char highlighting
   */
  /**
   * Phase 10b shared viewer-native SBA navigation.  Used by BOTH
   * citation clicks and inline ``<a class="section-link">`` refs
   * inside CEPI answers so the two paths never drift.  Precedence:
   *
   *   1. Current-doc exact match via ``SBAViewerEmbed.scrollToSection``
   *      (zero round-trip when the viewer's TOC already carries
   *      the section_number verbatim).
   *   2. Phase 10b fuzzy resolver — strips legacy prefixes
   *      (``Clinical_Review_1.3.2``), trailing periods, case
   *      variation, and surfaces cross-doc matches.
   *   3. Cross-doc: switch ``selectedSbaId`` to the match's
   *      document_id and seed ``sbaHighlightChunkIds`` so the
   *      viewer's mount effect auto-scrolls + highlights.
   */
  const navigateToSbaSection = useCallback(async (sectionRef: string) => {
    const viewer = sbaViewerRef.current;
    if (viewer && viewer.scrollToSection(sectionRef)) {
      return;
    }

    // Resolver needs a drug_id for scope.  Bail silently if we
    // don't have one yet — the viewer's scrollToSection already
    // logged the miss at debug level.
    if (!drug?.id) return;
    try {
      const resp = await resolveSectionRef(drug.id, sectionRef);
      const match = resp.matches[0];
      if (!match) return;

      if (match.document_id === selectedSbaId) {
        // Resolver found it in the current doc but the exact-
        // match miss above means the stored section_number
        // differs enough from the input (e.g. legacy prefixed
        // ref ``Clinical_Review_1.3.2`` resolving to bare
        // ``1.3.2``).  Scroll via chunk_id or page_start —
        // bypasses the TOC lookup.
        if (match.first_chunk_id && viewer) {
          viewer.highlightChunks([match.first_chunk_id]);
        } else if (match.page_start && viewer) {
          viewer.scrollToPage(match.page_start);
        }
        return;
      }

      // Cross-doc match — switch to the target doc.  If the
      // section has a first chunk, feed the viewer's
      // ``highlightChunkIds`` prop (auto-scrolls + bbox
      // overlays on mount).  Otherwise stash the page target
      // so the ``sbaPendingPageScroll`` effect below fires a
      // plain scrollToPage once the new viewer mounts — this
      // is the chunkless section case (title-only or
      // empty-body sections that chunking produced no
      // output for).
      setSelectedSbaId(match.document_id);
      if (match.first_chunk_id != null) {
        setSbaHighlightChunkIds([match.first_chunk_id]);
      } else if (match.page_start != null) {
        setSbaPendingPageScroll({
          docId: match.document_id,
          page: match.page_start,
        });
      }
    } catch (err) {
      logger.error('Failed to resolve SBA section reference:', err);
    }
  }, [drug?.id, selectedSbaId]);

  // Fire the pending page scroll after the new SBA doc finishes
  // loading.  Small timer accounts for the viewer's TOC + pages
  // fetch — ``sbaViewerRef`` is attached on mount but the page
  // DOM refs populate during the initial render pass.
  useEffect(() => {
    if (!sbaPendingPageScroll) return;
    if (sbaDocDetail?.id !== sbaPendingPageScroll.docId) return;
    const timer = setTimeout(() => {
      sbaViewerRef.current?.scrollToPage(sbaPendingPageScroll.page);
      setSbaPendingPageScroll(null);
    }, 300);
    return () => clearTimeout(timer);
  }, [sbaDocDetail?.id, sbaPendingPageScroll]);

  /**
   * Citation click for regulatory-document chats (SBA + EPAR).
   *
   * Both viewers share the same chunk-highlight contract — feed them a
   * chunk_id and they scroll + draw bbox overlays.  Family is carried
   * on ``citation.version_type`` so this handler routes to the right
   * viewer state setter without forking into per-family handlers.
   *
   * SBA additionally supports cross-doc navigation (citation pointing at
   * a sibling document inside the current bundle).  EPAR has no bundle
   * projection today, so its branch only drives the in-document path.
   */
  const handleRegulatoryCitationClick = useCallback(
    async (citation: SimpleCitation) => {
      if (routeToOtherDrugTab(citation)) return;
      // EU historical citations route the SAME as in handleCitationClick — but
      // an EU drug viewed as its EU SmPC wires THIS handler (isViewingEu), so
      // without these branches an eu_historical / eu_history_section citation
      // falls through to the regulatory routing and silently no-ops (the bug:
      // "citation not working" on EU drugs).
      //   eu_historical      → the version COMPARE at the cited pair + section.
      //   eu_history_section → the INDIVIDUAL archived-version viewer at page.
      if (citation.document_type === 'eu_historical') {
        const next = applyEuHistoricalCompareTarget(searchParams, citation);
        if (next) setSearchParams(next);
        return;
      }
      if (citation.document_type === 'eu_history_section' && citation.document_id != null) {
        const next = new URLSearchParams(searchParams);
        next.set('euhview', '1');
        next.set('euhdoc', String(citation.document_id));
        if (citation.page != null) next.set('euhpage', String(citation.page));
        if (citation.highlight_y != null) next.set('euhy', String(citation.highlight_y));
        if (citation.presentation_key) next.set('euhpres', citation.presentation_key);
        if (citation.section_number) next.set('euhsection', citation.section_number);
        setSearchParams(next);
        return;
      }

      // Task B4: ``investigate_drugs`` may surface label citations even
      // inside a regulatory-document chat (the tool does corpus-wide work
      // and routes evidence by drug_id).  Dispatch on document_type so a
      // label citation lands on the drug page; otherwise preserve the
      // legacy SBA/EPAR/UK PIL/CA PM/AU routing below.
      if (citation.document_type === 'label' && citation.drug_id != null) {
        const section = citation.section_number ?? '';
        // Carry the cited sentence ACROSS the navigate.
        //
        // This branch is the route a label citation takes when it surfaces in a
        // regulatory-document chat, whose panel passes no `onChunkClick` — so
        // MessageBubble's text-highlight route is unavailable and the highlight
        // has to happen after arrival instead. The navigate unmounts the PDF
        // viewer and mounts the label, so anything held in component state at
        // click time is gone before the label's DOM exists.
        //
        // `stashEvidenceChunk` is the mechanism that already exists for this hop
        // (Cogni search → drug page): one-shot in localStorage, and the
        // `?section=` arrival effect already takes it and highlights the matching
        // text. The URL this branch builds carries `?section=`, so it lands in
        // that effect with no further wiring.
        //
        // The FULL sentence, not `anchor_start`: the consumer highlights for the
        // length of what it is given, so a 60-char probe would box a fragment of
        // the evidence. No captured sentence ⇒ nothing stashed, and the label
        // opens with no highlight rather than a guessed one.
        if (citation.evidence_text) {
          stashEvidenceChunk(citation.drug_id, citation.evidence_text);
        }
        navigate(`/drug/${citation.drug_id}?section=${encodeURIComponent(section)}`);
        return;
      }

      // Competitive-intel (CI) citations can surface inside a regulatory-
      // document chat too (investigate_drugs / get_competitor_page run
      // corpus-wide regardless of which viewer is open). Same external-href
      // contract as handleCitationClick above — no in-app viewer to route to.
      if (citation.document_type === 'competitive_intel') {
        const safeUrl = safeCompetitiveIntelUrl(citation.external_url);
        if (safeUrl) {
          window.open(safeUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      // PAGE-KEYED citation (progressive read): plan the anchor BEFORE any of
      // the routing below, because every branch of it ends in `return`.
      //
      // This must stay above `regulatoryCitationHref` in particular. That
      // builder returns a URL for ANY citation carrying drug_id + document_id,
      // chunk or no chunk — see its own test, "omits the chunkIds param when
      // the citation has no chunk_id" -> '/drug/42?source=sba&version=100' —
      // and the block below then navigates and returns. A progressive-read
      // citation ALWAYS carries both, so planning further down meant planning
      // never, for every citation this exists to serve.
      //
      // Setting state then navigating is the right order: the navigate switches
      // the viewer to the cited document, and this anchor is what puts a box on
      // the cited sentence once it is there.
      const planned = planPageAnchors(citation);
      setCitationPageAnchor(planned.length ? {
        family: citation.version_type ?? 'sba',
        // The document the sentences were located on. Without it these anchors
        // follow the reader to whatever they open next.
        documentId: typeof citation.document_id === 'number'
          ? citation.document_id : null,
        anchors: planned,
      } : null);

      // The citation's document_id is authoritative. Routing through the
      // shared URL builder switches EPAR/UK/CA/AU/EU/JP/SBA to the cited
      // physical row and seeds the correct family chunk highlight.
      const regulatoryHref = regulatoryCitationHref(citation);
      if (regulatoryHref) {
        if (citation.document_type === 'approval_letter') {
          // THE FOURTH SPELLING OF THE SAME RULE, found by audit after the
          // other three were unified. `citation.chunk_id != null` skipped an
          // approval-letter citation whose chunk ids live only on its spans —
          // the exact shape that broke the SBA path — and the skip lands in
          // the `else` below, which CLEARS the anchors. So it did not merely
          // fail to highlight; it actively wiped what was there.
          const letterIds = citedChunkIds(citation);
          if (letterIds.length > 0 && citation.document_id != null) {
            // Span fallback too: `anchor_start` is absent on a citation whose
            // sentences arrived only as per-span anchors, and the pending-ref
            // path is this family's ONLY way to draw a box.
            const firstSpan = (citation.evidence_anchors ?? [])
              .find((a) => a.start);
            const start = citation.anchor_start ?? firstSpan?.start ?? null;
            const end = citation.anchor_start
              ? citation.anchor_end ?? null
              : firstSpan?.end ?? null;
            // `text` TOO — this family reaches the viewer on no other path.
            // `applyChunkAnchors` is a deliberate no-op for `approval_letter`
            // ("pending-ref owns it") and `SOURCE_FAMILY` in the citeAnchors
            // URL parser has no key for it, so an anchor built here without the
            // sentence means approval-letter citations could never use the
            // fifth matching pass at all. Silent: the field is optional.
            // The SPAN's own sentence leads, whichever source gave `start`
            // above. `evidence_anchors[].text` is one sentence, captured for
            // that span; `evidence_text` is the first span's sentences JOINED,
            // so it can be a paragraph. Preferring the join whenever
            // `anchor_start` happened to be present handed the fifth pass a
            // multi-sentence needle, which selects a paragraph-sized window —
            // the exact failure the per-sentence split exists to prevent.
            const text = firstSpan?.text ?? citation.evidence_text ?? null;
            // Omitted rather than nulled when absent, so an anchor without a
            // sentence stays byte-identical to what shipped before.
            const anchor: ChunkAnchor | null = start
              ? { start, end, ...(text ? { text: text.slice(0, ANCHOR_TEXT_MAX) } : {}) }
              : null;
            approvalLetterPendingAnchorRef.current = {
              documentId: citation.document_id,
              chunkId: letterIds[0]!,
              anchor,
            };
          } else {
            approvalLetterPendingAnchorRef.current = null;
            setApprovalLetterHighlightChunkIds([]);
            setApprovalLetterChunkAnchors({});
          }
        }
        // Before the RETURN — that is the part that matters. The per-family
        // anchor setters live ~100 lines further down, past this `return`, so
        // they never ran for any citation with an href. (Relative to the
        // navigate on the next line the position is irrelevant: React batches
        // both in one handler. Mutation-verified as equivalent, so do not read
        // significance into the order of these two statements.)
        seedChunkAnchor(citation);
        navigate(regulatoryHref);
        return;
      }

      const isEpar = citation.version_type === 'epar';
      const isUkPil = citation.version_type === 'uk_pil';
      const isCaPm = citation.version_type === 'ca_pm';
      const isAu = citation.version_type === 'au';
      const isEu = citation.version_type === 'eu';
      const isJpPdf = citation.version_type === 'jp_pdf';
      const isZhPdf = citation.version_type === 'zh_pdf';
      const isApprovalLetter = citation.version_type === 'approval_letter';

      // GATED ON EVERY CITED CHUNK, not on `citation.chunk_id` alone — the
      // third copy of a rule that three call sites spelled three ways. A
      // citation whose chunk id lives only on its SPANS was refused entry
      // here and fell through to the section-number resolve below, so it
      // never set an id or an anchor. `citedChunkIds` is the one definition;
      // the URL writer and `seedChunkAnchor` now use it too.
      const citedIds = citedChunkIds(citation);
      if (citedIds.length > 0) {
        // Stash the anchor phrases (if any) so the viewer can draw a
        // tight sentence-level highlight instead of the paragraph-
        // sized chunk bbox.  Per-family map because anchors are
        // viewer-instance-scoped — we don't want a stale EPAR anchor
        // bleeding into a later SBA citation click.
        const anchor: ChunkAnchor | null = citation.anchor_start
          ? { start: citation.anchor_start, end: citation.anchor_end ?? null }
          : null;

        if (isApprovalLetter) {
          if (anchor) {
            // The first CITED id, not `citation.chunk_id`, which may be null
            // on a citation whose chunks are named only by its spans.
            setApprovalLetterChunkAnchors({ [citedIds[0]!]: [anchor] });
          } else {
            setApprovalLetterChunkAnchors({});
          }
          setApprovalLetterHighlightChunkIds(citedChunkIds(citation));
          return;
        }

        if (isUkPil) {
          // EVERY cited span, not just the first. `seedChunkAnchor`
          // builds the plural map from `evidence_anchors` and routes it
          // by family, which is what the navigate path already did.
          seedChunkAnchor(citation);
          setUkPilHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isCaPm) {
          // EVERY cited span, not just the first. `seedChunkAnchor`
          // builds the plural map from `evidence_anchors` and routes it
          // by family, which is what the navigate path already did.
          seedChunkAnchor(citation);
          setCaPmHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isAu) {
          // EVERY cited span, not just the first. `seedChunkAnchor`
          // builds the plural map from `evidence_anchors` and routes it
          // by family, which is what the navigate path already did.
          seedChunkAnchor(citation);
          setAuHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isEu) {
          // EVERY cited span, not just the first. `seedChunkAnchor`
          // builds the plural map from `evidence_anchors` and routes it
          // by family, which is what the navigate path already did.
          seedChunkAnchor(citation);
          setEuHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isJpPdf) {
          // JP PMDA supplementary (jp_pdf) — switch the open doc if the
          // citation points at a different one, then drive the prop-based
          // chunk highlight (JPPdfViewerEmbed ``highlightChunkIds``).
          const targetDocId = citation.document_id ?? selectedJpPdfId ?? null;
          if (targetDocId != null && targetDocId !== selectedJpPdfId) {
            setSelectedJpPdfId(targetDocId);
          }
          setJpPdfHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isZhPdf) {
          // CN NMPA supplementary (zh_pdf) — mirror of the jp_pdf branch.
          // Admin-only: the render gate (isViewingZhPdf) enforces admin, so a
          // non-admin who somehow gets here still can't view the doc.
          const targetDocId = citation.document_id ?? selectedZhPdfId ?? null;
          if (targetDocId != null && targetDocId !== selectedZhPdfId) {
            setSelectedZhPdfId(targetDocId);
          }
          setZhPdfHighlightChunkIds(citedChunkIds(citation));
          return;
        }
        if (isEpar) {
          // EVERY cited span, not just the first. `seedChunkAnchor`
          // builds the plural map from `evidence_anchors` and routes it
          // by family, which is what the navigate path already did.
          seedChunkAnchor(citation);
          setEparHighlightChunkIds(citedChunkIds(citation));
          return;
        }

        // SBA branch: seed anchors then drive the existing imperative
        // / prop-driven highlight paths.
        // EVERY cited span, not just the first. `seedChunkAnchor`
        // builds the plural map from `evidence_anchors` and routes it
        // by family, which is what the navigate path already did.
        seedChunkAnchor(citation);

        const targetDocId = citation.document_id ?? selectedSbaId ?? null;
        if (targetDocId != null && targetDocId !== selectedSbaId) {
          setSelectedSbaId(targetDocId);
          setSbaHighlightChunkIds(citedChunkIds(citation));
          setSbaPendingPageScroll(null);
          return;
        }

        if (sbaViewerRef.current) {
          sbaViewerRef.current.highlightChunks(citedChunkIds(citation));
          return;
        }

        // Viewer still mounting: seed the prop-driven path.
        setSbaHighlightChunkIds(citedChunkIds(citation));
        return;
      }

      if (!citation.section_number) return;
      if (isApprovalLetter) {
        setApprovalLetterHighlightChunkIds([]);
        setApprovalLetterChunkAnchors({});
        return;
      }
      if (isEpar) {
        // EPAR citation without chunk_id (LLM-emitted link missing
        // data-chunk-id, e.g. "<a section-id=846 section=2.10>").
        // Mirror the SBA path: hit /epar/sections/resolve to look up
        // the section's first chunk, then drive the viewer through
        // setEparHighlightChunkIds — same prop-driven highlight flow
        // the working chunk-id branch uses above.
        if (!drug?.id) return;
        try {
          const resp = await resolveEparSectionRef(drug.id, citation.section_number);
          const match = resp.matches[0];
          if (match?.first_chunk_id != null) {
            setEparHighlightChunkIds([match.first_chunk_id]);
          }
        } catch (err) {
          logger.error('Failed to resolve EPAR section reference:', err);
        }
        return;
      }
      if (isUkPil) {
        // UK PIL citation without chunk_id — fall back to
        // ``section_id`` when the LLM emitted one (CIPI tool returns
        // section_id on every citation, so this is the common path).
        // The viewer's prop-driven highlight effect accepts chunk_id
        // only, so resolve section_id → first chunk via the
        // ``/api/uk_pil/documents/{id}/toc`` endpoint's section
        // list (cheap; cached) — or, if the citation also gave us
        // ``section_number``, fall through to a future resolver.
        // For the common case (no chunk_id, no section_id), we
        // can't pinpoint a page without a section_id; bail silently.
        if (citation.section_id == null) return;
        // The viewer reads the section list itself when given a
        // section to scroll to; we just need to seed an empty
        // highlight to clear stale state.  Section scroll happens
        // via the viewer's TOC sidebar selection — clicking the
        // chat link won't scroll yet without chunk_id, but the
        // section is already visible via the TOC.
        setUkPilHighlightChunkIds([]);
        return;
      }
      if (isCaPm) {
        // CA PM citation without chunk_id — mirrors the UK PIL branch
        // above.  Without a chunk_id we can't pinpoint a page; bail
        // silently when no section_id, otherwise clear stale highlight
        // state (the section is reachable via the viewer's TOC).
        if (citation.section_id == null) return;
        setCaPmHighlightChunkIds([]);
        return;
      }
      if (isAu) {
        // AU TGA citation without chunk_id — mirrors the CA PM branch
        // above.  Without a chunk_id we can't pinpoint a page; bail
        // silently when no section_id, otherwise clear stale highlight
        // state (the section is reachable via the viewer's TOC).
        if (citation.section_id == null) return;
        setAuHighlightChunkIds([]);
        return;
      }
      if (isEu) {
        // EU EMA SmPC citation without chunk_id — mirrors the AU branch
        // above.  Without a chunk_id we can't pinpoint a page; bail
        // silently when no section_id, otherwise clear stale highlight
        // state (the section is reachable via the viewer's TOC).
        if (citation.section_id == null) return;
        setEuHighlightChunkIds([]);
        return;
      }
      await navigateToSbaSection(citation.section_number);
    },
    [routeToOtherDrugTab, drug?.id, navigate, navigateToSbaSection, selectedSbaId],
  );

  const handleFdaLetterCitationClick = useCallback((citation: SimpleCitation) => {
    if (routeToOtherDrugTab(citation)) return;
    // Task B4: ``investigate_drugs`` may surface label citations from an
    // FDA-letter chat session (corpus-wide tool run).  Route label
    // citations to the drug page; otherwise preserve the legacy
    // letter-viewer routing below.
    if (citation.document_type === 'label' && citation.drug_id != null) {
      const section = citation.section_number ?? '';
      navigate(`/drug/${citation.drug_id}?section=${encodeURIComponent(section)}`);
      return;
    }

    // Competitive-intel (CI) citations can surface inside an FDA-letter chat
    // too (corpus-wide tool run). Same external-href contract as the other
    // citation hosts on this page — no in-app viewer to route to.
    if (citation.document_type === 'competitive_intel') {
      const safeUrl = safeCompetitiveIntelUrl(citation.external_url);
      if (safeUrl) {
        window.open(safeUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (!citation.section_id) return;
    // Phase 7: when the citation carries a chunk_id, drive the
    // FdaLetterPageViewer bbox-highlight via state.  The viewer fetches
    // the chunk row, computes the highlight rect, and scrolls the
    // matching page into view — so we don't need the legacy
    // anchorMatchAndHighlight path for PDF-source sections.  Setting
    // chunk_id is harmless on HTML-source sections too (the viewer
    // delegates to the htmlFallback slot and the legacy anchor-match
    // path below still runs).
    if (citation.chunk_id !== undefined && citation.chunk_id !== null) {
      // Re-set to the same id triggers a re-fetch + scroll in the
      // viewer; clear first to defeat the "same id, skip effect" case.
      setFdaLetterActiveChunkId(null);
      setTimeout(() => setFdaLetterActiveChunkId(citation.chunk_id ?? null), 0);
    }
    const sectionEl = findInTab(rootRef, `fda-letter-section-${citation.section_id}`);
    if (!sectionEl) return;

    clearHighlights();
    sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (citation.anchor_start) {
      waitForContent(sectionEl).then(container => {
        if (!container) return;
        const highlighted = anchorMatchAndHighlight(container, citation.anchor_start!, citation.anchor_end || '', citation.anchor_length || DEFAULT_ANCHOR_LENGTH, tabId);
        if (highlighted) {
          scrollToChunkHighlight();
        }
      });
    }
  }, [routeToOtherDrugTab, navigate]);

  /**
   * Handle section link click from Q&A panel
   * This is called when user clicks an inline section link like:
   * <a class="section-link" data-section="5.1">Section 5.1</a>
   *
   * It navigates to the section in the current FDA label.
   * If viewing a historical/draft version, it switches back to current first.
   */
  const handleSectionLinkClick = useCallback((sectionNumber: string) => {
    // SBA: run the full Phase 10b 3-step precedence so inline
    // CEPI section links get the same fuzzy tolerance as
    // citation chips — prefix omission, case, trailing period,
    // cross-doc navigation.  Kicked off fire-and-forget
    // because the outer handler is sync-flavoured and we don't
    // need to block on the resolver.
    if (isViewingSba) {
      void navigateToSbaSection(sectionNumber);
      return;
    }

    // EPAR view symmetry — preserve the EPAR doc when the user clicks an
    // FDA-section ref inside CEPI. EPAR has its own viewer (EPARViewerEmbed)
    // with chunk-based navigation; FDA section numbers don't resolve here.
    if (isViewingEpar) {
      return;
    }

    // Helper function to find and scroll to a section.
    //
    // The lookup itself now lives in findSectionScrollTarget, shared with
    // handleCitationClick. It was duplicated-by-divergence before: this path
    // matched on the ``data-section-number`` attribute (correct), while the
    // citation path built a ``section-${section_number}`` ref-map key that
    // could never hit. Keeping ONE implementation is what stops that from
    // silently recurring — the two behaviours are identical by construction.
    const findAndScrollToSection = (targetSectionNumber: string): boolean => {
      const target = findSectionScrollTarget(
        sectionRefs.current.entries(),
        targetSectionNumber,
      );
      if (!target) return false;
      // Expand the section first
      setExpandedSections(prev => new Set([...prev, target.sectionId]));
      revealRow(target.sectionId);
      // Scroll after expansion animation
      setTimeout(() => {
        target.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Add highlight effect
        target.element.classList.add('section-highlight');
        setTimeout(() => {
          target.element.classList.remove('section-highlight');
        }, 2000);
      }, 100);
      return true;
    };

    // Retry logic for when switching views (refs may not be ready immediately)
    const scrollWithRetry = (targetSectionNumber: string, maxRetries = 5) => {
      let retryCount = 0;
      const attemptScroll = () => {
        if (findAndScrollToSection(targetSectionNumber)) {
          return; // Success
        }
        retryCount++;
        if (retryCount < maxRetries) {
          // Retry with increasing delay (100, 200, 300, 400, 500ms)
          setTimeout(attemptScroll, 100 * retryCount);
        } else {
          console.warn(`Could not find section ${targetSectionNumber} after ${maxRetries} attempts`);
        }
      };
      // Start first attempt after a brief delay for DOM update
      setTimeout(attemptScroll, 150);
    };

    // If viewing a non-current-FDA view, switch back to current FDA label first.
    // SBA and EPAR are guarded above; this branch covers historical, draft,
    // and FDA-letter views which legitimately do route through FDA section refs.
    withFullLabel(sectionNumber, () => {
    if (selectedHistoricalId !== null || selectedVersionId !== null || selectedFdaLetterId !== null || selectedApprovalLetterId !== null) {
      // Clear old refs before switching (they'll be repopulated on render)
      sectionRefs.current.clear();
      // Reset to current FDA label
      setSelectedHistoricalId(null);
      setSelectedVersionId(null);
      setSelectedFdaLetterId(null);
      setSelectedApprovalLetterId(null);
      // Scroll to section with retry logic for view switch
      scrollWithRetry(sectionNumber);
    } else {
      // Already viewing current FDA label, just scroll (may still need retry if sections collapsed)
      scrollWithRetry(sectionNumber);
    }
    });
  }, [
    withFullLabel,
    revealRow,
    selectedHistoricalId,
    selectedVersionId,
    selectedSbaId,
    selectedEparId,
    selectedFdaLetterId,
    selectedApprovalLetterId,
    isViewingSba,
    isViewingEpar,
    navigateToSbaSection,
  ]);

  const doChunkHighlight = useCallback((targetSectionId: string, chunk: ChunkClickData) => {
    const targetSection = drug?.sections?.find(
      s => `section-${s.id}` === targetSectionId
    );
    if (targetSection && isSubsectionCheck(targetSection)) {
      const parentNum = getParentSectionNum(targetSection);
      setCollapsedParents(prev => {
        if (prev.has(parentNum)) {
          const newSet = new Set(prev);
          newSet.delete(parentNum);
          return newSet;
        }
        return prev;
      });
    }
    setExpandedSections(prev => new Set([...prev, targetSectionId]));
    const sectionEl = sectionRefs.current.get(targetSectionId);
    if (!sectionEl) return;
    // Scroll only after the reveal commit has removed `hidden`.
    revealRow(targetSectionId, () => {
      waitForContent(sectionEl).then(container => {
        if (!container) return;
        const highlighted = anchorMatchAndHighlight(
          container, chunk.anchorStart, chunk.anchorEnd, chunk.anchorLength, tabId,
        );
        if (highlighted) {
          scrollToChunkHighlight();
        } else {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }, [drug?.sections, revealRow]);

  const handleChunkClick = useCallback((chunk: ChunkClickData) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }

    // EPAR view symmetry — preserve the EPAR doc when the user clicks a
    // chunk-link inside CEPI. EPAR has its own viewer (EPARViewerEmbed)
    // with chunk-based navigation; FDA section anchors don't resolve here.
    if (isViewingEpar) {
      return;
    }

    withFullLabel(chunk.sectionNumber, () => {
    const needsViewSwitch = selectedHistoricalId !== null
      || selectedVersionId !== null
      || selectedSbaId !== null
      || selectedFdaLetterId !== null
      || selectedApprovalLetterId !== null;
    if (needsViewSwitch) {
      sectionRefs.current.clear();
      setSelectedHistoricalId(null);
      setSelectedVersionId(null);
      setSelectedSbaId(null);
      setSelectedFdaLetterId(null);
      setSelectedApprovalLetterId(null);
    }
    clearHighlights();

    const findSection = (): string | null => {
      for (const [sectionId, el] of sectionRefs.current.entries()) {
        if (el.getAttribute('data-section-number') === chunk.sectionNumber) {
          return sectionId;
        }
      }
      return null;
    };

    let targetSectionId = findSection();

    // If subsection not found, uncollapse parent first
    if (!targetSectionId && chunk.sectionNumber.includes('.')) {
      const parentNum = chunk.sectionNumber.split('.')[0]!;
      setCollapsedParents(prev => {
        const newSet = new Set(prev);
        newSet.delete(parentNum);
        return newSet;
      });
    }

    if (targetSectionId) {
      doChunkHighlight(targetSectionId, chunk);
      return;
    }

    // Section not found by number (e.g., empty/Unknown) — only search
    // un-numbered sections (Highlights, Medication Guide, etc.)
    const unnumberedSectionIds: string[] = [];
    for (const [sectionId, el] of sectionRefs.current.entries()) {
      const num = el.getAttribute('data-section-number');
      if (!num || num === 'Unknown') {
        unnumberedSectionIds.push(sectionId);
      }
    }
    setExpandedSections(prev => new Set([...prev, ...unnumberedSectionIds]));

    const searchUnnumbered = (attempt = 0) => {
      for (const sectionId of unnumberedSectionIds) {
        const el = sectionRefs.current.get(sectionId);
        if (!el) continue;
        const container = el.querySelector('.spl-content, .prose');
        if (!container) continue;
        const text = collapseWs(container.textContent || '');
        const startAnchor = collapseWs(chunk.anchorStart).trim();
        const endAnchor = chunk.anchorEnd ? collapseWs(chunk.anchorEnd).trim() : '';
        const hasStart = text.indexOf(startAnchor) !== -1;
        const hasEnd = endAnchor ? text.indexOf(endAnchor) !== -1 : false;
        if (hasStart || hasEnd) {
          doChunkHighlight(sectionId, chunk);
          return;
        }
      }
      if (attempt < 5) {
        searchTimerRef.current = setTimeout(() => searchUnnumbered(attempt + 1), 500);
      }
    };

    requestAnimationFrame(() => searchUnnumbered(0));
    });
  }, [withFullLabel, isViewingEpar, selectedHistoricalId, selectedVersionId, selectedSbaId, selectedEparId, selectedFdaLetterId, selectedApprovalLetterId, doChunkHighlight]);

  // Compute "viewing context" based on which version is currently selected
  // This recalculates previous/next/current relative to the viewed version
  //
  // IMPORTANT: Navigation mental model:
  //   - Versions are ordered chronologically ASCENDING: [v1, v2, v3, v4, v5]
  //   - "Previous" = older version (lower index)
  //   - "Next" = newer version (higher index)
  //   - "Latest" = last in array (highest version_number)
  //   - "First" = first in array (lowest version_number)
  const currentlyViewing = useMemo(() => {
    if (!allVersionsData?.versions || allVersionsData.versions.length === 0) {
      return null;
    }

    // Backend returns versions sorted DESCENDING (newest first).
    // Re-sort to ASCENDING (oldest first) for intuitive navigation:
    // [v1, v2, v3, v4, v5] where index 0 = oldest, index N-1 = newest
    const versions = [...allVersionsData.versions].sort(
      (a, b) => (a.version_number ?? 0) - (b.version_number ?? 0)
    );

    // Find the index of the currently viewed version
    let currentIdx: number;

    if (selectedHistoricalId !== null) {
      // Find the historical version in the sorted list
      currentIdx = versions.findIndex(
        v => v.source === 'fda_historical' && v.id === selectedHistoricalId
      );
    } else if (selectedVersionId !== null) {
      // Find the draft version in the sorted list
      currentIdx = versions.findIndex(
        v => v.source === 'draft' && v.id === selectedVersionId
      );
    } else {
      // Viewing FDA current - find it in the sorted list
      currentIdx = versions.findIndex(
        v => v.source === 'fda_current' && v.id === parseInt(currentDrugId || '0', 10)
      );
    }

    // Fallback: if not found, default to latest (last in ascending array)
    if (currentIdx === -1) {
      currentIdx = versions.length - 1;
    }

    const currentVersion = versions[currentIdx];

    // With ASCENDING order:
    // - Previous (older) = index - 1 (if exists)
    // - Next (newer) = index + 1 (if exists)
    const hasPrevious = currentIdx > 0;
    const hasNext = currentIdx < versions.length - 1;
    const previousVersion = hasPrevious ? versions[currentIdx - 1] : null;
    const nextVersion = hasNext ? versions[currentIdx + 1] : null;

    // Latest = last element in ascending array (highest version_number)
    const latestVersion = versions[versions.length - 1];

    // Flags
    const isFirst = currentIdx === 0;
    const isLatest = currentIdx === versions.length - 1;

    return {
      current: currentVersion,
      previous: previousVersion,
      next: nextVersion,
      latest: latestVersion,
      is_first: isFirst,
      is_latest: isLatest,
      fda_count: allVersionsData.fda_count,
      draft_count: allVersionsData.draft_count,
      all_versions: versions, // Now sorted ascending
    };
  }, [allVersionsData, selectedHistoricalId, selectedVersionId, currentDrugId]);

  // Task B4 (submission-group-selector-v2 plan): the "Compare: Select
  // version..." picker's FDA-current/historical option source. Once the
  // review-cycle view is on (`sbaCycleInfo` present), B2 hides the main
  // version `<select>`'s FDA optgroup — the submission control now owns
  // FDA label navigation — so Compare must stop reading FDA options off
  // `currentlyViewing.all_versions` (a separate, unrelated version list)
  // and instead source them from the SAME label set the submission
  // control renders: `available_cycles`' attached `label_versions`
  // UNION `unmatchedLabels` (every US FDA label attaches to a cycle OR
  // is categorized unmatched — A1-A4 guarantee no label falls through
  // both). Mapping mirrors `labelDispatch` in VersionSelector exactly:
  // `is_current` -> `('fda_current', drugs.id)`, else ->
  // `('fda_historical', drug_version_id)`. Drafts still come from
  // `allVersionsData.versions` (unaffected — B3 gives them their own
  // picker later, out of scope here). When `sbaCycleInfo` is null
  // (non-US / flag off), Compare is unchanged — falls back to
  // `currentlyViewing.all_versions` as before.
  const compareOptions = useMemo((): UnifiedVersionSummary[] => {
    const allVersions = currentlyViewing?.all_versions ?? [];
    if (!sbaCycleInfo) {
      return allVersions;
    }
    const currentDrugIdNum = parseInt(currentDrugId || '0', 10);
    const toOption = (lv: SBACycleLabelVersion | UnmatchedLabel): UnifiedVersionSummary => ({
      id: lv.is_current ? currentDrugIdNum : lv.drug_version_id,
      source: lv.is_current ? 'fda_current' : 'fda_historical',
      version_number: lv.version_number,
      effective_date: lv.effective_date,
      is_current: lv.is_current,
    });
    const cycleLabels = sbaCycleInfo.availableCycles.flatMap((c) => c.label_versions ?? []);
    const unmatchedLabels = sbaCycleInfo.unmatchedLabels ?? [];
    const fdaOptions = [...cycleLabels, ...unmatchedLabels].map(toOption);
    const draftOptions = allVersions.filter((v) => v.source === 'draft');
    return [...fdaOptions, ...draftOptions].sort(
      (a, b) => (a.version_number ?? 0) - (b.version_number ?? 0),
    );
  }, [currentlyViewing, sbaCycleInfo, currentDrugId]);

  // Fetch drug data when currentDrugId changes (from URL nav)
  useEffect(() => {
    let cancelled = false;

    // A route change must invalidate drug A BEFORE awaiting drug B metadata.
    // Otherwise drug A's SBA request can resolve during getDrug(B) and still
    // pass both guards because the refs still identify A.
    sbaAbortRef.current?.abort();
    sbaAbortRef.current = null;
    ++sbaGenerationRef.current;
    sbaDrugIdRef.current =
      currentDrugId && /^\d+$/.test(currentDrugId) ? parseInt(currentDrugId, 10) : null;
    setSbaListPayload(null);
    setSbaListStatus('idle');
    setReviewLettersLoaded(false);
    setSelectedApprovalLetterId(null);
    setSbaDetailError(null);
    // Same reason for the AU/EU doc lists: the stale-id redirect must not
    // compare drug B's URL doc id against drug A's still-"loaded" list.
    setAuDocsLoaded(false);
    setEuDocsLoaded(false);
    regDocsDrugIdRef.current = null;

    const fetchDrug = async () => {
      if (!currentDrugId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Check if ID is a number (drug_id) or UUID (set_id)
        const isNumeric = /^\d+$/.test(currentDrugId);

        let data;
        if (isNumeric) {
          // Fetch by drug_id (numeric ID)
          const drugId = parseInt(currentDrugId);
          data = await authApi.getDrug(drugId);

          if (cancelled) return;

          // Fetch version history if drug has set_id
          if (data.set_id) {
            fetchVersionHistory(data.set_id);
          }

          // Fetch all versions (FDA current + historical + drafts - 3-table architecture)
          fetchAllVersions(drugId);
          // Fetch linked SBA (FDA Review) documents.
          // Region MUST come from the local `data`, not the `drug` state —
          // `setDrug(data)` has not been applied in this render yet, so
          // `drug?.region` would read stale (undefined on first load) and
          // silently fetch classic for a user who opted in.
          // Preference is read through a REF, not the captured
          // `useSubmissionView`: this closure was built before
          // `await authApi.getDrug(...)`, so a change during that await would
          // otherwise load this drug in the opposite view from what is now true.
          //
          // 2026-08-17-b: `isAdminRef` is NO LONGER consulted here. Submission
          // view is GA for every user, so there is nothing to wait for auth
          // about — which also removes the cold-load flip this used to cause
          // (mount picked classic because auth was still in flight, then the
          // promotion effect upgraded once /auth/sync landed). What still
          // differs by role is the HA-review document set, and the BACKEND
          // scopes that (routes.py get_drug_sba_documents), so the frontend
          // never needs to know the role to choose a view.
          const mountMode: SbaListMode =
            submissionViewPrefRef.current && data.region === 'US'
              ? 'submission'
              : 'classic';
          // Claim the promotion slot when the mount fetch ALREADY asked for
          // submission — otherwise the promotion effect sees a still-null
          // `sbaCycleInfo` (a legacy/unavailable response) and fires a second,
          // redundant request for a view this drug just declined to provide.
          if (mountMode === 'submission') {
            promotedForDrugRef.current = drugId;
          }
          if (data.region === 'US_FDA'
            || (data.region === 'US' && isUsFdaVisible(isAdminRef.current))) {
            // US-Drugs@FDA mode: letters/reviews come from
            // us_fda_submissions, never the /sbas roll-up.
            promotedForDrugRef.current = drugId;
          } else {
            void fetchDrugSBAs(drugId, mountMode, 'mount');
          }
          // Fetch linked EPAR (EMA Review) documents
          fetchDrugEPARs(drugId);
          // Fetch linked UK MHRA PIL PDFs (region='UK' drugs only;
          // returns empty list for non-UK drugs).
          fetchDrugUKPILs(drugId);
          // Fetch linked CA Health Canada PM PDFs (region='CA' drugs
          // only; returns empty list for non-CA drugs).
          fetchDrugCAPMs(drugId);
          // Fetch linked AU TGA (PI / CMI) documents (region='AU'
          // drugs only; returns empty list for non-AU drugs).
          regDocsDrugIdRef.current = drugId;
          setAuDocsLoaded(false);
          fetchDrugAUDocs(drugId);
          // Fetch linked EU EMA SmPC documents (region='EU' drugs only;
          // returns empty list for non-EU drugs).  Reset the loaded flag
          // first so the render gate re-arms for this drug.
          setEuDocsLoaded(false);
          fetchDrugEUDocs(drugId);
          // Fetch linked JP PMDA supplementary PDFs (region='JP' drugs
          // only; returns empty list for non-JP drugs).
          fetchDrugJpPdfDocs(drugId);
          // Fetch linked CN NMPA supplementary PDFs (region='CN' drugs).
          // Backend is admin-gated server-side (empty/403 for non-admins), so
          // the doc list stays empty and the admin-gated UI hides it. Called
          // unconditionally like the JP fetch to avoid the isAdmin cold-load
          // race (isAdmin resolves after this fetch fires).
          fetchDrugZhPdfDocs(drugId);
          // Swissmedic labels (region='CH'); empty for other regions / non-admins.
          fetchDrugChPdfDocs(drugId);
          // BfArM labels (region='DE'); empty for other regions / non-admins.
          fetchDrugDePdfDocs(drugId);
          // Drugs@FDA label PDFs (region='US'); admin-gated server-side, so
          // empty for non-admins. Called unconditionally like the JP/CN
          // fetches to avoid the isAdmin cold-load race (isAdmin resolves
          // after this fetch fires).
          fetchDrugFdaLabelPdfDocs(drugId);
          // Fetch linked FDA letters
          fetchDrugFDALetters(drugId);
        } else {
          // Fetch by set_id (UUID) - get the latest/current version
          // This requires a new API endpoint or different approach
          // For now, show error message suggesting to use numeric ID
          throw new Error(
            'Drug lookup by Set ID is not yet supported. ' +
            'Please navigate to this drug from search results, which will use the numeric Drug ID.'
          );
        }

        if (!cancelled) {
          setDrug(data);
          setDrugForId(currentDrugId);
        }
      } catch (err: any) {
        if (!cancelled) {
          logger.error('Error fetching drug:', err);
          setError(
            err.message ||
            err.response?.data?.detail ||
            'Drug not found. Please ensure you are using a valid Drug ID.'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          // Every exit path lands here — resolved metadata, a failed request,
          // and the non-numeric/set_id throw above — so the page is marked
          // settled on this route id even when it has no NEW drug to show.
          // A failure therefore re-enables route-gated controls (the user can
          // still act on what is on screen) instead of freezing them.
          setSettledDrugRouteId(currentDrugId);
        }
      }
    };

    fetchDrug();
    return () => {
      cancelled = true;
      sbaAbortRef.current?.abort();
      sbaAbortRef.current = null;
      ++sbaGenerationRef.current;
    };
    // The preference is DELIBERATELY not a dep: adding it would re-run this
    // entire drug fetch on every toggle. That omission is safe only because
    // the body reads `submissionViewPrefRef.current` — a ref that always holds
    // the live value — instead of the captured `useSubmissionView`. "React
    // rebuilds the closure on every render" is NOT the reason: the closure
    // that matters here is the one already suspended at
    // `await authApi.getDrug(...)`, and it keeps whatever it captured. If you
    // ever replace the ref read with the state variable, this dep array
    // becomes a stale-read bug, not a deliberate optimisation.
  }, [currentDrugId]);

  const fetchAllVersions = async (drugId: number) => {
    try {
      logger.debug('[All Versions] Fetching unified versions for drug:', { drugId });
      const data = await authApi.getAllVersions(drugId);
      logger.debug('[All Versions] Response:', { data });
      setAllVersionsData(data);
    } catch (err) {
      logger.error('Failed to fetch all versions:', { error: err });
      setAllVersionsData(null);
    }
  };

  // Deferred scroll for ORIGIN-version citations. The click handler switches
  // the viewer to ('fda_historical', drug_version_id) and parks the target
  // section NUMBER in the ref; the accordion rows only exist once
  // ``historicalVersionData`` lands, so the scroll has to wait for it.
  //
  // Section rows are keyed ``section-${section.id}`` — the DATABASE id, not
  // the section number — so the number has to be resolved against the loaded
  // sections first. (Scrolling by number silently found nothing.)
  //
  // Section-level only, deliberately: origin citations carry no chunk_id (the
  // backend omits it because origin chunk ids collide with current-label
  // ones), so there is nothing to highlight and pretending otherwise would
  // box the wrong text.
  // Two entry points, one behaviour:
  //   * a CEPI origin citation click (parks the number in the ref), and
  //   * an Investigator deep link ``?source=historical&version=N&hsection=14``.
  //
  // A dedicated ``hsection`` param — NOT the existing ``section`` — because the
  // ``section`` handler deliberately RESETS to the current FDA label before
  // scrolling (DrugDetailPage: "switch back to current FDA label first"). That
  // is right for search-result links and exactly wrong here: it would undo the
  // version switch we just made.
  //
  // Matching mirrors ``findAndScrollToSection``: scan ``data-section-number``
  // off the rendered rows rather than reconstructing an element id, and fall
  // back to the parent number. Same reason it does — the row id is keyed on the
  // section's DATABASE id, and a pre-PLR origin label often renders only the
  // parent (§5) of a modern subsection (§5.12).
  useEffect(() => {
    const wanted = pendingHistoricalSectionRef.current
      || searchParams.get('hsection');
    if (!wanted || !selectedHistoricalId) return;
    if (!historicalVersionData?.sections?.length) return;  // wait for the fetch

    pendingHistoricalSectionRef.current = null;

    withFullLabel(wanted, () => resolveHistoricalSection(wanted));
  }, [selectedHistoricalId, historicalVersionData, searchParams, resolveHistoricalSection, withFullLabel]);


  /**
   * Fetch the drug's SBA list in an explicit mode.
   *
   * `classic` → `GET /sbas` (legacy flat shape, latest revision per doc_type).
   * `submission` → `GET /sbas?cycle=all` (cycle envelope, ALL revisions).
   *
   * These return DIFFERENT document sets, which is exactly why the page
   * refetches on toggle instead of fetching the envelope once and rendering
   * both from it — see the spec §4.
   *
   * `reason` distinguishes a mount/drug-change load from a user-initiated
   * runtime toggle. Only the latter runs selection reconciliation (Task 5).
   */
  const fetchDrugSBAs = async (
    drugId: number,
    requestedMode: SbaListMode,
    reason: 'mount' | 'runtime-toggle',
  ) => {
    // A request for a drug the page has already navigated away from can never
    // pass isCurrent(), so it would set 'loading' and never clear it. Reject it
    // before touching status. Covers the window where `drug` is still the old
    // drug but the route effect has already repointed the ref.
    if (drugId !== sbaDrugIdRef.current) return;

    sbaAbortRef.current?.abort();
    const controller = new AbortController();
    sbaAbortRef.current = controller;
    const generation = ++sbaGenerationRef.current;
    const isCurrent = () =>
      generation === sbaGenerationRef.current && drugId === sbaDrugIdRef.current;

    setSbaListStatus('loading');
    setReviewLettersLoaded(false);
    try {
      const data = await authApi.getDrugSBAs(
        drugId,
        requestedMode === 'submission' ? 'all' : undefined,
        controller.signal,
      );
      if (!isCurrent()) return;

      if (requestedMode === 'submission' && !hasCycleEnvelope(data)) {
        // Capability flag off / non-US / degenerate drug. The server gave us a
        // REAL legacy payload — commit that, never envelope documents.
        commitSbaPayload({
          drugId,
          documents: data.documents ?? [],
          reviewLetters: [],
          cycleInfo: null,
        }, reason);
        setSelectedApprovalLetterId(null);
        setSbaListStatus('unavailable');
        return;
      }

      commitSbaPayload(
        {
          drugId,
          documents: data.documents ?? [],
          reviewLetters: (
            requestedMode === 'submission' && hasCycleEnvelope(data)
              ? data.review_letters ?? []
              : []
          ),
          // `requestedMode` is load-bearing, not just a type narrower: a
          // CLASSIC requester must never be flipped into submission view by
          // the response alone. The server cannot return an envelope without
          // `?cycle=`, but the intent is stated here rather than assumed.
          cycleInfo: requestedMode === 'submission' && hasCycleEnvelope(data)
            ? {
                selectedCycle: data.selected_cycle ?? null,
                availableCycles: data.available_cycles ?? [],
                unclassifiedCount: data.unclassified_count ?? 0,
                unclassifiedLetterCount: data.unclassified_letter_count ?? 0,
                unmatchedLabels: data.unmatched_labels ?? [],
              }
            : null,
        },
        reason,
      );
      if (requestedMode === 'classic') {
        setSelectedApprovalLetterId(null);
      }
      setSbaListStatus('idle');
    } catch (err) {
      if (controller.signal.aborted) return;
      logger.error('Failed to fetch SBA documents:', { error: err });

      if (requestedMode === 'submission') {
        // One fallback to the legacy shape so a failed opt-in still leaves the
        // user with a usable, shape-correct classic list.
        try {
          const fallback = await authApi.getDrugSBAs(drugId, undefined, controller.signal);
          if (!isCurrent()) return;
          commitSbaPayload(
            {
              drugId,
              documents: fallback.documents ?? [],
              reviewLetters: [],
              cycleInfo: null,
            },
            reason,
          );
          setSelectedApprovalLetterId(null);
        } catch (fallbackErr) {
          if (controller.signal.aborted) return;
          logger.error('Legacy fallback also failed:', { error: fallbackErr });
          // Retain the last successful payload — do NOT clear the list.
        }
      }
      // Mode-appropriate copy: only a runtime toggle actually attempted a
      // view switch. A mount-time failure (classic or submission) must not
      // tell a user who never clicked anything that a switch failed.
      if (isCurrent()) {
        setSbaListStatus(reason === 'runtime-toggle' ? 'transition-error' : 'load-error');
      }
    } finally {
      if (isCurrent()) {
        setReviewLettersLoaded(true);
      }
    }
  };

  /**
   * Single commit point. Task 5 adds selection reconciliation here; keeping
   * every write to `sbaListPayload` funnelled through one function is what
   * guarantees documents and cycleInfo are never written apart.
   */
  const commitSbaPayload = (payload: SbaListPayload, reason: 'mount' | 'runtime-toggle') => {
    // Mount-time deep links keep their existing behavior; this rule addresses
    // the NEW runtime-mode transition only.
    if (reason === 'runtime-toggle') {
      const openId = selectedSbaIdRef.current;
      if (openId !== null && !payload.documents.some((d) => d.id === openId)) {
        // The open review is absent from the target shape. Fall back to the
        // drug's current label. Never substitute a different review merely
        // because it shares a doc_type. `selectedSbaId` is NOT cleared here —
        // `handleUnifiedVersionChange('fda_current', …)` runs `clearAll()`,
        // which nulls it (plus every sibling selection). The clears below are
        // viewer state `clearAll()` does not touch.
        setSbaDocDetail(null);
        setSbaHighlightChunkIds([]);
        setSbaChunkAnchors({});
        setSbaPendingPageScroll(null);
        handleUnifiedVersionChange('fda_current', payload.drugId);
      }
    }
    setSbaListPayload(payload);
  };

  /** User clicked the Beta toggle. Label follows the committed response. */
  const handleSubmissionViewToggle = () => {
    // 2026-08-17-b: no admin check — submission view is GA. The `drug` guard
    // stays: a toggle before metadata lands has no region to validate and no
    // id to fetch with.
    if (!drug) return;
    const next: SbaListMode = sbaCycleInfo ? 'classic' : 'submission';
    setUseSubmissionView(next === 'submission');
    void fetchDrugSBAs(drug.id, next, 'runtime-toggle');
  };

  // Retry an opted-in user into Submission View when the mount fetch could not.
  //
  // 2026-08-17-b: this is NO LONGER an admin-promotion effect — the mount
  // fetch now picks submission for anyone with the preference, so the
  // auth-race it originally existed for is gone. It is kept as the safety net
  // for the remaining case: the mount fetch ran before `drugRouteSettled`, so
  // `sbaCycleInfo` is still null and nothing else would retry.
  //
  // `promotedForDrugRef` makes this ONE SHOT PER DRUG. Without it, a response
  // that comes back `unavailable` leaves `sbaCycleInfo` null, the effect's own
  // condition stays true, and it refires forever. Keying on the drug id also
  // resets it for free on navigation.
  //
  // Reason is `'mount'`, not `'runtime-toggle'`: no user action occurred, so
  // selection reconciliation must not run and clear a deep-linked document.
  const promotedForDrugRef = useRef<number | null>(null);
  useEffect(() => {
    if (!drug || drug.region !== 'US') return;
    if (usFdaMode) return;
    if (!drugRouteSettled) return;
    if (!submissionViewPrefRef.current) return;
    if (sbaCycleInfo) return;
    if (promotedForDrugRef.current === drug.id) return;
    promotedForDrugRef.current = drug.id;
    void fetchDrugSBAs(drug.id, 'submission', 'mount');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drug, drugRouteSettled, sbaCycleInfo]);

  // US-Drugs@FDA links + submissions (only in the new mode). A US drug with
  // no linked US_FDA product — or a failed counterparts read — shows the
  // DailyMed picker only.
  useEffect(() => {
    if (!usFdaMode || !drug) return;
    const d = drug;
    let alive = true;
    (async () => {
      let counterparts: { us_fda: UsFdaProductRef[]; dailymed: UsFdaProductRef[] } = { us_fda: [], dailymed: [] };
      try {
        counterparts = await authApi.getUsFdaCounterparts(d.id);
      } catch (err) {
        logger.error('Failed to fetch US_FDA counterparts:', { error: err });
      }
      if (!alive) return;
      const products: UsFdaProductRef[] = d.region === 'US_FDA'
        ? [{ drug_id: d.id, set_id: d.set_id ?? null, brand_name: d.brand_name ?? null, manufacturer: d.manufacturer ?? null }]
        : counterparts.us_fda ?? [];
      const apps = await Promise.all(products.map(async (product) => {
        try {
          const r = await authApi.getUsFdaSubmissions(product.drug_id);
          return { product, submissions: r.submissions ?? [] };
        } catch (err) {
          logger.error('Failed to fetch US_FDA submissions:', { error: err });
          return { product, submissions: [] };
        }
      }));
      if (!alive) return;
      setUsFdaData({
        drugId: d.id,
        dailymed: counterparts.dailymed ?? [],
        apps: apps.filter((a) => a.submissions.length > 0),
      });
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drug?.id, usFdaMode]);

  // Active source follows the URL (C1): a US_FDA page or a Drugs@FDA
  // document source → Drugs@FDA; else DailyMed.
  const usFdaSourceParam = searchParams.get('source') ?? '';
  const usFdaVersionParam = Number(searchParams.get('version'));
  const isDrugsAtFdaSource = DRUGS_AT_FDA_SOURCES.has(usFdaSourceParam);
  const drugsAtFdaActive = usFdaMode && (drug?.region === 'US_FDA' || isDrugsAtFdaSource);
  const drugsAtFdaSelected = drugsAtFdaActive && isDrugsAtFdaSource && usFdaVersionParam > 0
    ? { source: usFdaSourceParam as DrugsAtFdaSource, id: usFdaVersionParam }
    : null;
  const usFdaApps = usFdaMode && usFdaData?.drugId === drug?.id ? usFdaData.apps : NO_US_FDA_APPS;
  // The DailyMed picker (VersionSelector) gets a dimmable wrapper only in
  // Layout A; otherwise it renders exactly as before.
  const wrapDailyMedPicker = (el: React.ReactElement) => (usFdaMode ? (
    <div data-testid="dailymed-picker" data-active={drugsAtFdaActive ? 'false' : 'true'}
      className={drugsAtFdaActive ? 'opacity-50' : undefined}>
      {el}
    </div>
  ) : el);
  const openDrugsAtFdaDocument = (usFdaDrugId: number, source: DrugsAtFdaSource, documentId: number) => {
    // Drugs@FDA documents open on their US_FDA product page (a US page's
    // picker navigates there), so review/letter ownership always matches the
    // page's drug and compare stays inside that application.
    navigate(`/drug/${usFdaDrugId}?source=${source}&version=${documentId}`,
      { replace: usFdaDrugId === drug?.id });
  };

  // A US_FDA page opened without a document lands on the submission whose
  // label is current, else the first that has a label (newest-first), else on the first
  // submission's first document.
  useEffect(() => {
    // `drug` still holds the page being left while the route changes.
    if (!usFdaMode || drug?.region !== 'US_FDA' || String(drug.id) !== urlId
      || searchParams.get('source')) return;
    const subs = usFdaApps.flatMap((a) => a.submissions);
    const withLabel = subs.find((s) => s.label?.is_current) ?? subs.find((s) => s.label);
    const doc = withLabel ? firstDocument(withLabel) : (subs[0] ? firstDocument(subs[0]) : null);
    if (doc) setSearchParams({ source: doc.source, version: String(doc.id) }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usFdaMode, drug?.region, drug?.id, urlId, usFdaApps, searchParams]);

  // NOTE: the old `handleSbaCycleChange` (per-cycle refetch handler for
  // VersionSelector's now-removed cycle `<select>`) was retired here —
  // linked split-dropdowns design, Task 4 Step 2. `fetchDrugSBAs` above
  // fetches the FULL envelope once; all label⇄doc cross-linking happens
  // client-side in `VersionSelector`, so there is nothing left to
  // refetch on selection. Its deep-link URL cleanup (clearing a stale
  // `?source=sba&version=`) is superseded by `handleUnifiedVersionChange`
  // below: every branch there (including the label-open path via
  // `labelDispatch` → `onUnifiedVersionChange('fda_current'|
  // 'fda_historical', ...)` and the doc-open path via
  // `onUnifiedVersionChange('sba', id)`) calls `setSearchParams({...},
  // { replace: true })` with a brand-new params object — a full replace,
  // not a merge — so any previously-set `source`/`version` pair is
  // always overwritten, never left stale.

  const fetchDrugEPARs = async (drugId: number) => {
    try {
      const data = await authApi.getDrugEPARs(drugId);
      setEparDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch EPAR documents:', { error: err });
      setEparDocuments([]);
    }
  };

  const fetchDrugUKPILs = async (drugId: number) => {
    try {
      const data = await authApi.getDrugUKPILs(drugId);
      setUkPilDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch UK PIL documents:', { error: err });
      setUkPilDocuments([]);
    }
  };

  const fetchDrugCAPMs = async (drugId: number) => {
    try {
      const data = await authApi.getDrugCAPMs(drugId);
      setCaPmDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch CA PM documents:', { error: err });
      setCaPmDocuments([]);
    }
  };

  const fetchDrugAUDocs = async (drugId: number) => {
    let docs: SBASummaryForDrug[] = [];
    try {
      const data = await authApi.getDrugAUDocs(drugId);
      docs = data.documents || [];
    } catch (err) {
      logger.error('Failed to fetch AU TGA documents:', { error: err });
    }
    if (regDocsDrugIdRef.current !== drugId) return;  // route moved on
    setAuDocuments(docs);
    setAuDocsLoaded(true);
  };

  const fetchDrugEUDocs = async (drugId: number) => {
    let docs: SBASummaryForDrug[] = [];
    try {
      const data = await authApi.getDrugEUDocs(drugId);
      docs = data.documents || [];
    } catch (err) {
      logger.error('Failed to fetch EU EMA SmPC documents:', { error: err });
    }
    if (regDocsDrugIdRef.current !== drugId) return;  // route moved on
    setEuDocuments(docs);
    // Resolved (with or without docs) — unblocks the render gate so an
    // un-backfilled EU drug can fall through to the section-text view.
    setEuDocsLoaded(true);
  };

  const fetchDrugJpPdfDocs = async (drugId: number) => {
    try {
      const data = await authApi.getJPPdfDocs(drugId);
      setJpPdfDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch JP PMDA supplementary documents:', { error: err });
      setJpPdfDocuments([]);
    }
  };

  // BfArM (DE) + HPRA (IE) + AIFA (IT) label PDFs share this slot: each
  // listing is empty for other regions, so the three merge into one list.
  const fetchDrugDePdfDocs = async (drugId: number) => {
    setDePdfDocsLoaded(false);
    try {
      // one family's listing failing must not hide the others
      const safe = async (f: () => Promise<{ documents?: SBASummaryForDrug[] }>) => {
        try { return await f(); } catch (err) {
          logger.error('Failed to fetch label PDF documents:', { error: err });
          return { documents: [] };
        }
      };
      const lists = await Promise.all([
        safe(() => authApi.getDrugDePdfDocs(drugId)),
        safe(() => authApi.getDrugLabelPdfDocs(drugId, 'ie_pdf')),
        safe(() => authApi.getDrugLabelPdfDocs(drugId, 'it_pdf')),
      ]);
      setDePdfDocuments(lists.flatMap((data, i) => (data.documents || []).map(
        (d: SBASummaryForDrug) => ({ ...d, family: (['de_pdf', 'ie_pdf', 'it_pdf'] as const)[i] ?? 'de_pdf' }))));
    } catch (err) {
      logger.error('Failed to fetch BfArM label documents:', { error: err });
      setDePdfDocuments([]);
    }
    setDePdfDocsLoaded(true);
  };

  const fetchDrugFdaLabelPdfDocs = async (drugId: number) => {
    try {
      const data = await authApi.getDrugFdaLabelPdfDocs(drugId);
      setFdaLabelPdfDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch Drugs@FDA label PDF documents:', { error: err });
      setFdaLabelPdfDocuments([]);
    }
  };

  const fetchDrugChPdfDocs = async (drugId: number) => {
    try {
      const data = await authApi.getDrugChPdfDocs(drugId);
      setChPdfDocuments(data.documents || []);
    } catch (err) {
      logger.error('Failed to fetch Swissmedic label documents:', { error: err });
      setChPdfDocuments([]);
    }
  };

  const fetchDrugZhPdfDocs = async (drugId: number) => {
    try {
      const data = await authApi.getZhPdfDocs(drugId);
      setZhPdfDocuments(data.documents || []);
    } catch (err) {
      // Non-admins get a 403 here (endpoint is admin-gated server-side) — that
      // is expected; keep the list empty so the admin-only UI stays hidden.
      logger.error('Failed to fetch CN NMPA supplementary documents:', { error: err });
      setZhPdfDocuments([]);
    }
  };

  const fetchDrugFDALetters = async (drugId: number) => {
    try {
      const data = await authApi.getDrugFDALetters(drugId);
      setFdaLetters(data.letters || []);
    } catch (err) {
      logger.error('Failed to fetch FDA letters:', { error: err });
      setFdaLetters([]);
    }
  };

  const fetchVersionHistory = async (setId: string) => {
    setIsLoadingVersions(true);
    try {
      const history = await authApi.getVersionHistory(setId);
      setVersions(history.versions);
      // Default to FDA label view (null), user can select drafts from dropdown
      setSelectedVersionId(null);
    } catch (err) {
      logger.error('Failed to fetch version history:', { error: err });
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const fetchVersionDetails = async (versionId: number) => {
    setIsLoadingComments(true);
    try {
      const detail = await authApi.getVersion(versionId);

      // Debug logging for table rendering investigation
      if (detail.document_sections) {
        const sectionsWithTables = detail.document_sections.filter(
          (s: { content_html?: string }) => s.content_html && s.content_html.includes('<table')
        );
        console.log('[DrugDetailPage] Version details loaded:', {
          versionId,
          totalSections: detail.document_sections.length,
          sectionsWithTables: sectionsWithTables.length,
          sampleSection: sectionsWithTables[0] ? {
            title: sectionsWithTables[0].title,
            hasContentHtml: !!sectionsWithTables[0].content_html,
            contentHtmlPreview: sectionsWithTables[0].content_html?.substring(0, 150),
          } : null,
        });
      }

      setVersionDetail(detail);
      setComments(detail.comments || []);
    } catch (err) {
      logger.error('Failed to fetch version details:', { error: err });
      setVersionDetail(null);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  useEffect(() => {
    if (selectedVersionId) {
      fetchVersionDetails(selectedVersionId);
      fetchAdjacentVersions(selectedVersionId);
    } else {
      // Clear version details when switching to FDA view
      setVersionDetail(null);
      setComments([]);
      // For FDA view with user-uploaded drafts, fetch draft adjacent versions
      if (versions.length > 0) {
        // Use the current/latest version to get adjacent info
        const currentVersion = versions.find(v => v.is_current) || versions[0];
        if (currentVersion) {
          fetchAdjacentVersions(currentVersion.id);
        }
      } else {
        setAdjacentVersions(null);
      }
    }
  }, [selectedVersionId, versions, drug?.id]);

  const fetchAdjacentVersions = async (versionId: number) => {
    try {
      const data = await authApi.getAdjacentVersions(versionId);
      setAdjacentVersions(data);
    } catch (err) {
      logger.error('Failed to fetch adjacent versions:', { error: err });
      setAdjacentVersions(null);
    }
  };

  // Fetch historical version data when selectedHistoricalId changes
  useEffect(() => {
    if (selectedHistoricalId) {
      // Clear stale data immediately so displayDrug falls back to loading state
      setHistoricalVersionData(null);
      setHistoricalVersionError(null);
      let cancelled = false;
      const fetchHistoricalVersion = async () => {
        setIsLoadingHistorical(true);
        try {
          logger.debug('[DrugDetailPage] Fetching historical version:', { selectedHistoricalId });
          const data = await authApi.getHistoricalVersion(selectedHistoricalId);
          if (!cancelled) {
            logger.debug('[DrugDetailPage] Historical version data:', { data });
            setHistoricalVersionData(data);
          }
        } catch (err) {
          if (!cancelled) {
            logger.error('Failed to fetch historical version:', { error: err });
            setHistoricalVersionData(null);
            setHistoricalVersionError('Historical label version is unavailable.');
          }
        } finally {
          if (!cancelled) {
            setIsLoadingHistorical(false);
          }
        }
      };
      fetchHistoricalVersion();
      return () => { cancelled = true; };
    }
    setHistoricalVersionData(null);
    setHistoricalVersionError(null);
    setIsLoadingHistorical(false);
    return undefined;
  }, [selectedHistoricalId]);

  // Fetch SBA document detail when selectedSbaId changes
  useEffect(() => {
    const routeDrugId =
      currentDrugId && /^\d+$/.test(currentDrugId)
        ? parseInt(currentDrugId, 10)
        : null;
    if (!selectedSbaId || !drug || routeDrugId == null || drug.id !== routeDrugId) {
      setSbaDocDetail(null);
      setIsLoadingSba(false);
      return undefined;
    }

    const requestedDocumentId = selectedSbaId;
    const requestedDrugId = routeDrugId;
    const generation = ++sbaDetailGenerationRef.current;
    let cancelled = false;
    setSbaDocDetail(null);
    setSbaDetailError(null);
    setIsLoadingSba(true);

    // Phase 10c: the auto-expand-first-3-sections flow was for the removed
    // accordion view. SBAViewerEmbed renders the whole document from page 1.
    authApi.getSBADocument(requestedDocumentId).then((data: SBADocumentDetail) => {
      if (
        cancelled
        || generation !== sbaDetailGenerationRef.current
        || selectedSbaIdRef.current !== requestedDocumentId
      ) {
        return;
      }

      // A missing field is tolerated only for a rolling-deploy legacy
      // response. An explicit null or another drug ID is not allowed to render
      // beneath this route's drug header.
      // A US_FDA product page is exempt: its reviews come from that product's
      // us_fda_submissions, while sba_documents.drug_id still names the
      // legacy DailyMed attachment (spec D7).
      if ('drug_id' in data && data.drug_id !== requestedDrugId && drug.region !== 'US_FDA') {
        setSbaDocDetail(null);
        setSelectedSbaId(null);
        setSbaHighlightChunkIds([]);
        setSbaChunkAnchors({});
        setSbaPendingPageScroll(null);
        setSbaDetailError('Review not available for this drug');
        setSearchParams((previous) => {
          const next = new URLSearchParams(previous);
          next.delete('source');
          next.delete('version');
          next.delete('sbaChunkIds');
          return next;
        }, { replace: true });
        return;
      }

      setSbaDocDetail(data);
    }).catch(err => {
      if (
        cancelled
        || generation !== sbaDetailGenerationRef.current
        || selectedSbaIdRef.current !== requestedDocumentId
      ) {
        return;
      }
      logger.error('Failed to load SBA document:', err);
      setSbaDocDetail(null);
    }).finally(() => {
      if (!cancelled && generation === sbaDetailGenerationRef.current) {
        setIsLoadingSba(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentDrugId, drug, selectedSbaId, setSearchParams]);

  // Fetch EPAR document detail when selectedEparId changes — mirrors
  // the SBA effect above.  Response shape is identical
  // (epar_routes.get_epar_document was cloned from sba_routes), so
  // ``SBADocumentDetail`` types both.
  useEffect(() => {
    if (selectedEparId) {
      let cancelled = false;
      setIsLoadingEpar(true);
      authApi.getEPARDocument(selectedEparId).then((data: SBADocumentDetail) => {
        if (!cancelled) {
          setEparDocDetail(data);
          setIsLoadingEpar(false);
        }
      }).catch(err => {
        if (!cancelled) {
          logger.error('Failed to load EPAR document:', err);
          setIsLoadingEpar(false);
        }
      });
      return () => { cancelled = true; };
    } else {
      setEparDocDetail(null);
      return undefined;
    }
  }, [selectedEparId]);

  // Fetch FDA letter detail when selectedFdaLetterId changes
  useEffect(() => {
    if (selectedFdaLetterId) {
      let cancelled = false;
      setIsLoadingFdaLetter(true);
      setFdaLetterSectionContent({});
      // Phase 7: reset the bbox-highlight when switching letters so a
      // stale chunk id from the previous letter doesn't pop a highlight
      // on the new letter's viewer.
      setFdaLetterActiveChunkId(null);
      authApi.getFDALetterDetail(selectedFdaLetterId).then((data: FDALetterDetail) => {
        if (!cancelled) {
          setFdaLetterDetail(data);
          setIsLoadingFdaLetter(false);
          // Auto-expand all sections (letters are short, usually 1-3 sections)
          const sectionIds = (data.sections || []).map((s) => `fda-letter-section-${s.id}`);
          setExpandedSections(new Set(sectionIds));
          // Lazy-load all section content
          for (const s of data.sections || []) {
            if (s.content_length > 0) {
              loadFdaLetterSectionContent(s.id);
            }
          }
        }
      }).catch(err => {
        if (!cancelled) {
          logger.error('Failed to load FDA letter detail:', err);
          setIsLoadingFdaLetter(false);
        }
      });
      return () => { cancelled = true; };
    } else {
      setFdaLetterDetail(null);
      setIsLoadingFdaLetter(false);
      return undefined;
    }
  }, [selectedFdaLetterId]);

  const handleCompare = (versionIdA: number, versionIdB: number) => {
    setComparisonVersions({ a: versionIdA, b: versionIdB });
    setShowComparisonModal(true);
  };

  // State to track if we're comparing drug versions (vs label_versions)
  const [isComparingDrugVersions, setIsComparingDrugVersions] = useState(false);

  // State for polymorphic version comparison (3-table architecture)
  const [polymorphicCompareParams, setPolymorphicCompareParams] = useState<{
    sourceA: VersionSource;
    idA: number;
    sourceB: VersionSource;
    idB: number;
  } | null>(null);

  // Polymorphic compare handler for 3-table architecture (Dec 2025)
  // Convention: A (left) = older version, B (right) = newer version
  // Left shows removed text (red), Right shows added text (green)
  const handlePolymorphicCompare = (sourceA: VersionSource, idA: number, sourceB: VersionSource, idB: number) => {
    setPolymorphicCompareParams({ sourceA, idA, sourceB, idB });
    setShowComparisonModal(true);
  };

  /**
   * Time-based polymorphic compare: ensures older version is A (left), newer version is B (right)
   * Uses effective_date for FDA versions, created_at for drafts, falls back to version_number
   */
  const handleTimeBasedPolymorphicCompare = (
    version1: { source: VersionSource; id: number; version_number?: number; effective_date?: string | null | undefined; created_at?: string | null | undefined },
    version2: { source: VersionSource; id: number; version_number?: number; effective_date?: string | null | undefined; created_at?: string | null | undefined }
  ) => {
    // Determine which version is newer using time-based comparison
    const getVersionTime = (v: typeof version1): number => {
      // For FDA versions, use effective_date
      if (v.effective_date) {
        return new Date(v.effective_date).getTime();
      }
      // For drafts, use created_at
      if (v.created_at) {
        return new Date(v.created_at).getTime();
      }
      // Fall back to version_number (higher = newer)
      return v.version_number ?? 0;
    };

    const time1 = getVersionTime(version1);
    const time2 = getVersionTime(version2);

    // Ensure older version is A (left), newer version is B (right)
    // Left = previous (red for removed), Right = current (green for added)
    if (time1 <= time2) {
      // version1 is older or same, put it on left (A)
      handlePolymorphicCompare(version1.source, version1.id, version2.source, version2.id);
    } else {
      // version2 is older, put it on left (A)
      handlePolymorphicCompare(version2.source, version2.id, version1.source, version1.id);
    }
  };


  const [comparisonData, setComparisonData] = useState<any>(null);

  // Auto-expand all sections when viewing a draft version
  useEffect(() => {
    if (versionDetail?.document_sections && versionDetail.document_sections.length > 0) {
      // Expand all draft document sections
      const allSectionIds = versionDetail.document_sections.map((_, i) => `doc-section-${i}`);
      setExpandedSections(new Set(allSectionIds));
      setAllExpanded(true);
    }
  }, [versionDetail]);

  // Handle URL params for deep linking to specific version (draft, historical, or SBA)
  // URL format: /drug/123?source=historical&version=456 OR /drug/123?source=draft&version=789
  //             /drug/123?source=sba&version=2 (SBA document from search results)
  useEffect(() => {
    const sourceParam = searchParams.get('source');
    const versionParam = searchParams.get('version');

    if (versionParam) {
      if (
        sourceParam === 'approval-letter'
        && !/^[1-9]\d*$/.test(versionParam)
      ) {
        setSelectedApprovalLetterId(null);
        return;
      }
      const versionId = parseInt(versionParam, 10);
      if (!isNaN(versionId)) {
        if (sourceParam !== 'approval-letter') {
          // Approval letters are a distinct source from the existing
          // enforcement-letter corpus. Any other deep link must clear
          // the approval viewer before activating its own target.
          setSelectedApprovalLetterId(null);
        }
        if (sourceParam === 'approval-letter' && usFdaVisible) {
          // US-Drugs@FDA mode: the usFda selection sync below owns this
          // source, independent of the retired submission preference.
        } else if (sourceParam === 'approval-letter' && (useSubmissionView || drug?.region === 'US_FDA')) {
          if (versionId !== selectedApprovalLetterId) {
            setApprovalLetterHighlightChunkIds([]);
            setApprovalLetterChunkAnchors({});
            setSelectedApprovalLetterId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
            setSelectedJpPdfId(null);
            setSelectedFdaLetterId(null);
          }
        } else if (sourceParam === 'approval-letter') {
          // The submission-split feature owns this source. With the
          // feature disabled, preserve the legacy page state.
          setSelectedApprovalLetterId(null);
        } else if (sourceParam === 'fda-letter') {
          // FDA Letter document
          if (versionId !== selectedFdaLetterId) {
            setSelectedFdaLetterId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
            setSelectedJpPdfId(null);
          }
        } else if (sourceParam === 'sba') {
          // SBA (FDA Review) document — e.g. from SBA search results
          if (versionId !== selectedSbaId) {
            setSelectedSbaId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedFdaLetterId(null);
            setSelectedEparId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'epar') {
          // EPAR (EMA Review) document
          if (versionId !== selectedEparId) {
            setSelectedEparId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'ukpil') {
          // UK MHRA Patient Information Leaflet (PDF)
          if (versionId !== selectedUkPilId) {
            setSelectedUkPilId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'capm') {
          // CA Health Canada Product Monograph (PDF)
          if (versionId !== selectedCaPmId) {
            setSelectedCaPmId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'audoc') {
          // AU TGA (PI / CMI) document
          if (versionId !== selectedAuId) {
            setSelectedAuId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'eudoc') {
          // EU EMA SmPC document
          if (versionId !== selectedEuId) {
            setSelectedEuId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
          }
        } else if (sourceParam === 'jppdfdoc') {
          // JP PMDA supplementary PDF
          if (versionId !== selectedJpPdfId) {
            setSelectedJpPdfId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'fdalabelpdfdoc') {
          // Drugs@FDA label PDF (admin-only). Clears selectedVersionId too:
          // a US drug's SPL viewer would otherwise win the render branch and
          // the deep link would silently open the wrong document.
          if (versionId !== selectedFdaLabelPdfId) {
            setSelectedFdaLabelPdfId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
          }
        } else if (sourceParam === 'depdfdoc') {
          // BfArM label PDF (admin-only canary).
          if (versionId !== selectedDePdfId) {
            setSelectedDePdfId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
          }
        } else if (sourceParam === 'chpdfdoc') {
          // Swissmedic label PDF (admin-only canary).
          if (versionId !== selectedChPdfId) {
            setSelectedChPdfId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
          }
        } else if (sourceParam === 'zhpdfdoc') {
          // CN NMPA supplementary PDF (admin-only). Mirror of jppdfdoc.
          if (versionId !== selectedZhPdfId) {
            setSelectedZhPdfId(versionId);
            setSelectedVersionId(null);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
            setSelectedUkPilId(null);
            setSelectedCaPmId(null);
            setSelectedAuId(null);
            setSelectedEuId(null);
          }
        } else if (sourceParam === 'historical') {
          // Historical FDA version
          if (versionId !== selectedHistoricalId) {
            setSelectedHistoricalId(versionId);
            setSelectedVersionId(null);
            setSelectedSbaId(null);
            setSelectedEparId(null);
            setSelectedFdaLetterId(null);
          }
        } else if (sourceParam === 'draft' || !sourceParam) {
          // Draft version (or legacy URL without source param)
          if (versionId !== selectedVersionId) {
            setSelectedVersionId(versionId);
            setSelectedHistoricalId(null);
            setSelectedSbaId(null);
            setSelectedFdaLetterId(null);
          }
        }
      }
    } else {
      // No version param - viewing current FDA version
      if (selectedSbaId !== null) {
        setSelectedSbaId(null);
        setSbaDetailError(null);
      }
      // Only clear if we're not already on current
      if (selectedHistoricalId !== null || selectedVersionId !== null) {
        // Don't auto-clear on mount, only when URL explicitly has no params
        // This prevents resetting state during normal navigation
      }
    }
  }, [searchParams]);

  // US-Drugs@FDA mode: the Drugs@FDA document (label PDF / approval letter /
  // review) is derived from the URL on EVERY transition, so it is mutually
  // exclusive and follows browser Back/Forward and same-target re-selection.
  // Re-runs when `usFdaMode` flips true (drug/route metadata settled), so a
  // cold deep link no longer depends on `drug` being loaded at first run.
  useEffect(() => {
    if (!usFdaMode) return;
    const source = searchParams.get('source');
    const raw = searchParams.get('version');
    const id = raw && /^[1-9]\d*$/.test(raw) ? parseInt(raw, 10) : null;
    const next = {
      pdf: source === 'fdalabelpdfdoc' ? id : null,
      letter: source === 'approval-letter' ? id : null,
      review: source === 'sba' ? id : null,
    };
    if (next.letter !== selectedApprovalLetterId) {
      setApprovalLetterHighlightChunkIds([]);
      setApprovalLetterChunkAnchors({});
    }
    setSelectedFdaLabelPdfId(next.pdf);
    setSelectedApprovalLetterId(next.letter);
    setSelectedSbaId(next.review);
    if (next.pdf !== null || next.letter !== null || next.review !== null) {
      setSelectedVersionId(null);
      setSelectedHistoricalId(null);
      setSelectedFdaLetterId(null);
    }
    // `currentDrugId`: the fetch-reset effect clears these selections when the drug
    // id changes (a cross-product navigation), so reconcile again after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, usFdaMode, currentDrugId]);

  // A deep link is valid only after the cycle=all envelope confirms
  // that the completed letter belongs to this drug's NDA/BLA scope.
  // Clearing both state and URL prevents a stale prior document from
  // remaining visible when an unknown letter id is requested.
  useEffect(() => {
    if (
      !reviewLettersLoaded
      || selectedApprovalLetterId === null
      || sbaCycleInfo === null
      || reviewLetters.some(letter => letter.id === selectedApprovalLetterId)
    ) {
      return;
    }
    setSelectedApprovalLetterId(null);
    if (searchParams.get('source') === 'approval-letter') {
      setSearchParams({}, { replace: true });
    }
  }, [
    reviewLetters,
    reviewLettersLoaded,
    searchParams,
    selectedApprovalLetterId,
    setSearchParams,
  ]);

  // Auto-land on the primary per-family PDF viewer **only for regions
  // whose drug label itself is a per-family PDF document**.  This is
  // intentionally narrower than "any region with per-family data":
  //
  //   AU → per-family ``au_documents`` (PI / CMI) IS the label.
  //        PI is the prescribing label, CMI is the consumer leaflet;
  //        bundle priority puts PI first.
  //   CA → per-family ``ca_pm_documents`` (PM) IS the label.
  //        Health Canada's Product Monograph is the authoritative label.
  //
  //   EU → label is the SmPC, which is parsed into legacy ``sections``
  //        (``sections.section_number = '1'..'10'``).  ``epar_documents``
  //        holds the **EPAR review report**, the EMA analogue of FDA's
  //        SBA — supplementary, not primary.  Landing on EPAR would show
  //        the regulator's review when the user expected the label.
  //   UK → label is the UK SmPC, parsed into legacy ``sections``.
  //        ``uk_pil_documents`` holds the patient information leaflet
  //        (the simplified consumer-facing booklet), supplementary too.
  //   US → label is the FDA SPL, parsed into legacy ``sections``.
  //        ``sba_documents`` is the review document, supplementary.
  //
  // So EU / UK / US all correctly default to the legacy ``drug.sections``
  // rendering — that's where their actual label content lives — and the
  // dropdown still surfaces EPAR / PIL / SBA / FDA Letter as
  // supplementary documents the user can click into.
  //
  // Guards:
  //   * Don't override an explicit ``?source=…&version=…`` deep link.
  //   * Don't override an existing selection (any ``selected*Id``).
  //   * Only fire when the relevant per-family list is non-empty.
  useEffect(() => {
    if (!drug?.region) return;
    // Respect explicit deep links.
    if (searchParams.get('source') && searchParams.get('version')) return;
    // Respect any existing user selection.
    if (
      selectedSbaId !== null || selectedEparId !== null
      || selectedUkPilId !== null || selectedCaPmId !== null
      || selectedAuId !== null || selectedEuId !== null
      || selectedFdaLetterId !== null
      || selectedApprovalLetterId !== null
      || selectedHistoricalId !== null || selectedVersionId !== null
    ) return;

    const region = drug.region;
    const primaryAu = auDocuments[0];
    const primaryCaPm = caPmDocuments[0];
    const primaryEu = euDocuments[0];
    if (region === 'AU' && primaryAu) {
      setSelectedAuId(primaryAu.id);
    } else if (region === 'CA' && primaryCaPm) {
      setSelectedCaPmId(primaryCaPm.id);
    } else if (region === 'EU' && primaryEu) {
      // EU → per-family ``eu_documents`` (SmPC) IS the prescribing
      // label, rendered in the regulatory PDF viewer just like AU PI.
      // ``epar_documents`` remains the supplementary review report.
      setSelectedEuId(primaryEu.id);
    }
    // UK / US: no auto-land — their label content IS in legacy
    // ``sections`` (SmPC for UK, FDA SPL for US).  The dropdown
    // still surfaces EPAR / PIL / SBA / FDA Letter docs as
    // supplementary entries the user can switch to manually.
  }, [
    drug?.region,
    auDocuments,
    caPmDocuments,
    euDocuments,
    searchParams,
    selectedSbaId,
    selectedEparId,
    selectedUkPilId,
    selectedCaPmId,
    selectedAuId,
    selectedEuId,
    selectedFdaLetterId,
    selectedApprovalLetterId,
    selectedHistoricalId,
    selectedVersionId,
  ]);

  // Phase 10c: viewer-native SBA deep-link parser.  URL shape
  // ``?source=sba&version=<doc_id>&sbaChunkIds=<id1>,<id2>,...``
  // from SbaDocumentResults click-through.
  // Drops the chunk ids into ``sbaHighlightChunkIds`` state;
  // ``<SBAViewerEmbed>`` picks them up via its prop and
  // auto-fetches + scrolls + draws the bbox overlay (see
  // SBAViewerEmbed.tsx highlightChunkIds effect).  Then
  // strips the sba params from the URL so a refresh doesn't
  // re-fire the highlight.
  useEffect(() => {
    if (searchParams.get('source') !== 'approval-letter') return;
    const raw = searchParams.get('approvalLetterChunkIds');
    if (!raw) return;
    const ids = raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    const documentId = Number(searchParams.get('version'));
    if (selectedApprovalLetterId !== documentId) return;

    setApprovalLetterHighlightChunkIds(ids);
    const pending = approvalLetterPendingAnchorRef.current;
    if (
      pending
      && pending.documentId === documentId
      && ids.includes(pending.chunkId)
      && pending.anchor
    ) {
      setApprovalLetterChunkAnchors({ [pending.chunkId]: [pending.anchor] });
    } else {
      setApprovalLetterChunkAnchors({});
    }
    approvalLetterPendingAnchorRef.current = null;

    dropParam('approvalLetterChunkIds');
  }, [searchParams, selectedApprovalLetterId, dropParam]);

  // The cited SENTENCES, arriving by URL.
  //
  // A citation click opens a NEW tab. ``seedChunkAnchor`` runs on the page that
  // was clicked FROM and writes into ITS state; the new tab mounts a fresh
  // DrugDetailPage whose ``chunkAnchors`` are empty. Chunk ids survive because
  // they ride the URL — which is exactly why the new tab fetched the chunk and
  // scrolled to the right page and then drew nothing. Confirmed on staging
  // 2026-10-05: the box existed, correct and complete, in the ORIGINATING tab.
  //
  // Parsed before the family's chunk-id effect below strips its own param, and
  // ``source`` names the viewer (``audoc`` -> ``au``), which is not the same
  // spelling as ``version_type``.
  useEffect(() => {
    const raw = searchParams.get('citeAnchors');
    if (!raw) return;
    const SOURCE_FAMILY: Record<string, string> = {
      sba: 'sba', epar: 'epar', ukpil: 'uk_pil', capm: 'ca_pm',
      audoc: 'au', eudoc: 'eu', jppdfdoc: 'jp_pdf', zhpdfdoc: 'zh_pdf', chpdfdoc: 'ch_pdf', depdfdoc: 'de_pdf',
      fdalabelpdfdoc: 'fda_label_pdf',
    };
    const family = SOURCE_FAMILY[searchParams.get('source') ?? ''];
    if (family) {
      const entry: Record<number, ChunkAnchor[]> = {};
      try {
        for (const a of JSON.parse(decodeURIComponent(raw)) as unknown[]) {
          // Positional tuple: [chunkId, start, end, text?]. `text` is the
          // whole cited sentence and was appended fourth so that a URL written
          // by an older build — three elements — still parses, and simply
          // leaves the fifth matching pass without a needle. A malformed param
          // must cost the highlight, never the page.
          if (!Array.isArray(a)) continue;
          const [id, start, end, text] = a as [unknown, unknown, unknown, unknown];
          if (typeof id === 'number' && typeof start === 'string' && start) {
            // Appended, not assigned: several spans legitimately share a chunk.
            (entry[id] ??= []).push({
              start,
              end: typeof end === 'string' && end ? end : null,
              text: typeof text === 'string' && text ? text : null,
            });
          }
        }
      } catch {
        // Leave the anchors unset — navigate and scroll, claim no highlight.
      }
      if (Object.keys(entry).length > 0) applyChunkAnchors(family, entry);
    }
    dropParam('citeAnchors');
  }, [searchParams, dropParam, applyChunkAnchors]);

  useEffect(() => {
    if (searchParams.get('source') !== 'sba') return;
    const raw = searchParams.get('sbaChunkIds');
    if (!raw) return;
    const ids = raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;

    setSbaHighlightChunkIds(ids);

    dropParam('sbaChunkIds');
  }, [searchParams, dropParam]);

  // EPAR equivalent of the sbaChunkIds parser above.  URL shape:
  // ``?source=epar&version=<doc_id>&eparChunkIds=<id1>,<id2>,...``
  // Emitted by the keyword-search multi-source highlights so an EPAR
  // hit opens the EPAR viewer at the matched chunk.  Same shape as
  // SBA — separate state because the two viewers don't share refs.
  useEffect(() => {
    if (searchParams.get('source') !== 'epar') return;
    const raw = searchParams.get('eparChunkIds');
    if (!raw) return;
    const ids = raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;

    setEparHighlightChunkIds(ids);

    dropParam('eparChunkIds');
  }, [searchParams, dropParam]);

  // UK PIL / CA PM / AU equivalents of the sbaChunkIds parser above.  URL
  // shapes (emitted by KeywordSearchResults.navigateToHighlight + the Compare
  // drug-name click for the matched non-US corpus):
  //   ?source=ukpil&version=<doc>&ukPilChunkIds=<id,...>
  //   ?source=capm&version=<doc>&caPmChunkIds=<id,...>
  //   ?source=audoc&version=<doc>&auChunkIds=<id,...>   (au_pi / au_cmi)
  // The version→selected<Family>Id switch is handled by the shared source-param
  // effect above; here we only plumb the chunk ids into the family's highlight
  // state so the viewer scrolls + draws the bbox overlay, then strip the param.
  useEffect(() => {
    if (searchParams.get('source') !== 'ukpil') return;
    const raw = searchParams.get('ukPilChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setUkPilHighlightChunkIds(ids);
    dropParam('ukPilChunkIds');
  }, [searchParams, dropParam]);

  useEffect(() => {
    if (searchParams.get('source') !== 'capm') return;
    const raw = searchParams.get('caPmChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setCaPmHighlightChunkIds(ids);
    dropParam('caPmChunkIds');
  }, [searchParams, dropParam]);

  useEffect(() => {
    if (searchParams.get('source') !== 'audoc') return;
    const raw = searchParams.get('auChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setAuHighlightChunkIds(ids);
    dropParam('auChunkIds');
  }, [searchParams, dropParam]);

  // EU equivalent of the audoc chunkIds parser above.  URL shape:
  //   ?source=eudoc&version=<doc>&euChunkIds=<id,...>   (eu smpc)
  useEffect(() => {
    if (searchParams.get('source') !== 'eudoc') return;
    const raw = searchParams.get('euChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setEuHighlightChunkIds(ids);
    dropParam('euChunkIds');
  }, [searchParams, dropParam]);

  // Stale-id redirect for ?source=audoc|eudoc&version=<id>.  /au_docs and
  // /eu_docs return only the CURRENT doc, so a link to a superseded revision's
  // id is never in the list and the viewer would show "Loading…" forever.
  // Once the list has loaded: land on the current doc (same pick as the
  // auto-land effect) and rewrite ``version``; if the family has no docs,
  // clear the selection and the source/version params.  Chunk ids belonged to
  // the old doc, so they are dropped.  Declared after the chunk-id parsers so
  // clearing their highlight state wins within the commit.
  useEffect(() => {
    const redirect = (
      loaded: boolean,
      selectedId: number | null,
      docs: SBASummaryForDrug[],
      setSelected: (id: number | null) => void,
      setChunks: (ids: number[]) => void,
      chunkParam: string,
    ) => {
      if (!loaded || selectedId === null || docs.some((d) => d.id === selectedId)) return;
      setChunks([]);
      const current = docs[0];
      setSelected(current ? current.id : null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(chunkParam);
        if (current) {
          next.set('version', String(current.id));
        } else {
          next.delete('source');
          next.delete('version');
        }
        return next;
      }, { replace: true });
    };
    redirect(auDocsLoaded, selectedAuId, auDocuments, setSelectedAuId, setAuHighlightChunkIds, 'auChunkIds');
    redirect(euDocsLoaded, selectedEuId, euDocuments, setSelectedEuId, setEuHighlightChunkIds, 'euChunkIds');
  }, [auDocsLoaded, euDocsLoaded, selectedAuId, selectedEuId, auDocuments, euDocuments, setSearchParams]);

  // JP PMDA supplementary (jp_pdf) equivalent of the audoc/eudoc chunkIds parser.
  // URL shape:  ?source=jppdfdoc&version=<doc>&jpPdfChunkIds=<id,...>
  // Emitted by regulatoryCitationHref (CEPI citation) + KeywordSearchResults.
  // version→selectedJpPdfId is handled by the shared source-param effect above;
  // here we only plumb the chunk ids into the highlight state so the JP viewer
  // scrolls + draws the bbox overlay, then strip the param.
  useEffect(() => {
    if (searchParams.get('source') !== 'jppdfdoc') return;
    const raw = searchParams.get('jpPdfChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setJpPdfHighlightChunkIds(ids);
    dropParam('jpPdfChunkIds');
  }, [searchParams, dropParam]);

  // CN NMPA supplementary (zh_pdf) equivalent of the jppdfdoc chunkIds parser.
  // URL shape:  ?source=zhpdfdoc&version=<doc>&zhPdfChunkIds=<id,...>
  // version→selectedZhPdfId is handled by the shared source-param effect above;
  // here we only plumb the chunk ids into the highlight state, then strip the
  // param.
  useEffect(() => {
    if (searchParams.get('source') !== 'zhpdfdoc') return;
    const raw = searchParams.get('zhPdfChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setZhPdfHighlightChunkIds(ids);
    dropParam('zhPdfChunkIds');
  }, [searchParams, dropParam]);

  // Swiss (ch_pdf) equivalent: ?source=chpdfdoc&version=<doc>&chPdfChunkIds=<id,...>
  useEffect(() => {
    if (searchParams.get('source') !== 'chpdfdoc') return;
    const raw = searchParams.get('chPdfChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setChPdfHighlightChunkIds(ids);
    dropParam('chPdfChunkIds');
  }, [searchParams, dropParam]);

  // US Drugs@FDA (fda_label_pdf) equivalent:
  // ?source=fdalabelpdfdoc&version=<doc>&fdaLabelPdfChunkIds=<id,...>
  useEffect(() => {
    if (searchParams.get('source') !== 'fdalabelpdfdoc') return;
    const raw = searchParams.get('fdaLabelPdfChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setFdaLabelPdfHighlightChunkIds(ids);
    dropParam('fdaLabelPdfChunkIds');
  }, [searchParams, dropParam]);

  // German (de_pdf) equivalent: ?source=depdfdoc&version=<doc>&dePdfChunkIds=<id,...>
  useEffect(() => {
    if (searchParams.get('source') !== 'depdfdoc') return;
    const raw = searchParams.get('dePdfChunkIds');
    if (!raw) return;
    const ids = raw.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setDePdfHighlightChunkIds(ids);
    dropParam('dePdfChunkIds');
  }, [searchParams, dropParam]);

  // CN main-label default: an NMPA drug's Package Insert (说明书) is its MAIN label
  // (the analogue of JP's default jplabel), so open it automatically instead of a
  // blank viewer. Only when: CN drug + docs loaded + nothing already selected +
  // no deep-link owns the selection (a `?source=` param does). Falls back to the
  // first doc if a package_insert isn't present. GA for all users 2026-08-31 —
  // the former `if (!isAdmin) return;` left non-admins on a blank viewer.
  // CH main-label default: open the Swiss Fachinformation (smpc) automatically,
  // same rules as the CN default below.
  useEffect(() => {
    if (drug?.region !== 'CH') return;
    if (chPdfDocuments.length === 0) return;
    if (namesADocument(searchParams)) return;
    if (selectedChPdfId !== null) return;
    if (selectedVersionId !== null || selectedHistoricalId !== null) return;
    const main = chPdfDocuments.find((d) => d.doc_type === 'smpc') ?? chPdfDocuments[0];
    if (main) setSelectedChPdfId(main.id);
  }, [drug?.region, chPdfDocuments, searchParams, selectedChPdfId,
      selectedVersionId, selectedHistoricalId]);

  // FR: load the ANSM documents, then open the requested one (?source=frhtmldoc)
  // or the RCP (main label).
  useEffect(() => {
    if (drug?.region !== 'FR') return;
    let alive = true;
    authApi.getFrHtmlDocs(drug.id)
      .then((d) => { if (alive) setFrHtmlDocuments(d.documents || []); })
      .catch(() => { if (alive) setFrHtmlDocuments([]); });         // 403 for non-admins
    return () => { alive = false; };
  }, [drug?.region, drug?.id]);
  useEffect(() => {
    if (drug?.region !== 'FR' || frHtmlDocuments.length === 0 || selectedFrHtmlId !== null) return;
    const want = searchParams.get('source') === 'frhtmldoc' ? Number(searchParams.get('version')) : null;
    const doc = frHtmlDocuments.find((d) => d.id === want)
      ?? frHtmlDocuments.find((d) => d.doc_type === 'smpc') ?? frHtmlDocuments[0];
    if (doc) setSelectedFrHtmlId(doc.id);
  }, [drug?.region, frHtmlDocuments, searchParams, selectedFrHtmlId]);

  // DE main-label default: open the BfArM Fachinformation (smpc), same rules as CH.
  useEffect(() => {
    if (!LABEL_PDF_REGIONS.has(drug?.region ?? '')) return;
    if (dePdfDocuments.length === 0) return;
    if (namesADocument(searchParams)) return;
    if (selectedDePdfId !== null) return;
    if (selectedVersionId !== null || selectedHistoricalId !== null) return;
    const main = dePdfDocuments.find((d) => d.doc_type === 'smpc') ?? dePdfDocuments[0];
    if (main) setSelectedDePdfId(main.id);
  }, [drug?.region, dePdfDocuments, searchParams, selectedDePdfId,
      selectedVersionId, selectedHistoricalId]);

  useEffect(() => {
    if (drug?.region !== 'CN') return;
    if (zhPdfDocuments.length === 0) return;
    if (searchParams.get('source')) return;         // a deep-link owns selection
    if (selectedZhPdfId !== null) return;            // already chosen
    if (selectedJpPdfId !== null || selectedEuId !== null ||
        selectedHistoricalId !== null || selectedSbaId !== null ||
        selectedVersionId !== null) return;          // another view is active
    const main = zhPdfDocuments.find((d) => d.doc_type === 'package_insert')
      ?? zhPdfDocuments[0];
    if (main) setSelectedZhPdfId(main.id);
  }, [drug?.region, zhPdfDocuments, searchParams, selectedZhPdfId,
      selectedJpPdfId, selectedEuId, selectedHistoricalId, selectedSbaId,
      selectedVersionId]);

  // Phase 8b: FDA-letter deep-link parser.  URL shape:
  // ``?source=fda-letter&version=<letter_id>&fdaLetterChunkId=<chunk_id>``
  // Emitted by ``KeywordSearchResults.navigateToHighlight`` (Phase 8a) when
  // the user clicks an fda_letter-sourced keyword-search highlight.  The
  // ``version`` → ``selectedFdaLetterId`` switch is already handled by the
  // shared source-param effect above (lines ~1813); here we ONLY plumb the
  // chunk id into ``fdaLetterActiveChunkId`` so ``<FdaLetterPageViewer>``
  // resolves bbox + page_num via ``/chunks/batch`` and scrolls the
  // overlay-tagged page into view.  Strip the param afterwards so a
  // post-scroll refresh doesn't re-fire the highlight (matches the
  // sbaChunkIds / eparChunkIds pattern).
  useEffect(() => {
    if (searchParams.get('source') !== 'fda-letter') return;
    const raw = searchParams.get('fdaLetterChunkId');
    if (!raw) return;
    const chunkId = parseInt(raw, 10);
    if (Number.isNaN(chunkId)) return;

    // Clear-then-set so re-selecting the SAME chunk id triggers the
    // viewer's "id changed" effect (matches the click-path trick in
    // ``handleFdaLetterCitationClick``).
    setFdaLetterActiveChunkId(null);
    setTimeout(() => setFdaLetterActiveChunkId(chunkId), 0);

    dropParam('fdaLetterChunkId');
  }, [searchParams, dropParam]);

  // Scroll to comment after comments are loaded (from URL param)
  useEffect(() => {
    const commentParam = searchParams.get('comment');
    if (commentParam && comments.length > 0 && !isLoadingComments) {
      const commentId = parseInt(commentParam, 10);
      if (!isNaN(commentId)) {
        const comment = comments.find(c => c.id === commentId);
        if (comment) {
          setSelectedCommentId(commentId);
          // Trigger scroll to comment
          handleCommentClick(comment);
          // Drive the ONLYOFFICE viewer to the Word comment (real comments only).
          if (useOnlyOfficeViewer && comment.doc_comment_id && !comment.doc_comment_id.startsWith('tc_')) {
            viewerHandleRef.current?.scrollToComment(comment.doc_comment_id);
          }
          // Clear URL params after applying them
          setSearchParams({}, { replace: true });
        }
      }
    }
  }, [comments, isLoadingComments, searchParams]);

  // Handle URL params for search result deep linking (section scroll + highlight)
  // URL format: /drug/123?section=5&highlight=hepatic%20impairment&sections=5,14.1,6.1
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    const highlightParam = searchParams.get('highlight');
    const sectionsParam = searchParams.get('sections');

    // Only process if we have search-related params (not version/comment params)
    if (!sectionParam && !highlightParam && !sectionsParam) {
      return undefined;
    }
    // A Japanese-text keyword hit is handled by the JP viewer effect below
    // (the US accordion scroll and the ≥3-letter English term filter would
    // drop it: ``肺炎`` is 2 characters).
    if (searchParams.get('source') === 'jpja') {
      return undefined;
    }

    // Read the matched evidence chunk text handed off by SearchPage. Stored
    // in localStorage (not sessionStorage) so it survives a window.open new
    // tab; takeEvidenceChunk reads-and-removes it so it never re-highlights.
    // urlId, not currentDrugId: on an in-tab drug change currentDrugId still
    // names the previous drug until its sync effect runs.
    if (urlId) {
      const storedChunk = takeEvidenceChunk(urlId);
      if (storedChunk) {
        setEvidenceChunkText(storedChunk);
      }
    }

    // Store section number for scrolling after data loads
    if (sectionParam) {
      setTargetSectionNumber(sectionParam);
    }

    // Parse multi-section highlighting (V2 search evidence sections)
    if (sectionsParam) {
      const sections = sectionsParam.split(',').filter(s => s.trim());
      setHighlightedSections(new Set(sections));
    }
  }, [searchParams, urlId]);

  // A new non-empty `highlight` resets the terms and the Find text. A URL
  // without one (every version switch rewrites the params) changes nothing.
  const urlHighlight = searchParams.get('highlight') ?? '';
  const urlJpja = searchParams.get('source') === 'jpja';
  const urlPhrase = searchParams.get('hlmode') === 'phrase';
  const urlExact = searchParams.get('kwExact') === '1';
  useEffect(() => {
    if (!urlHighlight) return;
    if (deepLink && deepLink.highlight === urlHighlight && deepLink.jpja === urlJpja && deepLink.phrase === urlPhrase && deepLink.englishExact === urlExact) return;
    setDeepLink({ highlight: urlHighlight, jpja: urlJpja, phrase: urlPhrase, englishExact: urlExact });
    setFindText(urlHighlight);
    setCommittedText(urlHighlight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlHighlight, urlJpja, urlPhrase, urlExact, urlId]);

  useEffect(() => {
    const t = setTimeout(() => {
      pendingMoveRef.current = null;
      setCommittedText(findText);
    }, 300);
    return () => clearTimeout(t);
  }, [findText]);

  // Pending deep-link scroll timers. They close over the drug that scheduled
  // them (setSearchParams would resolve against its old path), so they must die
  // on drug change/unmount. Not cleared by the section effect's own cleanup: its
  // 150 ms callback re-runs that effect via setTargetSectionNumber(null).
  const sectionScrollTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => {
    sectionScrollTimersRef.current.forEach(clearTimeout);
    sectionScrollTimersRef.current = [];
  }, [urlId]);

  // Scroll to target section after drug data loads and sections are rendered
  useEffect(() => {
    if (targetSectionNumber && drugForId === urlId && drug?.sections && drug.sections.length > 0) {
      // Find the section that matches the target section number
      // Prefer exact match with content, then first subsection with content
      const exactMatch = drug.sections.find(s => s.section_number === targetSectionNumber);
      const firstSubsection = drug.sections.find(
        s => s.section_number?.startsWith(targetSectionNumber + '.') && s.content_text?.trim()
      );
      // Use exact match if it has content, otherwise fall back to first subsection
      const targetSection = (exactMatch?.content_text?.trim() ? exactMatch : firstSubsection) || exactMatch;

      if (targetSection) {
        const sectionId = `section-${targetSection.id}`;

        // If target is a subsection, ensure its parent is not collapsed
        if (isSubsectionCheck(targetSection)) {
          const parentNum = getParentSectionNum(targetSection);
          setCollapsedParents(prev => {
            if (prev.has(parentNum)) {
              const newSet = new Set(prev);
              newSet.delete(parentNum);
              return newSet;
            }
            return prev;
          });
        }

        // Expand the target section
        setExpandedSections(prev => new Set([...prev, sectionId]));
        revealRow(sectionId);

        // Scroll to section after a brief delay for DOM update
        sectionScrollTimersRef.current.push(setTimeout(() => {
          const sectionElement = sectionRefs.current.get(sectionId);
          if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }

          // After section scroll + content renders, scroll to evidence chunk or first keyword match
          if (evidenceChunkText || highlightTerms.length > 0) {
            sectionScrollTimersRef.current.push(setTimeout(() => {
              // Prefer scrolling to evidence chunk highlight over keyword match
              const chunkEl = sectionElement?.querySelector('.evidence-chunk-highlight');
              const keywordEl = sectionElement?.querySelector('mark.search-highlight');
              const scrollTarget = chunkEl || keywordEl;
              if (scrollTarget) {
                scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 400));
          }

          // Clear target section after scrolling
          setTargetSectionNumber(null);

          // Clear section param from URL but keep highlight for a bit
          const currentParams = new URLSearchParams(searchParams);
          currentParams.delete('section');
          if (currentParams.toString()) {
            setSearchParams(currentParams, { replace: true });
          } else {
            setSearchParams({}, { replace: true });
          }
        }, 150));
      } else {
        // Section not found - clear target
        setTargetSectionNumber(null);
      }
    }
  }, [targetSectionNumber, drug?.sections, drugForId, urlId, revealRow]);

  const handleVersionChange = (versionId: number | null) => {
    setSelectedVersionId(versionId);
    setSelectedApprovalLetterId(null);
    setSelectedCommentId(null);
    // Clear historical selection when switching to draft or FDA current
    if (versionId !== null) {
      setSelectedHistoricalId(null);
    }
  };

  // Handler for unified version selection (3-table architecture)
  // Updates both React state AND URL query params for shareability and refresh-safety
  const handleUnifiedVersionChange = (source: VersionSource, id: number) => {
    // Common reset: clear ALL non-target selections.  Adding UK PIL
    // here means every branch now has to also clear selectedUkPilId
    // — otherwise switching from UK PIL → SBA would leave both
    // selected and the isViewing* guards would collapse.
    const clearAll = () => {
      setSelectedVersionId(null);
      setSelectedHistoricalId(null);
      setSelectedSbaId(null);
      setSelectedEparId(null);
      setSelectedUkPilId(null);
      setSelectedCaPmId(null);
      setSelectedAuId(null);
      setSelectedEuId(null);
      setSelectedJpPdfId(null);
      setSelectedZhPdfId(null);
      setSelectedChPdfId(null);
      setSelectedDePdfId(null);
      setSelectedFdaLabelPdfId(null);
      setSelectedFrHtmlId(null);
      setSelectedFdaLetterId(null);
      setSelectedApprovalLetterId(null);
      approvalLetterPendingAnchorRef.current = null;
      setApprovalLetterHighlightChunkIds([]);
      setApprovalLetterChunkAnchors({});
      setSbaDetailError(null);
    };

    if (source === 'fda_current') {
      // Viewing current FDA label - clear all version params from URL
      clearAll();
      setSearchParams({}, { replace: true });
    } else if (source === 'fda_historical') {
      // Viewing historical FDA version - update URL with source and historical ID
      clearAll();
      setSelectedHistoricalId(id);
      setSearchParams({ source: 'historical', version: id.toString() }, { replace: true });
    } else if (source === 'draft') {
      // Viewing draft - update URL with draft version ID
      clearAll();
      setSelectedVersionId(id);
      setSearchParams({ source: 'draft', version: id.toString() }, { replace: true });
    } else if (source === 'sba') {
      // Viewing SBA (FDA Review) document
      clearAll();
      setSelectedSbaId(id);
      setSearchParams({ source: 'sba', version: id.toString() }, { replace: true });
    } else if (source === 'epar') {
      // Viewing EPAR (EMA Review) document
      clearAll();
      setSelectedEparId(id);
      setSearchParams({ source: 'epar', version: id.toString() }, { replace: true });
    } else if (source === 'ukpil') {
      // Viewing UK MHRA Patient Information Leaflet PDF
      clearAll();
      setSelectedUkPilId(id);
      setSearchParams({ source: 'ukpil', version: id.toString() }, { replace: true });
    } else if (source === 'capm') {
      // Viewing CA Health Canada Product Monograph PDF
      clearAll();
      setSelectedCaPmId(id);
      setSearchParams({ source: 'capm', version: id.toString() }, { replace: true });
    } else if (source === 'audoc') {
      // Viewing AU TGA (PI / CMI) document
      clearAll();
      setSelectedAuId(id);
      setSearchParams({ source: 'audoc', version: id.toString() }, { replace: true });
    } else if (source === 'eudoc') {
      // Viewing EU EMA SmPC document
      clearAll();
      setSelectedEuId(id);
      setSearchParams({ source: 'eudoc', version: id.toString() }, { replace: true });
    } else if (source === 'jppdfdoc') {
      // Viewing JP PMDA supplementary PDF
      clearAll();
      setSelectedJpPdfId(id);
      setSearchParams({ source: 'jppdfdoc', version: id.toString() }, { replace: true });
    } else if (source === 'frhtmldoc') {
      // Viewing an ANSM label document (RCP / Notice)
      clearAll();
      setSelectedFrHtmlId(id);
      setSearchParams({ source: 'frhtmldoc', version: id.toString() }, { replace: true });
    } else if (source === 'fdalabelpdfdoc') {
      // Viewing a Drugs@FDA label PDF (Prescribing Information)
      clearAll();
      setSelectedFdaLabelPdfId(id);
      setSearchParams({ source: 'fdalabelpdfdoc', version: id.toString() }, { replace: true });
    } else if (source === 'depdfdoc') {
      // Viewing a BfArM label PDF
      clearAll();
      setSelectedDePdfId(id);
      setSearchParams({ source: 'depdfdoc', version: id.toString() }, { replace: true });
    } else if (source === 'chpdfdoc') {
      // Viewing a Swissmedic label PDF
      clearAll();
      setSelectedChPdfId(id);
      setSearchParams({ source: 'chpdfdoc', version: id.toString() }, { replace: true });
    } else if (source === 'zhpdfdoc') {
      // Viewing CN NMPA supplementary PDF (admin-only)
      clearAll();
      setSelectedZhPdfId(id);
      setSearchParams({ source: 'zhpdfdoc', version: id.toString() }, { replace: true });
    } else if (source === 'jplabel') {
      // Back to the JP XML package-insert label
      clearAll();
      setSearchParams({}, { replace: true });
    } else if (source === 'fda-letter') {
      // Viewing FDA Letter
      clearAll();
      setSelectedFdaLetterId(id);
      setSearchParams({ source: 'fda-letter', version: id.toString() }, { replace: true });
    } else if (source === 'approval-letter' && sbaCycleInfo !== null) {
      // FDA approval letter from the submission-linked cycle envelope.
      clearAll();
      setSelectedApprovalLetterId(id);
      setSearchParams({ source: 'approval-letter', version: id.toString() }, { replace: true });
    }
    setSelectedCommentId(null);
  };

  const handleUploadComplete = (response: UploadDraftResponse) => {
    setShowUploadModal(false);
    if (drug?.set_id) {
      fetchVersionHistory(drug.set_id);
    }
    // Refresh unified versions data so comparison panel updates
    if (drug?.id) {
      fetchAllVersions(drug.id);
    }
    setSelectedApprovalLetterId(null);
    setSelectedVersionId(response.version_id);
  };

  const handleCommentClick = useCallback((comment: CommentResponse) => {
    setSelectedCommentId(comment.id);

    // For draft versions, ensure all sections are expanded (they should already be)
    // For FDA labels, try to expand the section containing this comment
    if (versionDetail?.document_sections) {
      // Draft version - all sections should already be expanded
      // Find which section contains the context text
      const sectionIndex = versionDetail.document_sections.findIndex(
        section => comment.context_text && section.content.includes(comment.context_text)
      );
      if (sectionIndex >= 0) {
        const sectionId = `doc-section-${sectionIndex}`;
        setExpandedSections(prev => new Set([...prev, sectionId]));
      }
    } else if (comment.context_section_type) {
      // FDA label - expand by section type
      const sectionId = `section-${comment.context_section_type}`;
      setExpandedSections(prev => new Set([...prev, sectionId]));
    }

    // Scroll to the highlight mark for this comment.
    // Uses retry polling because a collapsed section may need time to expand + re-render.
    let attempts = 0;
    const maxAttempts = 5;
    const poll = () => {
      const el = findInTab(rootRef, `comment-highlight-${comment.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 100);
        return;
      }
      // Final fallback: TreeWalker text search
      if (comment.context_text && contentRef.current) {
        const textToFind = comment.context_text.slice(0, 100);
        const walker = document.createTreeWalker(
          contentRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );
        let node;
        while ((node = walker.nextNode())) {
          if (node.textContent?.includes(textToFind)) {
            const element = node.parentElement;
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              break;
            }
          }
        }
      }
    };
    setTimeout(poll, 50);

    // Drive the ONLYOFFICE viewer to the Word comment (real comments only).
    if (useOnlyOfficeViewer && comment.doc_comment_id && !comment.doc_comment_id.startsWith('tc_')) {
      viewerHandleRef.current?.scrollToComment(comment.doc_comment_id);
    }
  }, [versionDetail]);

  const toggleSection = (sectionId: string) => {
    const willExpand = !expandedSections.has(sectionId);
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });

    // Phase 10c: SBA section expansion no longer triggers a
    // per-section content fetch — ``<SBAViewerEmbed>`` owns
    // SBA rendering and fetches page text-spans on scroll.

    // Lazy-load FDA letter section content on first expand
    if (willExpand && sectionId.startsWith('fda-letter-section-')) {
      const numericId = parseInt(sectionId.replace('fda-letter-section-', ''), 10);
      if (!isNaN(numericId) && !fdaLetterSectionContent[numericId] && !loadingFdaLetterSections.has(numericId)) {
        loadFdaLetterSectionContent(numericId);
      }
    }
  };

  // Phase 10c: ``loadSbaSectionContent`` was the per-section
  // lazy-loader feeding the removed accordion view.  The
  // viewer's own page-text-span loader replaces it.

  const loadFdaLetterSectionContent = async (sectionId: number) => {
    setLoadingFdaLetterSections(prev => new Set(prev).add(sectionId));
    try {
      const data = await authApi.getFDALetterSection(sectionId);
      setFdaLetterSectionContent(prev => ({
        ...prev,
        [sectionId]: { content_html: data.content_html ?? undefined, content_text: data.content_text ?? undefined },
      }));
    } catch (err) {
      logger.error('Failed to load FDA letter section content:', err);
    } finally {
      setLoadingFdaLetterSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionId);
        return newSet;
      });
    }
  };

  const toggleAllSections = () => {
    // JP accordion lives inside JPSectionView with its own state — drive
    // it via events rather than the US expandedSections set.
    if (drug?.region === 'JP') {
      window.dispatchEvent(new CustomEvent(
        allExpanded ? 'jp-collapse-all' : 'jp-expand-all',
      ));
      setAllExpanded(!allExpanded);
      return;
    }
    if (allExpanded) {
      setExpandedSections(new Set());
      setAllExpanded(false);
    } else {
      const allIds = getTocSections().map(s => s.id);
      setExpandedSections(new Set(allIds));
      setAllExpanded(true);

      // Phase 10c: SBA "expand all" lazy-fetch chain is gone —
      // ``<SBAViewerEmbed>`` renders pages on demand via its
      // own IntersectionObserver loader.
    }
  };

  // Track collapsed parent sections (for hiding their children)
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());

  const toggleParentCollapse = (parentNum: string) => {
    setCollapsedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentNum)) {
        newSet.delete(parentNum);
      } else {
        newSet.add(parentNum);
      }
      return newSet;
    });
  };

  // Get sections for table of contents
  const getTocSections = (): TocSection[] => {
    // JP main label is structured XML rendered by JPSectionView.  The TOC
    // shows the FULL canonical PMDA template (jpTemplate): present
    // sections are clickable + carry their present subsections; absent
    // sections are greyed/non-clickable so numbering gaps read as "not in
    // this label" rather than "missing". Body only renders present ones.
    if (drug?.region === 'JP') {
      const out: TocSection[] = [];
      for (const t of jpTemplate) {
        out.push({
          id: `jp-toc-${t.section_number}`,
          title: `§${t.section_number}  ${t.title_en}`,
          level: 1,
          sectionNumber: t.section_number,
          jp: true,
          absent: !t.present,
        });
        if (t.present) {
          // present subsections of this section (e.g. §9.1–§9.6)
          for (const s of jpSections) {
            if (s.section_number.startsWith(t.section_number + '.')) {
              out.push({
                id: `jp-toc-${s.section_number}`,
                title: `§${s.section_number}  ${s.title}`,
                level: 2,
                sectionNumber: s.section_number,
                jp: true,
              });
            }
          }
        }
      }
      return out;
    }

    // If viewing SBA (FDA Review), emit TocSection entries that
    // carry ``sectionNumber`` so the TOC click handler routes
    // through the viewer's ``scrollToSection`` rather than the
    // dead ``sba-section-${id}`` DOM-ref path (Phase 10c removed
    // the accordion that those refs used to anchor).  ``id``
    // stays unique for React keying + expandedSections Set
    // membership; it is NOT used for DOM lookup anymore.
    if (isViewingSba && sbaDocDetail?.sections) {
      return sbaDocDetail.sections.map((s) => sbaTocEntry(s));
    }

    // If viewing historical version, use historical data
    if (selectedHistoricalId && historicalVersionData?.sections) {
      const allSections = [...historicalVersionData.sections]
        .filter((s: any) => s.section_number || s.content_text?.trim())
        .sort((a: any, b: any) => {
          // HIGHLIGHTS section goes after BW, before section 1
          const aHighlights = isHighlightsSection(a);
          const bHighlights = isHighlightsSection(b);
          if (aHighlights && !bHighlights) return b.section_number === 'BW' ? 1 : -1;
          if (!aHighlights && bHighlights) return a.section_number === 'BW' ? -1 : 1;
          // Sections without section_number go to the end
          if (!a.section_number && b.section_number) return 1;
          if (a.section_number && !b.section_number) return -1;
          if (!a.section_number && !b.section_number) return (a.title || '').localeCompare(b.title || '');
          const cmp = compareSectionNumbers(a.section_number, b.section_number);
          if (cmp !== 0) return cmp;
          return (a.title || '').localeCompare(b.title || '');
        });

      return allSections.map((s: any) => ({
        id: `section-${s.id}`,
        title: s.title || SECTION_NUMBER_NAMES[s.section_number || ''] || `Section ${s.section_number || 'Unlabeled'}`,
        level: isSubsectionCheck(s) ? 2 : 1,
      }));
    }

    // If we have a draft version with document_sections, use those
    if (versionDetail?.document_sections && versionDetail.document_sections.length > 0) {
      return versionDetail.document_sections.map((s, i) => ({
        id: `doc-section-${i}`,
        title: s.title || `Section ${i + 1}`,
        level: s.level || 1,
      }));
    }

    // Otherwise use the drug's FDA sections
    if (drug?.sections) {
      // Include sections with section_number OR content (like DISPLAY PANEL sections)
      const allSections = [...drug.sections]
        .filter((s) => s.section_number || s.content_text?.trim())
        .sort((a, b) => {
          // HIGHLIGHTS section goes after BW, before section 1
          const aHighlights = isHighlightsSection(a);
          const bHighlights = isHighlightsSection(b);
          if (aHighlights && !bHighlights) return b.section_number === 'BW' ? 1 : -1;
          if (!aHighlights && bHighlights) return a.section_number === 'BW' ? -1 : 1;
          // Sections without section_number go to the end
          if (!a.section_number && b.section_number) return 1;
          if (a.section_number && !b.section_number) return -1;
          if (!a.section_number && !b.section_number) return (a.title || '').localeCompare(b.title || '');
          const cmp = compareSectionNumbers(a.section_number, b.section_number);
          if (cmp !== 0) return cmp;
          return (a.title || '').localeCompare(b.title || '');
        });

      return allSections.map((s) => ({
        id: `section-${s.id}`,
        title: s.title || SECTION_NUMBER_NAMES[s.section_number || ''] || `Section ${s.section_number || 'Unlabeled'}`,
        level: isSubsectionCheck(s) ? 2 : 1,
      }));
    }

    return [];
  };

  // Get display sections (for main content area)
  // Supports current drug, draft version, and historical version
  const getDisplaySections = () => {
    // If viewing historical version, use historical data
    if (selectedHistoricalId && historicalVersionData?.sections) {
      return [...historicalVersionData.sections]
        .filter((s: any) => s.section_number || s.content_text?.trim())
        .sort((a: any, b: any) => {
          // HIGHLIGHTS section goes after BW, before section 1
          const aHighlights = isHighlightsSection(a);
          const bHighlights = isHighlightsSection(b);
          if (aHighlights && !bHighlights) return b.section_number === 'BW' ? 1 : -1;
          if (!aHighlights && bHighlights) return a.section_number === 'BW' ? -1 : 1;
          // Sections without section_number go to the end
          if (!a.section_number && b.section_number) return 1;
          if (a.section_number && !b.section_number) return -1;
          if (!a.section_number && !b.section_number) return (a.title || '').localeCompare(b.title || '');
          const cmp = compareSectionNumbers(a.section_number, b.section_number);
          if (cmp !== 0) return cmp;
          return (a.title || '').localeCompare(b.title || '');
        });
    }

    if (!drug?.sections) return [];

    // Include sections with section_number OR content (like DISPLAY PANEL sections)
    return [...drug.sections]
      .filter((s) => s.section_number || s.content_text?.trim())
      .sort((a, b) => {
        // HIGHLIGHTS section goes after BW, before section 1
        const aHighlights = isHighlightsSection(a);
        const bHighlights = isHighlightsSection(b);
        if (aHighlights && !bHighlights) return b.section_number === 'BW' ? 1 : -1;
        if (!aHighlights && bHighlights) return a.section_number === 'BW' ? -1 : 1;
        // Sections without section_number go to the end
        if (!a.section_number && b.section_number) return 1;
        if (a.section_number && !b.section_number) return -1;
        if (!a.section_number && !b.section_number) return (a.title || '').localeCompare(b.title || '');
        const cmp = compareSectionNumbers(a.section_number, b.section_number);
        if (cmp !== 0) return cmp;
        return (a.title || '').localeCompare(b.title || '');
      });
  };

  // Highlight search terms from query in the content (for deep linking)
  const highlightSearchTerms = (content: string): React.ReactNode =>
    highlightPlainText(content, highlightTerms, !!deepLink?.englishExact);

  // Combine both highlighting systems (comments + search terms)
  const highlightContent = (content: string): React.ReactNode => {
    // First apply comment highlighting
    const withCommentHighlights = highlightTextInContent(content);

    // If no search term highlights needed, return as-is
    if (highlightTerms.length === 0) {
      return withCommentHighlights;
    }

    // If content was modified by comment highlighting (is ReactNode array),
    // we need to apply search highlighting to text portions
    if (typeof withCommentHighlights !== 'string') {
      // For complex nodes, just apply search highlighting to the original content
      // This is a simplification - ideally we'd recurse through nodes
      return highlightSearchTerms(content);
    }

    return highlightSearchTerms(content);
  };

  // Highlight evidence chunk in plain text with distinct left-border styling
  const highlightEvidenceInPlainText = (
    content: string,
    chunkText: string,
    contentHighlighter: (text: string) => React.ReactNode
  ): React.ReactNode => {
    // Normalize for fuzzy matching
    const normalizedContent = content.replace(/\s+/g, ' ').toLowerCase();
    const normalizedChunk = chunkText.replace(/\s+/g, ' ').toLowerCase().trim();

    // Try to find the chunk start using the first ~60 chars
    const searchStr = normalizedChunk.slice(0, 60);
    const matchIdx = normalizedContent.indexOf(searchStr);

    if (matchIdx === -1) {
      // Fallback: no match found, just highlight normally
      return contentHighlighter(content);
    }

    // Map back to original content: find the nearest position
    // Count chars in normalized vs original to map index back
    let origIdx = 0;
    let normIdx = 0;
    while (normIdx < matchIdx && origIdx < content.length) {
      const ch = content.charAt(origIdx);
      if (/\s/.test(ch)) {
        // In normalized, consecutive whitespace becomes single space
        while (origIdx < content.length && /\s/.test(content.charAt(origIdx))) origIdx++;
        normIdx++; // single space in normalized
      } else {
        origIdx++;
        normIdx++;
      }
    }
    const startIdx = origIdx;

    // Find end position similarly using chunk length
    let chunkNormLen = 0;
    let endOrigIdx = startIdx;
    while (chunkNormLen < normalizedChunk.length && endOrigIdx < content.length) {
      const ch = content.charAt(endOrigIdx);
      if (/\s/.test(ch)) {
        while (endOrigIdx < content.length && /\s/.test(content.charAt(endOrigIdx))) endOrigIdx++;
        chunkNormLen++;
      } else {
        endOrigIdx++;
        chunkNormLen++;
      }
    }

    const before = content.slice(0, startIdx);
    const chunk = content.slice(startIdx, endOrigIdx);
    const after = content.slice(endOrigIdx);

    return (
      <>
        {before && contentHighlighter(before)}
        <div className="evidence-chunk-highlight my-1" data-evidence-chunk="true">
          {contentHighlighter(chunk)}
        </div>
        {after && contentHighlighter(after)}
      </>
    );
  };

  // Highlight all comment contexts in the content
  const highlightTextInContent = (content: string): React.ReactNode => {
    if (comments.length === 0) return content;

    // Find all matches and their positions
    // Skip comments with deleted context (won't be in visible document)
    const matches: Array<{ start: number; end: number; comment: CommentResponse }> = [];

    for (const comment of comments) {
      if (comment.context_text && !comment.context_is_deleted) {
        const index = content.indexOf(comment.context_text);
        if (index !== -1) {
          matches.push({
            start: index,
            end: index + comment.context_text.length,
            comment
          });
        }
      }
    }

    if (matches.length === 0) return content;

    // Sort by start position
    matches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep the first one)
    const nonOverlapping: typeof matches = [];
    for (const match of matches) {
      const lastMatch = nonOverlapping[nonOverlapping.length - 1];
      if (!lastMatch || match.start >= lastMatch.end) {
        nonOverlapping.push(match);
      }
    }

    // Build the result with highlights
    const result: React.ReactNode[] = [];
    let lastEnd = 0;

    for (const match of nonOverlapping) {
      // Add text before this match
      if (match.start > lastEnd) {
        result.push(content.slice(lastEnd, match.start));
      }

      // Add the highlighted match
      const isSelected = selectedCommentId === match.comment.id;
      result.push(
        <mark
          key={`highlight-${match.comment.id}`}
          id={`comment-highlight-${match.comment.id}`}
          className={`px-1 py-0.5 rounded cursor-pointer transition-all ${
            isSelected
              ? 'bg-yellow-300 ring-2 ring-yellow-500 ring-offset-1'
              : 'bg-yellow-100 hover:bg-yellow-200'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedCommentId(match.comment.id);
          }}
          title={`Comment by ${match.comment.author || 'Unknown'}: ${match.comment.comment_text.slice(0, 100)}...`}
        >
          {content.slice(match.start, match.end)}
        </mark>
      );

      lastEnd = match.end;
    }

    // Add remaining text
    if (lastEnd < content.length) {
      result.push(content.slice(lastEnd));
    }

    return <>{result}</>;
  };

  // Chat context for the shell panel: the same four-way choice the sidebar
  // mounts made. Null while the drug is still loading.
  const publishedChat: PublishedContext | null = !drug ? null
    : isViewingApprovalLetter ? {
      context: {
        mode: 'regulatory_document',
        regulatory_document: {
          family: 'approval_letter',
          document_id: selectedApprovalLetterId!,
          drug_id: drug.id,
        },
      },
      exportTitle: `CEPI - ${drug.brand_name || drug.generic_name || 'FDA Approval Letter'}`,
      exportDrugNames: [drug.brand_name || drug.generic_name || 'FDA Approval Letter'],
      handlers: { onCitationClick: handleRegulatoryCitationClick },
    }
    : (isViewingSba || isViewingEpar || isViewingUkPil || isViewingCaPm || isViewingAu || isViewingEu || isViewingJpPdf) ? (() => {
      // Family is the runtime discriminator; the backend dispatches via a
      // Family lookup, so this never grows when a regulator is added.
      const regulatoryFamily: 'sba' | 'epar' | 'uk_pil' | 'ca_pm' | 'au' | 'eu' | 'jp_pdf' =
        isViewingSba
          ? 'sba'
          : isViewingEpar
            ? 'epar'
            : isViewingUkPil
              ? 'uk_pil'
              : isViewingCaPm
                ? 'ca_pm'
                : isViewingEu
                  ? 'eu'
                  : isViewingJpPdf
                    ? 'jp_pdf'
                    : 'au';
      const regulatoryDocId = isViewingSba
        ? selectedSbaId || 0
        : isViewingEpar
          ? selectedEparId || 0
          : isViewingUkPil
            ? selectedUkPilId || 0
            : isViewingCaPm
              ? selectedCaPmId || 0
              : isViewingEu
                ? selectedEuId || 0
                : isViewingJpPdf
                  ? selectedJpPdfId || 0
                  : selectedAuId || 0;
      const ukPilDoc = ukPilDocuments.find(d => d.id === selectedUkPilId);
      const caPmDoc = caPmDocuments.find(d => d.id === selectedCaPmId);
      const auDoc = auDocuments.find(d => d.id === selectedAuId);
      const euDoc = euDocuments.find(d => d.id === selectedEuId);
      const jpPdfDoc = jpPdfDocuments.find(d => d.id === selectedJpPdfId);
      const regulatoryTradeName = isViewingSba
        ? sbaDocDetail?.trade_name || 'FDA Review'
        : isViewingEpar
          ? eparDocDetail?.trade_name || 'EMA EPAR'
          : isViewingUkPil
            ? ukPilDoc?.trade_name || 'UK PIL'
            : isViewingCaPm
              ? caPmDoc?.trade_name || 'Product Monograph'
              : isViewingEu
                ? euDoc?.trade_name || 'EU SmPC'
                : isViewingJpPdf
                  ? jpPdfDoc?.trade_name || 'PMDA Supplementary'
                  : auDoc?.trade_name || 'Australia TGA';
      return {
        context: {
          mode: 'regulatory_document' as const,
          regulatory_document: {
            family: regulatoryFamily,
            document_id: regulatoryDocId,
            drug_id: drug.id,
          },
        },
        exportTitle: `CEPI - ${regulatoryTradeName}`,
        exportDrugNames: [regulatoryTradeName],
        // Section-link clicks (no chunk_id, no section_id): without this
        // handler MessageBubble's onSectionLinkClick is undefined for the
        // regulatory panel and the click is a silent no-op.
        handlers: {
          onCitationClick: handleRegulatoryCitationClick,
          onSectionLinkClick: handleSectionLinkClick,
        },
      };
    })()
    : isViewingFdaLetter ? {
      context: {
        mode: 'fda_letter',
        fda_letter: { letter_id: selectedFdaLetterId! },
      },
      exportTitle: `CEPI - ${fdaLetterDetail?.product_name || 'FDA Enforcement Letter'}`,
      exportDrugNames: [fdaLetterDetail?.product_name || 'FDA Enforcement Letter'],
      handlers: { onCitationClick: handleFdaLetterCitationClick },
    }
    : {
      context: {
        mode: 'drug_qa',
        drug_qa: {
          drug_id: drugIdForQA,
          set_id: drug.set_id || '',
          brand_name: drug.brand_name || drug.generic_name || 'Unknown Drug',
          generic_name: drug.generic_name || undefined,
        },
      },
      originEligible: originToggleEligible,
      exportTitle: `CEPI - ${drug.brand_name || drug.generic_name || 'Drug'}`,
      exportDrugNames: [drug.brand_name || drug.generic_name || 'Drug'],
      handlers: {
        onCitationClick: handleCitationClick,
        onSectionLinkClick: handleSectionLinkClick,
        onChunkClick: handleChunkClick,
      },
    };
  usePublishChatContext(publishedChat);

  // Name this tab after the drug once loaded. Only for the drug the URL names
  // (after an in-tab navigation `drug` is still the previous one until the
  // fetch lands); idempotent on the tab's current title, so it also re-applies
  // if a navigation reset it.
  // ``tabId`` comes from the single ``useTab()`` destructure at the top of the
  // component — it is also what scopes the CSS highlight registry.
  const tabTitle = shell?.tabs.find((t) => t.id === tabId)?.title;
  const drugTabName = drug && String(drug.id) === urlId ? drug.brand_name || drug.generic_name : null;
  useEffect(() => {
    if (shell && drugTabName && tabTitle !== undefined && tabTitle !== drugTabName) {
      shell.setTabTitle(tabId, drugTabName);
    }
  }, [shell, tabId, tabTitle, drugTabName]);

  const isDraftVersion = versionDetail?.document_content != null;

  // Full-label (A5) view: true only where the article ladder below ends in the
  // FDA/JP section render. MUST track that ladder's branch order. CN/CH are
  // out of scope by region (with or without a PDF) and keep the old accordion.
  const auCaLoading = (drug?.region === 'AU' || drug?.region === 'CA') && !isViewingSba && !isViewingEpar
    && !isViewingUkPil && !isViewingCaPm && !isViewingAu && !isViewingEu && !isViewingFdaLetter
    && selectedVersionId === null && selectedHistoricalId === null;
  const labelView: 'sections' | 'jp' | null =
    euAutolandPending ? null
    : drug?.region === 'JP' ? (isViewingJpPdf ? null : 'jp')
    : drug?.region === 'CN' || drug?.region === 'CH' || LABEL_PDF_REGIONS.has(drug?.region ?? '') || drug?.region === 'FR' ? null
    : isViewingAu || isViewingEu || isViewingCaPm || isViewingUkPil || isViewingApprovalLetter
      || isViewingEpar || !!sbaDetailError || isViewingSba || isViewingFdaLetter || auCaLoading
      || (isDraftVersion && !!versionDetail?.document_sections) ? null
    : 'sections';

  // ---- A5 (T13): match counts, Matches only, Find, rail scroll-spy. These are
  // hooks, so they sit above the early returns below. ----
  const isLg = useMinWidthLg();
  const cfg = labelView === 'sections' ? US_ROWS : labelView === 'jp' ? JP_ROWS : null;
  const termsKey = highlightTerms.join('\u0000');
  const { counts, heads, ready, countedKey } = useArticleMatches(articleRoot, cfg, termsKey);
  const [activeHead, setActiveHead] = useState<HTMLElement | null>(null);
  const [liveText, setLiveText] = useState('');
  const [spyKey, setSpyKey] = useState<string | null>(null);
  const liveKeyRef = useRef<string | null>(null);
  const rowsModel: { key: string; sectionNumber: string | null; highlights?: boolean; bw?: boolean; collapsible?: boolean }[] =
    labelView === 'sections'
      ? getDisplaySections().map((s) => ({
          key: `section-${s.id}`,
          sectionNumber: s.section_number || null,
          highlights: isHighlightsSection(s),
          bw: s.section_number === 'BW',
          // Empty subsections render no heading, so they get no toggle.
          collapsible: !isHighlightsSection(s) && s.section_number !== 'BW' && (!!s.content_text?.trim() || !isSubsectionCheck(s)),
        }))
      : labelView === 'jp'
        ? jpSections.map((s) => ({ key: s.section_number, sectionNumber: s.section_number }))
        : [];

  highlightsRowRef.current = labelView === 'sections' && rowsModel.some((r) => r.highlights)
    ? { sectionNumber: rowsModel.find((r) => r.highlights)!.sectionNumber }
    : null;
  rowsRef.current = labelView === 'sections' ? rowsModel : [];
  const collapsibleKeys = rowsModel.filter((r) => r.collapsible).map((r) => r.key);
  const anyExpanded = collapsibleKeys.some((k) => !collapsedRows.has(k));
  const collapsedNums = rowsModel.filter((r) => r.sectionNumber && collapsedRows.has(r.key)).map((r) => r.sectionNumber as string);
  const toggleRow = (key: string) => setCollapsedRows((prev) => {
    const next = new Set(prev);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  const returnToFull = useCallback(() => {
    setViewMode('full');
    setSnapshot(null);
  }, []);
  // Anything that can change the counts returns to Full label (R7.2).
  const resetSeen = useRef(false);
  useEffect(() => {
    if (!resetSeen.current) { resetSeen.current = true; return; }
    returnToFull();
  }, [termsKey, selectedHistoricalId, jpViewDoc, currentDrugId, returnToFull]);
  // K2: every row is expanded again on a drug / version change.
  useEffect(() => { setCollapsedRows(new Set()); }, [currentDrugId, selectedHistoricalId, selectedVersionId]);

  const setViewModeTo = (m: 'full' | 'matches') => {
    if (m === 'full') { returnToFull(); return; }
    setSnapshot({ counts, keep: matchesOnlyKeep(rowsModel.filter((r) => !r.highlights), counts) });
    setViewMode('matches');
  };
  const shownCounts = viewMode === 'matches' && snapshot ? snapshot.counts : counts;
  let total = 0;
  shownCounts.forEach((n) => { total += n; });
  const showToggle = !!deepLink && highlightTerms.length > 0;

  const activeIndex = activeHead ? heads.indexOf(activeHead) + 1 || null : null;
  useEffect(() => {
    if (activeHead && !heads.includes(activeHead)) setActiveHead(null);
  }, [heads, activeHead]);
  useEffect(() => {
    if (!activeHead) return undefined;
    const marks = marksOfMatch(activeHead);
    marks.forEach((m) => m.classList.add('is-active-match'));
    return () => marks.forEach((m) => m.classList.remove('is-active-match'));
  }, [activeHead]);

  const move = (dir: 1 | -1, fromStart = false) => {
    const n = heads.length;
    if (n === 0) return;
    const cur = fromStart || !activeHead ? -1 : heads.indexOf(activeHead);
    const i = cur < 0 ? (dir === 1 ? 0 : n - 1) : (cur + dir + n) % n;
    const el = heads[i]!;
    setActiveHead(el);
    const rowKey = labelView === 'sections' ? el.closest('[data-label-row]')?.getAttribute('data-label-row') : null;
    const go = () => el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (rowKey) revealRow(rowKey, go); else go();
    setLiveText(`Match ${i + 1} of ${n}`);
  };
  const onFindEnter = (shift: boolean) => {
    pendingMoveRef.current = null;
    const dir = shift ? -1 : 1;
    if (findText === committedText) { move(dir); return; }
    setCommittedText(findText);
    const key = parseLabelTerms(findText, { jpja: !!deepLink?.jpja, phrase: !!deepLink?.phrase }).join('\u0000');
    // Heads detached by an intervening render (counted key unchanged) must not be selected.
    if (key === countedKey && heads.every((h) => h.isConnected)) move(dir, true);
    else pendingMoveRef.current = { dir, key };
  };
  // The queued Enter move fires once the recount of the new terms has settled.
  useEffect(() => {
    const p = pendingMoveRef.current;
    if (p && countedKey === p.key) {
      pendingMoveRef.current = null;
      if (heads.length > 0) { move(p.dir, true); liveKeyRef.current = countedKey; return; }
    }
    if (countedKey !== null && countedKey !== liveKeyRef.current) {
      liveKeyRef.current = countedKey;
      setLiveText(!countedKey ? '' : heads.length === 0 ? 'No matches' : heads.length === 1 ? '1 match' : `${heads.length} matches`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countedKey, heads]);

  // Scroll-spy (R6.4): the last row whose top has passed the sticky strip.
  const spyOffset = paneFit?.top ?? 0;
  useEffect(() => {
    const pane = rootRef.current;
    if (!pane || !articleRoot || !cfg) { setSpyKey(null); return undefined; }
    let raf = 0;
    const compute = () => {
      raf = 0;
      const limit = pane.getBoundingClientRect().top + spyOffset + 1;
      let key: string | null = null;
      articleRoot.querySelectorAll(cfg.rowSelector).forEach((row) => {
        if (labelView === 'jp' && !row.id.startsWith('jp-sec-')) return;
        if (row.closest('[hidden]')) return;
        if (row.getBoundingClientRect().top <= limit) key = cfg.rowKey(row);
      });
      setSpyKey(key);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    pane.addEventListener('scroll', onScroll);
    return () => {
      pane.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [rootRef, articleRoot, cfg, labelView, spyOffset, heads, viewMode, collapsedRows]);


  // Only show full-page loading on initial load (no drug data yet)
  // When switching drugs, we keep the layout and show loading in center pane
  if (isLoading && !drug) {
    return (
      <div className="min-h-full bg-slate-100 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  if (error && !drug) {
    return (
      <div className="min-h-full bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error || 'Drug not found'}</p>
          <button
            onClick={handleBackToSearch}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  // TypeScript guard: drug must exist at this point
  if (!drug) {
    return null;
  }

  // An exact historical target must never expose the mutable current label
  // while it is loading or when the requested version is unavailable.
  if (
    selectedHistoricalId !== null
    && historicalVersionData?.id !== selectedHistoricalId
  ) {
    return (
      <div className="min-h-full bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className={`text-xl ${historicalVersionError ? 'text-red-600' : 'text-slate-600'}`}>
            {historicalVersionError ?? 'Loading historical label version…'}
          </p>
          {historicalVersionError && (
            <button
              onClick={handleBackToSearch}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Back to Search
            </button>
          )}
        </div>
      </div>
    );
  }

  const tocSections = getTocSections();

  // A5 rail: TOC entries joined to the row model (TOC entries carry no section number for US).
  const railEntries: RailEntry[] = labelView
    ? tocSections.map((t) => {
        const level = t.level === 2 ? 2 : 1;
        if (labelView === 'jp') {
          return { key: t.sectionNumber ?? t.id, title: t.title, level, sectionNumber: t.sectionNumber ?? null, absent: t.absent };
        }
        const r = rowsModel.find((m) => m.key === t.id);
        return { key: t.id, title: t.title, level, sectionNumber: r?.sectionNumber ?? null, boxedWarning: r?.bw, highlights: r?.highlights };
      })
    : [];
  const onRailSelect = (e: RailEntry) => {
    if (labelView === 'jp') {
      window.dispatchEvent(new CustomEvent('jp-goto-section', { detail: e.sectionNumber }));
      return;
    }
    const go = () => {
      if (e.highlights) setHighlightsOpen(true);
      revealRow(e.key, () => sectionRefs.current.get(e.key)?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    };
    if (e.highlights) withFullLabel(e.sectionNumber, go); else go();
  };
  const zeroNoteKeyword =
    deepLink && ready && highlightTerms.length > 0 && total === 0 && committedText === deepLink.highlight
      ? deepLink.highlight
      : null;

  // Compare menu: every comparable label except the one being viewed.
  const compareMenuOptions = (currentlyViewing?.current ? compareOptions : [])
    .slice().reverse()
    .filter(v => !(v.id === currentlyViewing!.current!.id && v.source === currentlyViewing!.current!.source))
    .map(v => {
      // FDA-current/historical → regional name + DD/MM/YYYY (matches the main
      // VersionSelector dropdown). Drafts keep their own "Draft v3" format.
      const isFdaVersion = v.source === 'fda_current' || v.source === 'fda_historical';
      const label = isFdaVersion
        ? formatRegionalVersionLabel(drug?.region, v.effective_date, v.version_number)
        : v.source === 'draft'
          ? `Draft v${v.version_number}${
              v.created_at
                ? ` - ${new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : ''
            }`
          : `v${v.version_number}`;
      const star =
        v.id === currentlyViewing!.latest?.id && v.source === currentlyViewing!.latest?.source ? ' ★' : '';
      return { key: `${v.source}-${v.id}`, label: `${label}${star}` };
    });
  // Compare stays inside the active source: with Drugs@FDA active the only
  // compare is FdaLabelPdfVersionCompareLauncher (labels only).
  const showCompareMenu = compareOptions.length >= 2 && !!currentlyViewing?.current && compareMenuOptions.length > 0
    && !drugsAtFdaActive;
  const showWatch = !displayDrug?.is_historical && !!drug;
  const handleCompareMenuChoose = (key: string) => {
    if (!currentlyViewing?.current) return;
    const [selSource, selId] = key.split('-');
    const selectedVersion = compareOptions.find(v => v.source === selSource && v.id === Number(selId));
    if (selectedVersion) {
      handleTimeBasedPolymorphicCompare(
        {
          source: currentlyViewing.current.source as VersionSource,
          id: currentlyViewing.current.id,
          version_number: currentlyViewing.current.version_number,
          effective_date: currentlyViewing.current.effective_date,
          created_at: currentlyViewing.current.created_at,
        },
        {
          source: selectedVersion.source as VersionSource,
          id: selectedVersion.id,
          version_number: selectedVersion.version_number,
          effective_date: selectedVersion.effective_date,
          created_at: selectedVersion.created_at,
        }
      );
    }
  };

  return (
    <div className="min-h-full bg-slate-100">
      {/* Main content area */}
      <div>
      {/* Upload Modal */}
      {showUploadModal && drug.set_id && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg">
            <DraftUpload
              setId={drug.set_id}
              onUploadComplete={handleUploadComplete}
              onCancel={() => setShowUploadModal(false)}
            />
          </div>
        </div>
      )}

      {/* EU historical INDIVIDUAL-version viewer (opened by an eu_history_section
          CEPI citation via ?euhview=1&euhdoc=&euhpage=&euhterm=). Distinct from
          the compare launcher: this shows ONE archived version, scrolled to the
          cited section's page with its text boxed. When the EU SmPC pane is
          active it renders inline in place of the current SmPC instead (see the
          isViewingEu branch); this overlay is only for drugs without that pane. */}
      {euHistoricalOpen && !isViewingEu && !euAutolandPending && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-5xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <EuHistoricalViewer
              docId={euViewerDocId}
              initialPage={Number(searchParams.get('euhpage')) || 1}
              initialY={searchParams.get('euhy') != null ? Number(searchParams.get('euhy')) : undefined}
              initialSection={searchParams.get('euhsection')}
              presentationKey={searchParams.get('euhpres')}
              onClose={closeEuHistoricalView}
            />
          </div>
        </div>
      )}

      {/* Header: back link, identity, meta line, actions */}
      <header className="bg-white border-b border-line">
        <div className="max-w-[98%] mx-auto px-4 py-2 flex flex-col gap-1">
          <LabelBackLink fromTabId={fromTabId} onBack={handleBackToSearch} />
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <h1 className="font-display text-[26px] font-medium text-ink">
                  {displayDrug?.brand_name || displayDrug?.generic_name || drug.brand_name || drug.generic_name}
                </h1>
                {(displayDrug?.brand_name || drug.brand_name) && (displayDrug?.generic_name || drug.generic_name) && (
                  <span className="text-base text-ink-2">
                    {displayDrug?.generic_name || drug?.generic_name}
                  </span>
                )}
                {getDisplaySections().some((sec) => sec.section_number === 'BW') && (
                  <span className="rounded-ctl border border-signal px-1.5 font-mono text-[11px] text-signal">Boxed warning</span>
                )}
              </div>
              <GenericLinks generics={drug.generics} />
              {/* Meta line */}
              <p className="mt-1 text-[13px] text-ink-2">
                {[
                  displayDrug?.manufacturer || drug.manufacturer,
                  displayDrug?.dosage_form || drug.dosage_form,
                  displayDrug?.route || drug.route,
                  (displayDrug?.ndc_code || drug.ndc_code) && (
                    <span key="ndc" className="font-mono">NDC {displayDrug?.ndc_code || drug.ndc_code}</span>
                  ),
                  (displayDrug?.set_id || drug.set_id) && (
                    <span key="set" className="font-mono">{displayDrug?.set_id || drug.set_id}</span>
                  ),
                  approvalLabel && (
                    <React.Fragment key="appr">Approved <span className="font-mono">{approvalLabel}</span></React.Fragment>
                  ),
                  displayDrug?.is_historical && displayDrug?.version_number && `v${displayDrug.version_number}`,
                  displayDrug?.is_historical && displayDrug?.effective_time
                    && `Effective ${new Date(displayDrug.effective_time).toLocaleDateString()}`,
                ].filter(Boolean).reduce<React.ReactNode[]>((acc, part, i) => {
                  if (i > 0) acc.push(' · ');
                  acc.push(<React.Fragment key={i}>{part}</React.Fragment>);
                  return acc;
                }, [])}
              </p>

              {/* Draft version indicator and comparison buttons */}
              {isDraftVersion && versionDetail && (
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="inline-flex items-center gap-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    <span className="text-amber-800">
                      Viewing Draft v{versionDetail.version_number}
                      {versionDetail.original_filename && ` - ${versionDetail.original_filename}`}
                    </span>
                  </div>

                  {/* Version Comparison Buttons */}
                  {adjacentVersions && adjacentVersions.total_versions > 1 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-slate-500 mr-1">Compare:</span>

                      {/* Compare to Previous */}
                      {adjacentVersions.previous && (
                        <button
                          onClick={() => handleCompare(adjacentVersions.previous!.id, selectedVersionId!)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors"
                          title={`Compare v${adjacentVersions.previous.version_number} → v${versionDetail.version_number}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Previous (v{adjacentVersions.previous.version_number})
                        </button>
                      )}

                      {/* Compare to Next */}
                      {adjacentVersions.next && (
                        <button
                          onClick={() => handleCompare(selectedVersionId!, adjacentVersions.next!.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors"
                          title={`Compare v${versionDetail.version_number} → v${adjacentVersions.next.version_number}`}
                        >
                          Next (v{adjacentVersions.next.version_number})
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}

                      {/* Compare to Latest */}
                      {adjacentVersions.latest && adjacentVersions.latest.id !== selectedVersionId && (
                        <button
                          onClick={() => handleCompare(selectedVersionId!, adjacentVersions.latest!.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 transition-colors"
                          title={`Compare v${versionDetail.version_number} → v${adjacentVersions.latest.version_number} (Latest)`}
                        >
                          Latest (v{adjacentVersions.latest.version_number})
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Ingredients Panel - uses displayDrug to show historical ingredients when viewing historical */}
            {((displayDrug?.ingredients && displayDrug.ingredients.length > 0) || (drug.ingredients && drug.ingredients.length > 0)) && (
              <div className="lg:w-64 rounded-ctl border border-line bg-panel p-3">
                <h3 className="eyebrow mb-2">
                  Active ingredients
                </h3>
                <ul className="space-y-1">
                  {(displayDrug?.ingredients || drug.ingredients || []).filter((i: any) => i.is_active).map((ingredient: any) => (
                    <li key={ingredient.id} className="text-sm">
                      <span className="text-slate-900 font-medium">{ingredient.name}</span>
                      {ingredient.strength_value && (
                        <span className="text-slate-500 ml-1">
                          {ingredient.strength_value} {ingredient.strength_unit}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Right: Watch, Compare, Upload Draft + region launchers */}
            {(showWatch || showCompareMenu || !!drug.set_id) && (
            <div className="shrink-0 pt-1 flex flex-col items-end gap-2">
              {showWatch && (
                <SubscribeLabelButton
                  drugId={Number(currentDrugId)}
                  drugRegion={drug.region}
                  drugName={drug.brand_name ?? drug.generic_name ?? undefined}
                />
              )}
              {showCompareMenu && (
                <div data-testid="drug-header-controls-row">
                  <CompareMenu options={compareMenuOptions} onChoose={handleCompareMenuChoose} />
                </div>
              )}
              {drug.set_id && (
              <>
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-primary"
              >
                Upload draft
              </button>
                {drug?.region === 'DE' && selectedDePdfId !== null && (
                  <DeVersionCompareLauncher
                    docId={selectedDePdfId}
                    docType={dePdfDocuments.find((d) => d.id === selectedDePdfId)?.doc_type ?? 'smpc'}
                    brand={displayDrug?.brand_name || drug.brand_name
                      || displayDrug?.generic_name || drug.generic_name || ''}
                  />
                )}
                {drug?.region === 'EU' && (
                  <EuHistoricalCompareLauncher
                    drugId={drug.id}
                    setId={(displayDrug?.set_id || drug.set_id) as string}
                    brand={displayDrug?.brand_name || drug.brand_name
                      || displayDrug?.generic_name || drug.generic_name || ''}
                  />
                )}
                {/* JP compare launcher (US-style): pick an older version, Compare. */}
                {drug?.region === 'JP' && jpLineages.some((l) => l.versions.length >= 2) && (
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-ink-2">Compare:</span>
                    <select
                      aria-label="Compare version"
                      value={jpCompareTarget ?? ''}
                      onChange={(e) => setJpCompareTarget(e.target.value || null)}
                      className="h-9 max-w-[170px] rounded-ctl border border-line-input bg-white px-2 text-sm text-ink focus:outline-none focus:ring-4 focus:ring-accent-ring"
                    >
                      <option value="">Select version…</option>
                      {jpLineages.filter((l) => l.versions.length >= 2).map((l) => (
                        <optgroup key={l.lineage_key} label={lineageLabel(l)}>
                          {l.versions.slice(1).map((v) => (
                            <option key={v.doc_id} value={`${l.lineage_key}|${v.version_number}`}>
                              {v.edition != null ? `ed. ${v.edition}` : `v${v.version_number}`}{v.revision_date ? ` · ${v.revision_date}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowJpCompare(true)}
                      disabled={jpCompareTarget == null}
                      className="btn-secondary"
                    >
                      Compare
                    </button>
                  </div>
                )}
                {/* AU compare launcher: one button → modal with its own version picker
                    (AU is single-language English PDF → EU-style two-pane diff). */}
                {drug?.region === 'AU' && auVersions.length >= 2 && (
                  <button
                    onClick={() => setShowAuCompare(true)}
                    className="btn-secondary"
                  >
                    Compare versions
                  </button>
                )}
                {/* AU "view historical version" picker — points the PDF viewer at a
                    past version's document directly (not the compare modal). */}
                {drug?.region === 'AU' && auVersions.length >= 2 && (
                  <select
                    value={auViewVersion ?? ''}
                    onChange={(e) => setAuViewVersion(e.target.value ? Number(e.target.value) : null)}
                    title="View a historical version of this Product Information"
                    className="h-9 rounded-ctl border border-line-input bg-white px-2 text-sm text-ink hover:border-ink-3 focus:outline-none focus:ring-4 focus:ring-accent-ring"
                  >
                    {(() => {
                      const cur = auVersions.find((v) => v.is_current);
                      return (
                        <option value="">
                          {cur
                            ? `PI V${cur.version_number}${cur.revision_date ? ` · ${cur.revision_date}` : ''}`
                            : 'Current version'}
                        </option>
                      );
                    })()}
                    {[...auVersions]
                      .filter((v) => !v.is_current)
                      .sort((a, b) => b.version_number - a.version_number)
                      .map((v) => (
                        <option key={v.version_number} value={v.version_number}>
                          PI V{v.version_number}{v.revision_date ? ` · ${v.revision_date}` : ''}
                        </option>
                      ))}
                  </select>
                )}
                {showAuCompare && drug?.region === 'AU' && auVersions.length >= 2 && (
                  <AUVersionCompareModal
                    drugId={drug.id}
                    brand={(displayDrug?.brand_name || drug.brand_name
                      || displayDrug?.generic_name || drug.generic_name || '') as string}
                    versions={auVersions}
                    onClose={() => setShowAuCompare(false)}
                  />
                )}
              </>
              )}
            </div>
            )}
          </div>
        </div>
      </header>

      {/* Sticky Navigation Bar */}
      <div ref={setHeaderEl} className="sticky top-0 z-20 bg-panel border-b border-line shadow-sm overflow-visible">
        <div className="max-w-[98%] mx-auto px-4 py-2 overflow-visible">
          {/* Two rows, both children of the wrapping container below.
              Row 1: Back + TOC + drug identity, then Compare.
              Row 2: the FDA submission controls, `w-full` so they claim a
              full line of their own (see the block near the end of this
              container). They are NOT on row 1 — sharing it is what starved
              them to 299px and made them stack. */}
          <div className="flex flex-row flex-wrap items-center gap-x-4 gap-y-2 overflow-visible">
            {/* Row 1, left half: Back + TOC + drug identity, left-aligned.
                Compare shares this line; the submission controls no longer
                do. */}
            <div className="flex items-center gap-4 overflow-visible">
            {/* Left: Back + TOC Dropdown */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* TOC Dropdown — for SBA, the viewer has its own TOC
                  sidebar but the page-level dropdown is kept so
                  citation click-through and explicit navigation still
                  work. Clicks on SBA entries route through
                  sbaViewerRef.scrollToSection (Phase 10a contract).
                  Hidden for EU: the embedded EU viewer has its own
                  grouped Contents sidebar (per-presentation banners),
                  and this page-level dropdown would otherwise fall
                  through to the legacy section-text list — the wrong,
                  non-navigating content the EU PDF viewer can't use. */}
              {/* Hidden for FR too: FrHtmlViewer carries its own TOC sidebar. */}
              {!isViewingEu && !isViewingFrHtml && !euAutolandPending && labelView === null && (
              <div className="relative">
                <button
                  onClick={() => setShowTocDropdown(!showTocDropdown)}
                  className="flex items-center gap-2 px-4 py-2 text-base font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors border border-indigo-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <span>Contents</span>
                  <svg className={`w-4 h-4 transition-transform ${showTocDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* TOC Dropdown Panel - stays open until explicitly closed */}
                {showTocDropdown && (
                  <div className="absolute left-0 top-full mt-1 w-80 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl z-20">
                    <div className="sticky top-0 px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 uppercase">Table of Contents</span>
                      <div className="flex items-center gap-2">
                        {/* "View All" / "Close All" expands accordion
                            sections — no accordion exists in the SBA,
                            EPAR, or UK PIL viewer paths, so hide for
                            all three. */}
                        {!isViewingSba && !isViewingEpar && !isViewingUkPil && !isViewingCaPm && !isViewingAu && !isViewingEu && (
                          <button
                            onClick={toggleAllSections}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            {allExpanded ? 'Close All' : 'View All'}
                          </button>
                        )}
                        <button
                          onClick={() => setShowTocDropdown(false)}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded transition-colors"
                          title="Close"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <ul className="py-1">
                      {tocSections.map((section) => {
                        const isSubsection = section.level > 1;
                        const isExpanded = expandedSections.has(section.id);
                        // JP: canonical section not present in this label —
                        // greyed, non-clickable, hollow marker + tooltip.
                        if (section.absent) {
                          return (
                            <li key={section.id}>
                              <div
                                className="w-full text-left px-4 py-2 text-sm text-slate-300 flex items-center gap-2 cursor-default select-none"
                                title="Not present in the official PMDA package insert for this drug"
                              >
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 border border-slate-300" />
                                {section.title}
                              </div>
                            </li>
                          );
                        }
                        return (
                          <li key={section.id}>
                            <button
                              onClick={() => {
                                // JP entries: tell JPSectionView to expand
                                // the target accordion section (+ its parent)
                                // and scroll to it.
                                if (section.jp && section.sectionNumber) {
                                  window.dispatchEvent(new CustomEvent(
                                    'jp-goto-section',
                                    { detail: section.sectionNumber },
                                  ));
                                  return;
                                }
                                // SBA TOC entries carry the viewer's
                                // section_number and route through
                                // sbaViewerRef — no DOM ref to scroll
                                // to after Phase 10c.
                                if (section.sectionNumber && sbaViewerRef.current) {
                                  sbaViewerRef.current.scrollToSection(
                                    section.sectionNumber,
                                  );
                                  return;
                                }
                                toggleSection(section.id);
                                const element = sectionRefs.current.get(section.id);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 transition-colors flex items-center gap-2 ${
                                isSubsection ? 'pl-8 text-slate-600' : 'font-medium text-slate-800'
                              } ${isExpanded ? 'bg-indigo-50 text-indigo-700' : ''}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isExpanded ? 'bg-indigo-500' : 'bg-transparent'}`} />
                              {section.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Drug Name (Manufacturer) — identity, left-aligned next
                to Back/TOC (Task B5: was flex-1 + justify-center,
                which centered it across the whole header row). */}
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm font-bold text-slate-800 truncate">
                {displayDrug?.brand_name || displayDrug?.generic_name || drug?.brand_name || drug?.generic_name}
              </h2>
              {(displayDrug?.brand_name || drug?.brand_name) && (displayDrug?.generic_name || drug?.generic_name) && (
                <span className="text-xs text-slate-500 truncate">
                  ({displayDrug?.generic_name || drug?.generic_name})
                </span>
              )}
              {(displayDrug?.manufacturer || drug?.manufacturer) && (
                <>
                  <span className="text-slate-300">|</span>
                  <span className="text-xs text-slate-500 truncate">
                    {displayDrug?.manufacturer || drug?.manufacturer}
                  </span>
                </>
              )}
            </div>
            </div>

            {/* Row 2 — the FDA submission controls take the header's FULL
                width instead of fighting for the leftovers of row 1. As a
                `w-full` child of the wrapping row above, this claims its own
                line (the Task B5 pattern).

                It used to be a `flex-1 min-w-0` item inside
                `drug-header-controls-row`. `flex-1` means `flex: 1 1 0%`, so
                its hypothetical main size was 0 — it could never wrap onto its
                own line, it could only absorb whatever the identity block and
                the `flex-shrink-0` Compare controls left over. Measured on a
                1000px window: 299px, against the 492px three dropdowns need to
                sit side by side, so they stacked. */}
            {drug?.set_id && (
              <div className="w-full min-w-0 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="drug-header-selector-row">
              <span className="eyebrow">Document set</span>
              {isNiceHtaView ? (
                <div className="flex flex-col gap-2" data-testid="nice-hta-controls">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      aria-label="Module"
                      value={niceModule}
                      onChange={(e) => {
                        const pillar = e.target.value;
                        setNiceModule(pillar);
                        // Reset the group to the pillar's default and point the
                        // viewer at what that pillar shows.
                        if (pillar === 'Regulatory Affairs') {
                          setNiceCorpus('UK SmPC');
                          if (drug) handleUnifiedVersionChange('fda_current', drug.id);
                        } else if (pillar === 'Commercial Intelligence') {
                          setNiceCorpus('NICE Assessments');
                          const first = niceHtaDocs[0] as { id?: number } | undefined;
                          if (first?.id != null) handleUnifiedVersionChange('ukpil', first.id);
                        } else {
                          // Clinical Development — nothing wired; viewer goes blank.
                          setNiceCorpus('');
                        }
                      }}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    >
                      <option>Regulatory Affairs</option>
                      <option>Clinical Development</option>
                      <option>Commercial Intelligence</option>
                    </select>
                    {niceGroupOptions.length > 0 && (
                    <select
                      aria-label="Corpus"
                      value={niceCorpus}
                      onChange={(e) => {
                        const group = e.target.value;
                        setNiceCorpus(group);
                        if (niceModule === 'Regulatory Affairs' && group === 'UK SmPC') {
                          if (drug) handleUnifiedVersionChange('fda_current', drug.id);
                        } else if (niceModule === 'Commercial Intelligence' && group === 'NICE Assessments') {
                          const first = niceHtaDocs[0] as { id?: number } | undefined;
                          if (first?.id != null) handleUnifiedVersionChange('ukpil', first.id);
                        }
                        // Any other group has nothing wired; the viewer goes blank.
                      }}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    >
                      {niceGroupOptions.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                    )}
                  </div>
                  {showNiceButtons && (
                  <div className="flex flex-wrap gap-2" data-testid="nice-hta-doc-buttons">
                    {niceHtaDocs.map((d) => {
                      const docId = (d as { id: number }).id;
                      const label = (d as { doc_type_label?: string; doc_type?: string }).doc_type_label
                        || String((d as { doc_type?: string }).doc_type || '')
                             .split('_')
                             .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
                             .join(' ');
                      const active = selectedUkPilId === docId;
                      return (
                        <button
                          key={docId}
                          type="button"
                          onClick={() => handleUnifiedVersionChange('ukpil', docId)}
                          className={
                            'rounded-md border px-3 py-1.5 text-sm transition-colors '
                            + (active
                              ? 'border-indigo-500 bg-indigo-600 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50')
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>
              ) : (
              <div className="flex items-center gap-2 flex-wrap">
              {usFdaMode && drug?.region === 'US_FDA' ? (
                // US_FDA product page: the DailyMed picker lists the linked
                // DailyMed products and navigates to them. None → one picker.
                (usFdaData?.drugId === drug.id && usFdaData.dailymed.length > 0) ? (
                  <div data-testid="dailymed-picker" data-active="false"
                    className="flex items-center gap-2 opacity-50">
                    <span className="eyebrow">DailyMed</span>
                    <select
                      aria-label="DailyMed product"
                      value=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        navigate(`/drug/${e.target.value}`);
                      }}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800"
                    >
                      <option value="">Select DailyMed label…</option>
                      {usFdaData.dailymed.map((p) => (
                        <option key={p.drug_id} value={p.drug_id}>
                          {[p.brand_name, p.manufacturer].filter(Boolean).join(' · ') || p.set_id || `Drug ${p.drug_id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null
              ) : wrapDailyMedPicker(
              <VersionSelector
                versions={versions}
                selectedVersionId={selectedVersionId}
                selectedHistoricalId={selectedHistoricalId}
                onVersionChange={handleVersionChange}
                onUnifiedVersionChange={handleUnifiedVersionChange}
                isLoading={isLoadingVersions}
                drugRegion={drug?.region}
                showFdaOption={
                  drug?.region !== 'AU' &&
                  drug?.region !== 'CA' &&
                  drug?.region !== 'JP' &&
                  // CN (Chinese NMPA) has only its own zh_pdf docs (说明书 +
                  // 技术审评报告), shown in the "China — NMPA" group. Without this
                  // gate the drug's synthetic read-time fda_current row renders
                  // under a hardcoded "FDA Versions" group and — since CN has no
                  // REGION_LABEL_PREFIX entry — is mislabeled "EU SmPC - V1"
                  // (pure display artifact; no EU/FDA data exists). Suppress it.
                  drug?.region !== 'CN' &&
                  // CH: the Swissmedic group is the label (no FDA/EU rows exist).
                  drug?.region !== 'CH' &&
                  // DE: likewise, the BfArM group is the label.
                  !LABEL_PDF_REGIONS.has(drug?.region ?? '') &&
                  // FR: the ANSM label viewer is the label.
                  drug?.region !== 'FR' &&
                  // EU label is normally the per-family SmPC PDF (eu_documents),
                  // which renders as its own dropdown group. But EU drugs ingested
                  // as parsed sections only (no SmPC PDF) have no such entry, so
                  // surface the regional current-label (fda_current "EU SmPC") entry
                  // to keep a "back to label" option. Suppress it when a PDF exists
                  // (that group is the label) to avoid a redundant entry.
                  !(drug?.region === 'EU' && euDocuments.length > 0)
                }
                {...(allVersionsData?.versions ? { unifiedVersions: allVersionsData.versions } : {})}
                drugId={drug.id}
                sbaDocuments={sbaDocuments}
                pinnedSbaDocument={pinnedSbaDocument}
                selectedSbaId={selectedSbaId}
                reviewLetters={reviewLetters}
                selectedApprovalLetterId={selectedApprovalLetterId}
                sbaCycleInfo={sbaCycleInfo}
                // 2026-08-17-c: the "Switch to FDA Submission View" /
                // "Back to classic view" control is HIDDEN. Submission view is
                // the only view now, so an escape hatch back to classic no
                // longer makes sense.
                //
                // The toggle CODE is deliberately retained, not deleted:
                // classic remains a working, tested path (`sbaCycleInfo ==
                // null`) and re-exposing it is flipping this one expression
                // back to `drug?.region === 'US'`. Deleting the control would
                // also take `onSubmissionViewToggle` and the whole
                // `sbaListStatus` status-copy branch with it.
                //
                // Hidden HERE rather than inside VersionSelector because this
                // prop gates the toggle and nothing else
                // (VersionSelector.tsx:886) — so the component's own tests
                // keep covering the control for whenever it returns.
                submissionViewEligible={SUBMISSION_VIEW_TOGGLE_VISIBLE && drug?.region === 'US' && !usFdaMode}
                sbaListStatus={sbaListStatus}
                submissionViewToggleDisabled={!drugRouteSettled}
                onSubmissionViewToggle={handleSubmissionViewToggle}
                eparDocuments={eparDocuments}
                selectedEparId={selectedEparId}
                ukPilDocuments={ukPilDocuments}
                selectedUkPilId={selectedUkPilId}
                caPmDocuments={caPmDocuments}
                selectedCaPmId={selectedCaPmId}
                auDocuments={auDocuments}
                selectedAuId={selectedAuId}
                euDocuments={euDocuments}
                selectedEuId={selectedEuId}
                jpPdfDocuments={jpPdfDocuments}
                selectedJpPdfId={selectedJpPdfId}
                // GA for all users since 2026-08-31 — the zh doc group appears
                // in the dropdown for everyone, like the JP group above.
                zhPdfDocuments={zhPdfDocuments}
                selectedZhPdfId={selectedZhPdfId}
                chPdfDocuments={chPdfDocuments}
                selectedChPdfId={selectedChPdfId}
                dePdfDocuments={dePdfDocuments}
                selectedDePdfId={selectedDePdfId}
                // Layout A: Drugs@FDA labels live in their own picker.
                fdaLabelPdfDocuments={usFdaMode ? NO_FDA_LABEL_PDFS : fdaLabelPdfDocuments}
                selectedFdaLabelPdfId={selectedFdaLabelPdfId}
                frHtmlDocuments={frHtmlDocuments}
                selectedFrHtmlId={selectedFrHtmlId}
                fdaLetters={fdaLetters}
                selectedFdaLetterId={selectedFdaLetterId}
              />,
              )}
              {usFdaMode && usFdaApps.length > 0 && (
                <DrugsAtFdaPicker
                  apps={usFdaApps}
                  active={drugsAtFdaActive}
                  selected={drugsAtFdaSelected}
                  onOpen={openDrugsAtFdaDocument}
                />
              )}
              {/* JP: version picker beside the doc-type selector — switch which
                  version of the package insert is displayed (default current). */}
              {drug?.region === 'JP' && jpLineages.reduce((n, l) => n + l.versions.length, 0) > 1 && (() => {
                // Default = what /jp-sections opens with no doc_id (backend
                // default_doc_id): the newest current insert, else the newest one.
                const reps = jpLineages.flatMap((l) => l.versions);
                const curIds = reps.filter((v) => v.is_current).map((v) => v.doc_id);
                const pool = curIds.length ? curIds : reps.map((v) => v.doc_id);
                const defaultDoc = pool.length ? Math.max(...pool) : null;
                return (
                  <select
                    aria-label="Package insert version"
                    value={jpViewDoc ?? defaultDoc ?? ''}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      setJpViewDoc(id === defaultDoc ? null : id);
                    }}
                    className="h-[1.875rem] max-w-[22rem] px-2 py-1 text-sm border border-slate-300 rounded-lg bg-white"
                  >
                    {jpLineages.map((l) => (
                      <optgroup key={l.lineage_key} label={lineageLabel(l)}>
                        {l.versions.map((v) => (
                          <option key={v.doc_id} value={v.doc_id}>
                            {v.edition != null ? `ed. ${v.edition}` : `v${v.version_number}`}{v.revision_date ? ` · ${v.revision_date}` : ''}{v.is_current ? ' (current)' : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                );
              })()}
              {/* EU "view any version" dropdown, beside the doc-type selector like JP's: open the current
                  SmPC or any archived version in the viewer — NOT the compare. Current
                  clears the overlay (shows the main viewer); a historical version opens
                  the existing EuHistoricalViewer by its legacy euhdoc id. */}
              {drug?.region === 'EU' && euVersions.length >= 2 && (<>
                <select
                  value={euVerParam
                    || (euHdocParam > 0
                        ? (euVersions.find((v) => v.legacy_historical_id === euHdocParam)?.source_version_key || '')
                        : '')}
                  onChange={(e) => {
                    const next = new URLSearchParams(searchParams);
                    ['euhview', 'euhdoc', 'euver', 'euhpage', 'euhy', 'euhpres', 'euhterm', 'euhsection']
                      .forEach((k) => next.delete(k));
                    // Stable public handle: ?euver=<source_version_key> (survives
                    // re-ingest). Current version => no param (main viewer).
                    if (e.target.value) {
                      next.set('euhview', '1');
                      next.set('euver', e.target.value);
                    }
                    setSearchParams(next);
                  }}
                  title="View a specific version of this SmPC"
                  className="h-[1.875rem] max-w-[22rem] px-2 py-1 text-sm border border-slate-300 rounded-lg bg-white"
                >
                  {(() => {
                    const cur = euVersions.find((v) => v.is_current);
                    return (
                      <option value="">
                        {cur
                          ? `V${cur.version_number}${cur.effective_date ? ` · ${cur.effective_date}` : ''}`
                          : 'Current version'}
                      </option>
                    );
                  })()}
                  {euVersions
                    .filter((v) => !v.is_current && v.legacy_historical_id)
                    .map((v) => (
                      <option key={v.version_number} value={v.source_version_key}>
                        V{v.version_number}{v.effective_date ? ` · ${v.effective_date}` : ''}
                      </option>
                    ))}
                </select>
                {/* BETA until the EU version source (EMA revision vs EC decision) is settled */}
                <span title="Beta: EU versions are still being validated against the EMA / EC sources" className="rounded-ctl border border-signal/30 px-1.5 font-mono text-[10px] tracking-[0.08em] text-signal">BETA</span>
              </>)}
              </div>
              )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Layout - document + optional draft Comments panel (resizable) */}
      <div className="w-full px-4 pb-4">
        <div className="flex">
          {/* Main Content - Document */}
          <main
            ref={contentRef}
            className={`flex-1 flex flex-col min-w-0 px-1 relative ${
              isViewingSba || isViewingEpar || isViewingUkPil || isViewingCaPm || isViewingAu || isViewingEu || isViewingJpPdf || isViewingZhPdf || isViewingChPdf || isViewingDePdf || isViewingFrHtml ? 'min-h-0' : ''
            }`}
            style={isViewingSba || isViewingEpar || isViewingUkPil || isViewingCaPm || isViewingAu || isViewingEu || isViewingJpPdf || isViewingZhPdf || isViewingChPdf || isViewingDePdf || isViewingFrHtml ? { height: paneFit ? `${paneFit.height}px` : 'calc(100vh - 4rem)' } : undefined}
          >
            {/* Loading overlay when switching drugs (keeps the layout visible) */}
            {isLoading && drug && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-20 rounded-lg">
                <div className="text-center">
                  <svg className="w-8 h-8 animate-spin mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p className="mt-2 text-sm text-slate-600">Loading drug...</p>
                </div>
              </div>
            )}
            {/* Multi-section evidence indicator (V2 search) */}
            {highlightedSections.size > 0 && (
              <div className="mb-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-2">
                <span className="text-indigo-600">🎯</span>
                <span className="text-sm text-indigo-700">
                  <strong>{highlightedSections.size}</strong> relevant {highlightedSections.size === 1 ? 'section' : 'sections'} found
                </span>
                <span className="text-xs text-indigo-500 ml-2">
                  (highlighted with indigo border)
                </span>
                <button
                  onClick={() => setHighlightedSections(new Set())}
                  className="ml-auto text-indigo-400 hover:text-indigo-600 text-xs"
                >
                  Clear
                </button>
              </div>
            )}
            {/* Out-of-scope paths keep today's layout: 'contents' wrappers are
                layout-transparent, so the PDF height chain is untouched. */}
            <div className={labelView ? 'flex gap-6' : 'contents'}>
            {labelView && isLg && (
              <LabelContentsRail
                entries={railEntries}
                counts={showToggle ? shownCounts : null}
                visibleKeys={viewMode === 'matches' && snapshot ? snapshot.keep : null}
                activeKey={spyKey}
                onSelect={onRailSelect}
                style={{ top: spyOffset + 8, ...(paneFit ? { maxHeight: paneFit.height } : {}) }}
              />
            )}
            <div className={labelView ? 'min-w-0 flex-1' : 'contents'}>
            {labelView && (
              <LabelToolbar
                showToggle={showToggle}
                viewMode={viewMode}
                total={total}
                onViewMode={setViewModeTo}
                findText={findText}
                onFindTextChange={setFindText}
                onEnter={onFindEnter}
                onPrev={() => move(-1)}
                onNext={() => move(1)}
                termsActive={highlightTerms.length > 0}
                matchCount={heads.length}
                activeIndex={activeIndex}
                liveText={liveText}
                showMatchLegend={highlightTerms.length > 0}
                showCepiLegend={!!shell}
                zeroNoteKeyword={zeroNoteKeyword}
                collapseLabel={labelView === 'sections' && collapsibleKeys.length ? (anyExpanded ? 'Collapse all' : 'Expand all') : null}
                onToggleCollapseAll={() => setCollapsedRows(anyExpanded ? new Set(collapsibleKeys) : new Set())}
              />
            )}
            <div
              ref={setArticleRoot}
              data-label-article={labelView ? '' : undefined}
              className={`relative bg-white border border-slate-200 rounded-lg ${
                isViewingApprovalLetter || isViewingSba || isViewingEpar || isViewingUkPil || isViewingCaPm || isViewingAu || isViewingEu || isViewingJpPdf || isViewingZhPdf || isViewingChPdf || isViewingDePdf || isViewingFrHtml ? 'h-full flex flex-col overflow-hidden min-h-0' : ''
              }`}
            >
              {/* Unwired NICE-view combination (e.g. Clinical, or a Commercial
                  group other than NICE Assessments): blank the document view.
                  An overlay avoids surgery on the large viewer render chain. */}
              {niceBlank && (
                <div
                  data-testid="nice-blank-view"
                  className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 rounded-lg bg-white text-center"
                >
                  <p className="text-base font-medium text-gray-500">{niceCorpus || niceModule}</p>
                  <p className="text-sm text-gray-400">Coming soon</p>
                </div>
              )}
              {/* Conditional ladder: AU → CA PM → UK PIL → EPAR → SBA →
                  FDA Letter → Draft → FDA Label default.  AU is
                  FIRST in the chain so it takes over the entire
                  right-pane area when selected; subsequent branches
                  are gated on isViewingAu being false (via the
                  mutually-exclusive isViewing* booleans) so they only
                  fire in their own modes.  Without this, the chain
                  falls through to the default SmPC accordion render
                  at the tail and BOTH render simultaneously. */}
              {euAutolandPending ? (
                /* EU SmPC fetch in flight — hold a loader so the legacy
                   section view never flashes before the PDF viewer mounts. */
                <div className="flex items-center justify-center py-20">
                  <div className="text-center text-sm text-slate-500">
                    Loading EU SmPC…
                  </div>
                </div>
              ) : drug?.region === 'JP' ? (
                /* JP: the XML package-insert label (JPSectionView) is the
                   default view; supplementary PDFs (patient leaflet /
                   interview form / review report / RMP) are selected from
                   the version dropdown and render in the page-image viewer
                   with block-hover English translation. */
                isViewingJpPdf ? (() => {
                  const jpDoc = jpPdfDocuments.find((d) => d.id === selectedJpPdfId);
                  return (
                    <div className="h-full flex flex-col min-h-0">
                      <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                        <span className="font-semibold text-slate-700">
                          {jpDoc?.trade_name || 'PMDA Document'}
                        </span>
                        {jpDoc?.doc_type_label && (
                          <span className="text-slate-500">{jpDoc.doc_type_label}</span>
                        )}
                        <span className="ml-auto text-slate-400">Hover Japanese text for English →</span>
                      </div>
                      <div className="flex-1 min-h-0">
                        <JPPdfViewerEmbed key={selectedJpPdfId ?? 0} docId={selectedJpPdfId as number} highlightTerm={kwHighlightTerm} highlightChunkIds={jpPdfHighlightChunkIds} landingPage={jpPdfHighlightChunkIds.length > 0 ? undefined : citationLandingPage} />
                      </div>
                    </div>
                  );
                })() : (
                  <>
                    <JPSectionView
                      drugId={drug.id}
                      docId={jpViewDoc}
                      onSections={handleJpSections}
                      initialLang={jpJaDeepLink ? 'ja' : undefined}
                      highlightTerms={highlightTerms}
                      englishExact={!!deepLink?.englishExact}
                      alwaysOpen
                      {...(viewMode === 'matches' && snapshot ? { sectionFilter: Array.from(snapshot.keep) } : {})}
                      langResetKey={`${searchParams.get('source') ?? ''}|${searchParams.get('highlight') ?? ''}|${searchParams.get('section') ?? ''}`}
                      onLangChange={returnToFull}
                    />
                    {showJpCompare && (
                      <JPVersionCompareModal
                        drugId={drug.id}
                        drugName={(drug as any).brand_name || (drug as any).generic_name}
                        isOpen={showJpCompare}
                        onClose={() => setShowJpCompare(false)}
                        initialLineage={jpCompareTarget?.split('|')[0] ?? null}
                        initialFrom={jpCompareTarget ? Number(jpCompareTarget.split('|')[1]) : null}
                        initialTo={jpLineages.find((l) => l.lineage_key === jpCompareTarget?.split('|')[0])
                          ?.versions[0]?.version_number ?? null}
                      />
                    )}
                  </>
                )
              ) : isViewingFrHtml ? (
                <div className="flex-1 min-h-0">
                  {selectedFrHtmlId !== null ? (
                    <FrHtmlViewer key={selectedFrHtmlId} docId={selectedFrHtmlId} highlightTerm={kwHighlightTerm}
                      updated={frHtmlDocuments.find((d) => d.id === selectedFrHtmlId)?.updated_source ?? null} />
                  ) : (
                    <div className="p-6 text-sm text-slate-500">
                      {frHtmlDocuments.length ? 'Loading French label…' : 'No French (ANSM) label for this product.'}
                    </div>
                  )}
                </div>
              ) : isViewingFdaLabelPdf ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* The compare launcher renders nothing unless the FDA
                      application has >= 2 completed versions, so a
                      single-version product shows no dead control. */}
                  <div className="px-3 py-2 border-b border-line">
                    <FdaLabelPdfVersionCompareLauncher
                      docId={selectedFdaLabelPdfId as number}
                      brand={drug?.brand_name ?? ''}
                      // Layout A: Drugs@FDA labels compare only with other
                      // Drugs@FDA labels — no SPL (DailyMed) side.
                      drugId={usFdaMode ? null : (drug?.id ?? null)}
                    />
                  </div>
                  <div className="flex-1 min-h-0">
                    <FdaLabelPdfViewerEmbed englishExact={!!deepLink?.englishExact}
                      key={selectedFdaLabelPdfId ?? 0}
                      docId={selectedFdaLabelPdfId as number}
                      highlightTerm={kwHighlightTerm}
                      highlightChunkIds={fdaLabelPdfHighlightChunkIds}
                      landingPage={fdaLabelPdfHighlightChunkIds.length > 0
                        ? undefined : citationLandingPage}
                    />
                  </div>
                </div>
              ) : (LABEL_PDF_REGIONS.has(drug?.region ?? '') && isViewingDePdf) ? (
                <div className="flex-1 min-h-0">
                  {/* No family guess before the DE/IE/IT lists load (see dePdfDocsLoaded). */}
                  {(dePdfDocuments.some((d) => d.id === selectedDePdfId) || dePdfDocsLoaded) && (
                  <DePdfViewerEmbed englishExact={!!deepLink?.englishExact} key={selectedDePdfId ?? 0}
                    family={dePdfDocuments.find((d) => d.id === selectedDePdfId)?.family ?? 'de_pdf'}
                    docId={selectedDePdfId as number} highlightTerm={kwHighlightTerm} highlightChunkIds={dePdfHighlightChunkIds} landingPage={dePdfHighlightChunkIds.length > 0 ? undefined : citationLandingPage} />
                  )}
                </div>
              ) : (drug?.region === 'CH' && isViewingChPdf) ? (
                <div className="flex-1 min-h-0">
                  <ChPdfViewerEmbed englishExact={!!deepLink?.englishExact} key={selectedChPdfId ?? 0} docId={selectedChPdfId as number} highlightTerm={kwHighlightTerm} highlightChunkIds={chPdfHighlightChunkIds} landingPage={chPdfHighlightChunkIds.length > 0 ? undefined : citationLandingPage} />
                </div>
              ) : (drug?.region === 'CN' && isViewingZhPdf) ? (() => {
                /* CN: NMPA supplementary PDF in the page-image viewer with
                   block-hover English translation. GA for all users.
                   region='CN' with no doc selected never reaches here — it
                   falls through to region CN's pre-existing (non-zh) render. */
                const zhDoc = zhPdfDocuments.find((d) => d.id === selectedZhPdfId);
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">
                        {zhDoc?.trade_name || 'NMPA Document'}
                      </span>
                      {zhDoc?.doc_type_label && (
                        <span className="text-slate-500">{zhDoc.doc_type_label}</span>
                      )}
                      <span className="ml-auto text-slate-400">Hover Chinese text for English →</span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <ZhPdfViewerEmbed key={selectedZhPdfId ?? 0} docId={selectedZhPdfId as number} highlightTerm={kwHighlightTerm} highlightChunkIds={zhPdfHighlightChunkIds} landingPage={zhPdfHighlightChunkIds.length > 0 ? undefined : citationLandingPage} />
                    </div>
                  </div>
                );
              })() : isViewingAu ? (() => {
                const auDoc = auDocuments.find(
                  (d) => d.id === selectedAuId,
                );
                // "View old version": when a historical version is picked, render
                // that version's own au_documents.id (the viewer serves any doc by
                // id — no is_current filter), else the current selected doc.
                const auVer = auViewVersion != null
                  ? auVersions.find((v) => v.version_number === auViewVersion) : undefined;
                const effectiveAuDocId = auVer?.document_id ?? auDoc?.id;
                if (!auDoc && effectiveAuDocId == null) {
                  return (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center text-sm text-slate-500">
                        Loading Australia TGA document…
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">
                        {auDoc?.trade_name || 'Australia TGA'}
                      </span>
                      {auDoc?.doc_type_label && (
                        <span className="text-slate-500">{auDoc.doc_type_label}</span>
                      )}
                      {auVer && (
                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          Historical · PI V{auVer.version_number}{auVer.revision_date ? ` · ${auVer.revision_date}` : ''}
                        </span>
                      )}
                      {!auVer && (
                        <span className="ml-auto text-slate-400">
                          {(auDoc as unknown as { pdf_page_count?: number })?.pdf_page_count ?? 0} pages · {auDoc?.total_sections ?? 0} sections
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-h-0">
                      <AUViewerEmbed englishExact={!!deepLink?.englishExact}
                        key={effectiveAuDocId}
                        docId={effectiveAuDocId as number}
                        highlightChunkIds={auHighlightChunkIds}
                        chunkAnchors={auChunkAnchors}
                        pageAnchors={pageAnchorsFor('au', effectiveAuDocId as number)}
                        landingPage={auHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('au', effectiveAuDocId as number) ?? citationLandingPage)}
                        highlightTerm={kwHighlightTerm}
                      />
                    </div>
                  </div>
                );
              })() : isViewingEu ? (() => {
                const euDoc = euDocuments.find(
                  (d) => d.id === selectedEuId,
                );
                if (!euDoc) {
                  return (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center text-sm text-slate-500">
                        Loading EU EMA SmPC document…
                      </div>
                    </div>
                  );
                }
                if (euHistoricalOpen) {
                  return (
                    <EuHistoricalViewer
                      key={euViewerDocId}
                      docId={euViewerDocId}
                      initialPage={Number(searchParams.get('euhpage')) || 1}
                      initialY={searchParams.get('euhy') != null ? Number(searchParams.get('euhy')) : undefined}
                      initialSection={searchParams.get('euhsection')}
                      presentationKey={searchParams.get('euhpres')}
                      onClose={closeEuHistoricalView}
                    />
                  );
                }
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">
                        {euDoc.trade_name || 'EU EMA SmPC'}
                      </span>
                      {euDoc.doc_type_label && (
                        <span className="text-slate-500">{euDoc.doc_type_label}</span>
                      )}
                      <span className="ml-auto text-slate-400">
                        {(euDoc as unknown as { pdf_page_count?: number }).pdf_page_count ?? 0} pages · {euDoc.total_sections ?? 0} sections
                      </span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <EUViewerEmbed englishExact={!!deepLink?.englishExact}
                        docId={euDoc.id}
                        highlightChunkIds={euHighlightChunkIds}
                        chunkAnchors={euChunkAnchors}
                        pageAnchors={pageAnchorsFor('eu', euDoc.id)}
                        landingPage={euHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('eu', euDoc.id) ?? citationLandingPage)}
                        highlightTerm={kwHighlightTerm}
                      />
                    </div>
                  </div>
                );
              })() : isViewingCaPm ? (() => {
                const caPmDoc = caPmDocuments.find(
                  (d) => d.id === selectedCaPmId,
                );
                if (!caPmDoc) {
                  return (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center text-sm text-slate-500">
                        Loading Product Monograph document…
                      </div>
                    </div>
                  );
                }
                const pmCode = (caPmDoc as unknown as { pm_code?: string }).pm_code;
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">
                        {caPmDoc.trade_name || 'Product Monograph'}
                      </span>
                      {pmCode && (
                        <span className="text-slate-500">PM {pmCode}</span>
                      )}
                      <span className="ml-auto text-slate-400">
                        {(caPmDoc as unknown as { pdf_page_count?: number }).pdf_page_count ?? 0} pages · {caPmDoc.total_sections ?? 0} sections
                      </span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <CAPMViewerEmbed englishExact={!!deepLink?.englishExact}
                        docId={caPmDoc.id}
                        highlightChunkIds={caPmHighlightChunkIds}
                        chunkAnchors={caPmChunkAnchors}
                        pageAnchors={pageAnchorsFor('ca_pm', caPmDoc.id)}
                        landingPage={caPmHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('ca_pm', caPmDoc.id) ?? citationLandingPage)}
                        highlightTerm={kwHighlightTerm}
                      />
                    </div>
                  </div>
                );
              })() : isViewingUkPil ? (() => {
                const ukPilDoc = ukPilDocuments.find(
                  (d) => d.id === selectedUkPilId,
                );
                if (!ukPilDoc) {
                  return (
                    <div className="flex items-center justify-center py-20">
                      <div className="text-center text-sm text-slate-500">
                        Loading UK PIL document…
                      </div>
                    </div>
                  );
                }
                const parseMethod = (ukPilDoc as unknown as { parse_method?: string }).parse_method;
                const pmid = (ukPilDoc as unknown as { pmid?: string }).pmid;
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">
                        {ukPilDoc.trade_name || 'UK PIL'}
                      </span>
                      {pmid && (
                        <span className="text-slate-500">MHRA pmid {pmid}</span>
                      )}
                      {parseMethod === 'gemini_fallback' && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">
                          Gemini fallback
                        </span>
                      )}
                      <span className="ml-auto text-slate-400">
                        {(ukPilDoc as unknown as { pdf_page_count?: number }).pdf_page_count ?? 0} pages · {ukPilDoc.total_sections ?? 0} sections
                      </span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <UKPILViewerEmbed
                        docId={ukPilDoc.id}
                        highlightChunkIds={ukPilHighlightChunkIds}
                        chunkAnchors={ukPilChunkAnchors}
                        pageAnchors={pageAnchorsFor('uk_pil', ukPilDoc.id)}
                        landingPage={ukPilHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('uk_pil', ukPilDoc.id) ?? citationLandingPage)}
                        highlightTerm={kwHighlightTerm}
                        // NICE demo docs: TOC HIDDEN. The section tree renders
                        // double-numbered titles ("1 1 Recommendations") + raw
                        // slug labels for unnumbered headings — parser artifacts
                        // whose fix needs re-ingestion. Hidden until cleaned.
                        showToc={!(ukPilDoc.doc_type ?? '').startsWith('nice_')}
                      />
                    </div>
                  </div>
                );
              })() : isViewingApprovalLetter ? (() => {
                const letter = reviewLetters.find(
                  item => item.id === selectedApprovalLetterId,
                );
                return (
                  <div className="h-full flex flex-col min-h-0">
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="font-semibold text-slate-700">FDA Approval Letter</span>
                      {letter?.letter_date && (
                        <span className="text-slate-500">{letter.letter_date.slice(0, 10)}</span>
                      )}
                      {letter?.version_str && (
                        <span className="text-slate-500">· {letter.version_str}</span>
                      )}
                      {letter && (
                      <span className="ml-auto text-slate-400">
                        {letter.pdf_page_count ?? 0} pages
                      </span>
                      )}
                    </div>
                    <div className="flex-1 min-h-0">
                      <ApprovalLetterViewerEmbed englishExact={!!deepLink?.englishExact}
                        docId={selectedApprovalLetterId as number}
                        highlightChunkIds={approvalLetterHighlightChunkIds}
                        chunkAnchors={approvalLetterChunkAnchors}
                        pageAnchors={pageAnchorsFor('approval_letter', selectedApprovalLetterId as number)}
                        landingPage={approvalLetterHighlightChunkIds.length > 0
                          ? undefined
                          : (pageAnchorLandingFor('approval_letter', selectedApprovalLetterId as number)
                             ?? approvalLetterLandingPage)}
                        highlightTerm={kwHighlightTerm}
                      />
                    </div>
                  </div>
                );
              })() : isViewingEpar && eparDocDetail ? (
                <div className="h-full flex flex-col min-h-0">
                  <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
                    <span className="font-semibold text-slate-700">{eparDocDetail.trade_name || 'EPAR'}</span>
                    {eparDocDetail.application_number && (
                      <span className="text-slate-500">{eparDocDetail.application_type || 'EU'} {eparDocDetail.application_number}</span>
                    )}
                    {eparDocDetail.applicant && (
                      <span className="text-slate-500 truncate">· {eparDocDetail.applicant}</span>
                    )}
                    <span className="ml-auto text-slate-400">
                      {eparDocDetail.pdf_page_count ?? 0} pages · {eparDocDetail.total_sections ?? 0} sections
                    </span>
                  </div>
                  <div className="flex-1 min-h-0">
                    <EPARViewerEmbed
                      docId={eparDocDetail.id}
                      highlightChunkIds={eparHighlightChunkIds}
                      chunkAnchors={eparChunkAnchors}
                      pageAnchors={pageAnchorsFor('epar', eparDocDetail.id)}
                        landingPage={eparHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('epar', eparDocDetail.id) ?? citationLandingPage)}
                      highlightTerm={kwHighlightTerm}
                    />
                  </div>
                </div>
              ) : isViewingEpar && isLoadingEpar ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <svg className="w-8 h-8 animate-spin mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600">Loading EMA Review document...</p>
                  </div>
                </div>
              ) : sbaDetailError ? (
                <div
                  role="alert"
                  className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-600"
                >
                  {sbaDetailError}
                </div>
              ) : /* SBA (FDA Review) Document Viewer */
              isViewingSba && sbaDocDetail ? (
                <div className="h-full flex flex-col min-h-0">
                  {/* Thin metadata strip */}
                  <div
                    data-testid="sba-document-metadata"
                    className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0"
                  >
                    <span className="font-semibold text-slate-700">{sbaDocDetail.trade_name || 'SBA'}</span>
                    {(() => {
                      const identity = [
                        sbaDocDetail.doc_type_label
                          || sbaDocDetail.effective_doc_type_label,
                        formatSbaSubmissionLabel(
                          sbaDocDetail.submission_type,
                          sbaDocDetail.submission_number,
                        ),
                        formatSbaRegulatoryDate(
                          sbaDocDetail.submission_status_date,
                        ),
                      ].filter((value): value is string => !!value);
                      return identity.length > 0 ? (
                        <span className="text-slate-600">
                          {identity.join(' · ')}
                        </span>
                      ) : null;
                    })()}
                    {sbaDocDetail.submission_class_code && (
                      <span className="text-slate-400">
                        {sbaDocDetail.submission_class_code}
                      </span>
                    )}
                    {sbaDocDetail.application_number && (() => {
                      const appType = resolveSbaApplicationType(
                        sbaDocDetail.application_number,
                        sbaDocuments ?? [],
                        sbaDocDetail.application_type,
                      );
                      return (
                        <span className="text-slate-500">
                          {appType ? `${appType} ` : ''}{sbaDocDetail.application_number}
                        </span>
                      );
                    })()}
                    {sbaDocDetail.applicant && (
                      <span className="text-slate-500 truncate">· {sbaDocDetail.applicant}</span>
                    )}
                    <span className="ml-auto text-slate-400">
                      {sbaDocDetail.pdf_page_count ?? 0} pages · {sbaDocDetail.total_sections ?? 0} sections
                    </span>
                  </div>
                  <div className="flex-1 min-h-0">
                    <SBAViewerEmbed
                      ref={sbaViewerRef}
                      docId={sbaDocDetail.id}
                      highlightChunkIds={sbaHighlightChunkIds}
                      chunkAnchors={sbaChunkAnchors}
                      pageAnchors={pageAnchorsFor('sba', sbaDocDetail.id)}
                        landingPage={sbaHighlightChunkIds.length > 0 ? undefined
                          : (pageAnchorLandingFor('sba', sbaDocDetail.id) ?? citationLandingPage)}
                      // Investigator surface: no search-term highlighting in the
                      // opened HA/review document (evidence-chunk highlight kept).
                      // Compare (CompareKeywordPage) still passes its own terms.
                      onChunkClick={(_chunk) => {
                        // Could be used to show chunk details in CEPI panel
                      }}
                    />
                  </div>
                </div>
              ) : isViewingSba && isLoadingSba ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <svg className="w-8 h-8 animate-spin mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600">Loading FDA Review document...</p>
                  </div>
                </div>

              ) : isViewingFdaLetter && fdaLetterDetail ? (
                <div>
                  {/* FDA Letter Header */}
                  <div className="p-6 border-b border-slate-200">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      {/* Title removed per user request — info card shows metadata + TOC only */}
                      <div className="text-sm text-gray-600 space-y-1">
                        {fdaLetterDetail.company_name && <div><strong>Company:</strong> {fdaLetterDetail.company_name}</div>}
                        {fdaLetterDetail.product_name && <div><strong>Product:</strong> {fdaLetterDetail.product_name}</div>}
                        {fdaLetterDetail.division && <div><strong>Division:</strong> {fdaLetterDetail.division}</div>}
                        {fdaLetterDetail.application_number && <div><strong>Application:</strong> {fdaLetterDetail.application_number}</div>}
                      </div>
                      {/* TOC-style links to documents within this letter */}
                      {fdaLetterDetail.sections.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-amber-200">
                          <div className="text-xs font-semibold text-amber-700 uppercase mb-1">Documents</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {fdaLetterDetail.sections.map((section) => (
                              <button
                                key={section.id}
                                onClick={() => {
                                  const sectionId = `fda-letter-section-${section.id}`;
                                  if (!expandedSections.has(sectionId)) {
                                    toggleSection(sectionId);
                                  }
                                  setTimeout(() => {
                                    const el = findInTab(rootRef, sectionId);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 100);
                                }}
                                className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                {section.title || section.doc_type.replace(/_/g, ' ')}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Drugs referenced in this letter (cross-reference panel).
                      Item [3] of docs/superpowers/plans/2026-05-14-fda-letters-
                      post-search-spec.md.  Hidden when the backend returns no
                      refs (CI/IRB/internet-pharmacy letters). */}
                  <DrugReferencesPanel
                    references={fdaLetterDetail.drug_references}
                    letterId={fdaLetterDetail.id}
                  />

                  {/* FDA Letter Sections */}
                  {fdaLetterDetail.sections.map((section) => {
                    const sectionId = `fda-letter-section-${section.id}`;
                    const isExpanded = expandedSections.has(sectionId);
                    const cachedContent = fdaLetterSectionContent[section.id];
                    const sectionHtml = cachedContent?.content_html;
                    const sectionText = cachedContent?.content_text;
                    // Image-only sections (image-rich scanned promo PDFs where the
                    // pymupdf text-extraction path returned empty) have
                    // ``content_length=0`` but valid ``fda_letter_pages`` rows
                    // with image GCS paths. We still need to render them so the
                    // user sees the page image — the previous all-or-nothing
                    // ``hasContent`` gate hid the entire section, leaving the
                    // viewer blank (one example: TRULICITY promo letter 1876).
                    const hasTextContent = section.content_length > 0 || !!(sectionHtml || sectionText);
                    const isImageOnly = !hasTextContent;
                    // The viewer renders the page image unconditionally — we
                    // always show the header + mount the viewer; let the
                    // viewer's internal pages-empty + htmlFallback paths
                    // handle truly empty sections (rare).
                    const hasContent = true;
                    const isLoadingContent = loadingFdaLetterSections.has(section.id);
                    const contentLoaded = !!(sectionHtml || sectionText);

                    return (
                      <section
                        key={section.id}
                        id={sectionId}
                        ref={(el) => {
                          if (el) sectionRefs.current.set(sectionId, el);
                        }}
                        className="border-b border-slate-200 last:border-b-0 scroll-mt-20"
                      >
                        {hasContent && (
                          <button
                            onClick={() => toggleSection(sectionId)}
                            className="w-full text-left px-6 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                          >
                            <span className={`text-xs transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            } text-slate-400`}>
                              &#9654;
                            </span>
                            <h2 className="font-bold tracking-wide text-base uppercase text-slate-900">
                              {section.title || section.doc_type.replace(/_/g, ' ')}
                            </h2>
                          </button>
                        )}

                        {isExpanded && hasContent && (
                          <div className="px-6 pb-6 pt-2">
                            {/* Image-only document banner: scanned PDFs
                                where pymupdf text-extraction returned
                                empty. Page image still renders below
                                via FdaLetterPageViewer. */}
                            {isImageOnly && (
                              <div className="mb-3 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                                <span className="font-semibold uppercase tracking-wide text-[10px] mt-0.5">Image-only</span>
                                <span>This document is a scanned image — extracted text isn't available, so Cmd-F and chunk-level citations may not work. The page image is shown below.</span>
                              </div>
                            )}
                            {/* Phase 7: page-image viewer for PDF-source
                                sections.  The viewer fetches
                                /sections/{id}/pages itself; on empty
                                ``pages`` (HTML-source) it falls back to
                                the legacy sanitised-HTML render via the
                                ``htmlFallback`` slot.  The slot keeps
                                the loading-spinner + content_html /
                                content_text path identical to before. */}
                            <FdaLetterPageViewer englishExact={!!deepLink?.englishExact}
                              highlightTerms={highlightTerms}
                              section_id={section.id}
                              activeChunkId={fdaLetterActiveChunkId}
                              htmlFallback={
                                isLoadingContent && !contentLoaded ? (
                                  <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading section content...
                                  </div>
                                ) : sectionHtml ? (
                                  <div
                                    className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-p:text-slate-700 prose-li:text-slate-700 prose-td:text-slate-700 prose-th:text-slate-700 [&_table]:text-xs [&_table]:border-collapse [&_th]:bg-slate-50 [&_th]:px-2 [&_th]:py-1 [&_th]:border [&_th]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_td]:border [&_td]:border-slate-200"
                                    dangerouslySetInnerHTML={{ __html: deepLink?.englishExact ? highlightHtmlString(sanitizeHtml(sectionHtml), highlightTerms, undefined, null, { englishExact: true }) : sanitizeHtml(sectionHtml) }}
                                  />
                                ) : sectionText ? (
                                  <div className="prose max-w-none leading-relaxed whitespace-pre-wrap text-sm text-slate-700">
                                    {deepLink?.englishExact ? highlightSearchTerms(sectionText) : sectionText}
                                  </div>
                                ) : null
                              }
                            />
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>

              ) : isViewingFdaLetter && isLoadingFdaLetter ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <svg className="w-8 h-8 animate-spin mx-auto text-amber-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600">Loading FDA Enforcement Letter...</p>
                  </div>
                </div>

              ) : (drug?.region === 'AU' || drug?.region === 'CA') &&
                  !isViewingSba && !isViewingEpar && !isViewingUkPil
                  && !isViewingCaPm && !isViewingAu && !isViewingEu && !isViewingFdaLetter
                  && selectedVersionId === null && selectedHistoricalId === null ? (
                /* AU + CA drugs auto-land on their per-family PDF viewer
                   (see useEffect at ~L1965).  While auDocuments /
                   caPmDocuments are still being fetched, the conditional
                   ladder would otherwise fall through to the legacy
                   sections accordion for a frame before auto-land fires.
                   Show a small loader instead so the user doesn't see
                   the brief flash. */
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <svg className="w-8 h-8 animate-spin mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600">Loading document…</p>
                  </div>
                </div>

              ) : isDraftVersion && versionDetail?.document_sections ? (
                (useOnlyOfficeViewer && versionDetail?.id) ? (
                  <OnlyOfficeDraftViewer
                    versionId={versionDetail.id}
                    onReady={(api) => { viewerHandleRef.current = api; }}
                    onUnavailable={() => setUseOnlyOfficeViewer(false)}
                    className="w-full"
                  />
                ) : (
                <div>
                  {versionDetail.document_sections.map((section, index) => {
                    const sectionId = `doc-section-${index}`;
                    const isExpanded = expandedSections.has(sectionId);

                    return (
                      <section
                        key={index}
                        id={sectionId}
                        ref={(el) => {
                          if (el) sectionRefs.current.set(sectionId, el);
                        }}
                        className="border-b border-slate-200 last:border-b-0 scroll-mt-20"
                      >
                        {/* Section Header - Always Visible */}
                        <button
                          onClick={() => toggleSection(sectionId)}
                          className="w-full text-left px-6 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                        >
                          <span className={`text-slate-400 text-xs transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}>
                            &#9654;
                          </span>
                          <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide">
                            {section.title || `Section ${index + 1}`}
                          </h2>
                        </button>

                        {/* Section Content - Collapsible */}
                        {isExpanded && (
                          <div className="px-6 pb-6 pt-2">
                            <DraftContentRenderer
                              contentHtml={section.content_html}
                              contentText={section.content}
                              comments={comments}
                              selectedCommentId={selectedCommentId}
                              onCommentClick={(commentId) => {
                                const comment = comments.find(c => c.id === commentId);
                                if (comment) handleCommentClick(comment);
                              }}
                              highlightTerms={highlightTerms}
                              englishExact={!!deepLink?.englishExact}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
                )
              ) : (
                /* FDA Label Sections - Paged Layout */
                <div>
                  {getDisplaySections().map((section) => {
                    const sectionId = `section-${section.id}`;
                    if (labelView === 'sections' && viewMode === 'matches' && snapshot && !snapshot.keep.has(sectionId)) return null;
                    const isExpanded = expandedSections.has(sectionId);
                    const isBoxedWarning = section.section_number === 'BW';
                    const isSubsection = isSubsectionCheck(section);
                    const hasContent = section.content_text?.trim();
                    const parentNum = getParentSectionNum(section);
                    const isParentCollapsed = isSubsection && collapsedParents.has(parentNum);

                    // Hide subsections if their parent is collapsed (CN/CH accordion only)
                    if (labelView !== 'sections' && isParentCollapsed) {
                      return null;
                    }

                    // Check if this is a parent section (no content but has subsections)
                    const isEmptyParent = !hasContent && !isSubsection;
                    // Check if this section has matching search evidence (multi-section highlighting)
                    const isEvidenceMatch = section.section_number && highlightedSections.has(section.section_number);

                    if (labelView === 'sections') {
                      // Full-label view: every row expanded, plain headings. The
                      // data-label-* attributes are what US_ROWS (labelMatches) counts.
                      const isHighlights = isHighlightsSection(section);
                      const rowTitle = section.title || SECTION_NUMBER_NAMES[section.section_number || ''] || `Section ${section.section_number}`;
                      const showBody = !!hasContent && (!isHighlights || highlightsOpen);
                      const isCollapsible = !isHighlights && !isBoxedWarning && (!!hasContent || !isSubsection);
                      const isCollapsed = collapsedRows.has(sectionId);
                      const num = section.section_number || '';
                      const hiddenByAncestor = !!num && collapsedNums.some((c) => num.startsWith(c + '.'));
                      const rowToggle = (title: React.ReactNode) => (
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          onClick={() => toggleRow(sectionId)}
                          className={`w-full text-left flex items-center gap-3 ${isSubsection ? '' : 'uppercase'}`}
                        >
                          <span aria-hidden="true" className={`text-xs transition-transform text-slate-400 ${isCollapsed ? '' : 'rotate-90'}`}>
                            &#9654;
                          </span>
                          {title}
                        </button>
                      );
                      return (
                        <section
                          key={section.id}
                          id={sectionId}
                          hidden={hiddenByAncestor || undefined}
                          data-section-number={section.section_number || ''}
                          data-label-row={sectionId}
                          data-label-highlights={isHighlights ? '' : undefined}
                          ref={(el) => {
                            if (el) sectionRefs.current.set(sectionId, el);
                            else sectionRefs.current.delete(sectionId);
                          }}
                          className={`border-b border-slate-200 last:border-b-0 scroll-mt-20 ${
                            isBoxedWarning ? 'bg-red-50' : ''
                          } ${isSubsection ? 'bg-slate-50/50' : ''} ${
                            isEvidenceMatch ? 'border-l-4 border-l-indigo-500 bg-indigo-50/30' : ''
                          }`}
                        >
                          {isEmptyParent ? (
                            <h2 className={`px-6 py-3 bg-slate-100 font-bold tracking-wide text-base uppercase ${
                              isBoxedWarning ? 'text-red-800' : 'text-slate-900'
                            }`}>
                              {isCollapsible ? rowToggle(highlightSearchTerms(rowTitle)) : highlightSearchTerms(rowTitle)}
                            </h2>
                          ) : hasContent && isHighlights ? (
                            <h2 className="text-base">
                              <button
                                type="button"
                                aria-expanded={highlightsOpen}
                                onClick={() => setHighlightsOpen((o) => !o)}
                                className="w-full text-left px-6 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors font-bold tracking-wide text-slate-900 uppercase"
                              >
                                <span className={`text-xs transition-transform text-slate-400 ${highlightsOpen ? 'rotate-90' : ''}`}>
                                  &#9654;
                                </span>
                                Highlights of prescribing information
                              </button>
                            </h2>
                          ) : hasContent ? (
                            <h2 className={`px-6 py-4 font-bold tracking-wide ${
                              isBoxedWarning ? 'text-red-800' : 'text-slate-900'
                            } ${isSubsection ? 'pl-10 text-sm' : 'text-base uppercase'}`}>
                              {isCollapsible ? rowToggle(highlightSearchTerms(rowTitle)) : highlightSearchTerms(rowTitle)}
                            </h2>
                          ) : null}

                          {showBody && (
                            <div data-label-body hidden={isCollapsed || undefined} className={`px-6 pb-6 pt-2 ${isBoxedWarning ? 'border-l-4 border-red-600 ml-4' : ''} ${isSubsection ? 'pl-10' : ''}`}>
                              {section.content_xml ? (
                                <SplContentRenderer
                                  contentXml={section.content_xml}
                                  contentText={section.content_text}
                                  drugName={drug?.brand_name || ''}
                                  className={isBoxedWarning ? 'text-red-900' : ''}
                                  highlightTerms={highlightTerms}
                              englishExact={!!deepLink?.englishExact}
                                  evidenceChunkText={isEvidenceMatch ? evidenceChunkText : null}
                                />
                              ) : (
                                <div className={`prose max-w-none leading-relaxed whitespace-pre-wrap text-sm ${
                                  isBoxedWarning ? 'text-red-900' : 'text-slate-700'
                                }`}>
                                  {isEvidenceMatch && evidenceChunkText
                                    ? highlightEvidenceInPlainText(section.content_text || '', evidenceChunkText, highlightContent)
                                    : highlightContent(section.content_text || '')}
                                </div>
                              )}
                            </div>
                          )}
                        </section>
                      );
                    }

                    return (
                      <section
                        key={section.id}
                        id={sectionId}
                        data-section-number={section.section_number || ''}
                        ref={(el) => {
                          if (el) sectionRefs.current.set(sectionId, el);
                        }}
                        className={`border-b border-slate-200 last:border-b-0 scroll-mt-20 ${
                          isBoxedWarning ? 'bg-red-50' : ''
                        } ${isSubsection ? 'bg-slate-50/50' : ''} ${
                          isEvidenceMatch ? 'border-l-4 border-l-indigo-500 bg-indigo-50/30' : ''
                        }`}
                      >
                        {/* Section Header */}
                        {isEmptyParent ? (
                          /* Empty parent section - clickable to collapse/expand children */
                          <button
                            onClick={() => toggleParentCollapse(section.section_number || '')}
                            className="w-full text-left px-6 py-3 bg-slate-100 hover:bg-slate-200 transition-colors flex items-center gap-3"
                          >
                            <span className={`text-xs transition-transform ${
                              collapsedParents.has(section.section_number || '') ? '' : 'rotate-90'
                            } text-slate-500`}>
                              &#9654;
                            </span>
                            <h2 className={`font-bold tracking-wide text-base uppercase ${
                              isBoxedWarning ? 'text-red-800' : 'text-slate-900'
                            }`}>
                              {/* Section titles are FTS-searchable (title+body), so a keyword
                                  can match in the header — highlight it there too (no-op when
                                  not a keyword/deep-link search). */}
                              {highlightSearchTerms(section.title || SECTION_NUMBER_NAMES[section.section_number || ''] || `Section ${section.section_number}`)}
                            </h2>
                          </button>
                        ) : hasContent ? (
                          <button
                            onClick={() => toggleSection(sectionId)}
                            className={`w-full text-left px-6 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                              isBoxedWarning ? 'hover:bg-red-100' : ''
                            } ${isSubsection ? 'pl-10' : ''}`}
                          >
                            <span className={`text-xs transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            } ${isBoxedWarning ? 'text-red-500' : 'text-slate-400'}`}>
                              &#9654;
                            </span>
                            <h2 className={`font-bold tracking-wide ${
                              isBoxedWarning ? 'text-red-800' : 'text-slate-900'
                            } ${isSubsection ? 'text-sm' : 'text-base uppercase'}`}>
                              {section.title || SECTION_NUMBER_NAMES[section.section_number || ''] || `Section ${section.section_number}`}
                            </h2>
                          </button>
                        ) : null}

                        {/* Section Content */}
                        {isExpanded && hasContent && (
                          <div className={`px-6 pb-6 pt-2 ${isBoxedWarning ? 'border-l-4 border-red-600 ml-4' : ''} ${isSubsection ? 'pl-10' : ''}`}>
{section.content_xml ? (
                              <SplContentRenderer
                                contentXml={section.content_xml}
                                contentText={section.content_text}
                                drugName={drug?.brand_name || ''}
                                className={isBoxedWarning ? 'text-red-900' : ''}
                                highlightTerms={highlightTerms}
                              englishExact={!!deepLink?.englishExact}
                                evidenceChunkText={isEvidenceMatch ? evidenceChunkText : null}
                              />
                            ) : (
                              <div className={`prose max-w-none leading-relaxed whitespace-pre-wrap text-sm ${
                                isBoxedWarning ? 'text-red-900' : 'text-slate-700'
                              }`}>
                                {isEvidenceMatch && evidenceChunkText
                                  ? highlightEvidenceInPlainText(section.content_text || '', evidenceChunkText, highlightContent)
                                  : highlightContent(section.content_text || '')}
                              </div>
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
            </div>
            </div>
          </main>

          {/* Right Sidebar - draft Comments only (resizable, sticky). The CEPI
              chat lives in the shell's right-hand CEPI panel. When the ONLYOFFICE viewer
              is active it renders comments natively, so the sidebar is absent. */}
          {showDraftCommentsPanel && isDraftVersion && comments.length > 0 && (
            <div
              className="hidden lg:flex flex-col flex-shrink-0 relative sticky"
              style={{
                width: `${rightSidebarWidth}px`,
                top: paneFit ? `${paneFit.top}px` : '3.5rem',
                height: paneFit ? `${paneFit.height}px` : 'calc(100vh - 4rem)',
              }}
            >
              {/* Right resize handle */}
              <div
                onMouseDown={handleRightResizeStart}
                className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-indigo-400 transition-colors z-10 ${
                  isResizingRight ? 'bg-indigo-500' : 'bg-transparent hover:bg-indigo-300'
                }`}
                title="Drag to resize"
              />
              <div className="h-full flex flex-col pl-1">
                <div className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-indigo-700 border border-slate-200 border-b-0 rounded-t-lg bg-slate-50 flex-shrink-0">
                  Comments ({comments.length})
                </div>
                <div className="bg-white border border-slate-200 border-t-0 rounded-b-lg flex-1 overflow-y-auto min-h-0">
                  <CommentsPanel
                    comments={comments}
                    onCommentUpdated={(updated) => {
                      setComments(prev => prev.map(c => c.id === updated.id ? updated : c));
                    }}
                    onCommentClick={handleCommentClick}
                    selectedCommentId={selectedCommentId}
                    isSidebar={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Version Comparison Modal */}
      {showComparisonModal && comparisonVersions && (
        <VersionComparisonModal
          versionIdA={comparisonVersions.a}
          versionIdB={comparisonVersions.b}
          isDrugComparison={isComparingDrugVersions}
          comparisonData={comparisonData}
          polymorphicParams={polymorphicCompareParams}
          onClose={() => {
            setShowComparisonModal(false);
            setComparisonVersions(null);
            setIsComparingDrugVersions(false);
            setComparisonData(null);
            setPolymorphicCompareParams(null);
          }}
        />
      )}
      {showComparisonModal && !comparisonVersions && polymorphicCompareParams && (
        <VersionComparisonModal
          isDrugComparison={isComparingDrugVersions}
          comparisonData={comparisonData}
          polymorphicParams={polymorphicCompareParams}
          onClose={() => {
            setShowComparisonModal(false);
            setComparisonVersions(null);
            setIsComparingDrugVersions(false);
            setComparisonData(null);
            setPolymorphicCompareParams(null);
          }}
        />
      )}

      </div>
    </div>
  );
};

export default DrugDetailPage;
